import React, { useState, useEffect, useMemo, useRef } from "react";
import LoadingOverlay from "./LoadingOverlay";
import "./Startassessment.css";
import restart from "../assets/restart.png";
import Frame from "../assets/Frame.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpenReader, faCopy, faDownload, faLink, faStar, faTriangleExclamation, faLocationDot, faSliders, faCircleExclamation, faBullseye, faCaretDown, faCaretUp, faCheck, faDotCircle, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import infoIcon from "../assets/infoicon1.png";
import rightarrow from "../assets/rightarrow.png";
import { FaInfo, FaFileAlt, FaFolder, FaTimes } from "react-icons/fa";
import { paginateCanvasToPdf } from "./pdfBreakHelper";
import { setAssessmentArmed, takePendingLeave } from "../leaveSignal";
import { buildHineHaiStack, toHineEntries, toHaiEntries } from "../hineHaiStack";
import Toast from "./Toast";
import RichText from "./RichText";
import {
    pickInheritanceSession,
    buildKeywordLookup,
    orderHistoryGroups,
    formatHistoryAnswer,
    isInitialAppointmentType,
    formatAppointmentLabel,
    normaliseKeyword,
} from "./assessmentHistory";
import { API_BASE } from "../apiConfig";

// ---------------------------------------------------------------------------
// GOAL DOMAIN TEXT CLEANUP
// ---------------------------------------------------------------------------

// Some pathway rows store the goal domain with the domain repeated after a
// hyphen, e.g. "Play and Cognition-Play and Cognition". Compare with a
// normalised form (case, "&" and punctuation removed) so the duplicate is
// detected and collapsed to a single copy.
const normalizeGoalDomain = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]/g, "");

const cleanGoalDomainText = (d) => {
    const parts = String(d || "")
        .split("-")
        .map((p) => p.trim())
        .filter(Boolean);
    if (parts.length > 1) {
        const firstNorm = normalizeGoalDomain(parts[0]);
        if (firstNorm && parts.every((p) => normalizeGoalDomain(p) === firstNorm)) {
            return parts[0];
        }
    }
    return d;
};

// ---------------------------------------------------------------------------
// TARGET DATE DISPLAY (step-1 Review Smart Goal option)
// ---------------------------------------------------------------------------

// pathway_goals.target_date arrives as "YYYY-MM-DD" (sometimes with a time
// suffix). The step-1 Review Smart Goal option prints it exactly the way the
// goalreview page prints the same goal: "10 Oct 2026". Anything that is not a
// plain date string is passed through untouched instead of rendering "NaN".
//
// The value is read straight out of get_smart_goal_context.php (the same
// request that already fills the smartGoalContext state), so the target date
// reaches this page through the FRONTEND - no new backend field is needed.
const formatGoalTargetDate = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const [y, m, d] = raw.split("T")[0].split(" ")[0].split("-");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[parseInt(m, 10) - 1];
  if (!/^\d{4}$/.test(String(y || "")) || !month || !/^\d{1,2}$/.test(String(d || ""))) return raw;
  return `${parseInt(d, 10)} ${month} ${y}`;
};

// ---------------------------------------------------------------------------
// NETWORK / TIMEOUT UTILITIES
// ---------------------------------------------------------------------------

// All network calls in this file go through this wrapper so that a slow
// or dropped connection is detected quickly instead of hanging forever.
// A rejected request is then VERIFIED against the real network (see below)
// and only a CONFIRMED connection problem raises the "Network Issue" popup
// in the component (via globalNetworkErrorHandler, registered on mount).
// A merely slow API or a server-side error therefore never claims the
// therapist's internet is down.
const API_TIMEOUT_MS = 10000;

let globalNetworkErrorHandler = null;

// ---------------------------------------------------------------------------
// "NETWORK ISSUE" POPUP - connection problems ONLY
// ---------------------------------------------------------------------------
// The popup must never claim the internet is down when it is not. Two old
// rules produced constant false positives:
//
//   1. AbortError -> network issue. The AbortError is OUR OWN 10s timeout, so
//      every merely SLOW API (a heavy query on a local XAMPP, a cold PHP
//      session, a big draft) raised "Network Issue" while the connection was
//      perfectly healthy. This was the frequent one.
//   2. TypeError + navigator.onLine === false. The browser's onLine flag is a
//      guess about the device's interfaces, not proof that this request failed
//      because of connectivity.
//
// So a rejected request is no longer judged by its error type alone: the
// failure is VERIFIED against the real network before any popup is raised.
//
// The popup is an INTERNET problem popup and nothing else. Verification is
// therefore judged ONLY by connectivity that can be lost independently of this
// app: the device's own offline flag, the API host, and SEVERAL always-up
// internet hosts (one CDN can be blocked or filtered on its own, so a single
// gstatic probe was not enough to call it an internet problem).
//
//   - navigator says every interface is down        -> internet issue.
//   - the API host answers (no-cors: any response
//     counts, CORS headers can never make it lie)   -> not an internet issue.
//   - ANY always-up internet host answers           -> not an internet issue.
//   - none of the above answers                     -> internet issue.
//
// A slow API, a 500, a CORS error, a PHP error or a down API server can never
// take every one of these probes down, so none of them can raise this popup.
const NETWORK_PROBE_TIMEOUT_MS = 5000;
// One popup per 5s window: a burst of failing requests (every click retries)
// must still produce ONE popup, not a stack of them.
const NETWORK_POPUP_COOLDOWN_MS = 5000;

let networkCheckInFlight = false;
let lastNetworkPopupAt = 0;

const probeOnce = (url, timeout, mode = "no-cors") => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  // Resolving in EITHER case (connected / refused) with the boolean keeps the
  // probe itself from ever throwing.
  return fetch(url, { method: "GET", cache: "no-store", mode, signal: controller.signal })
    .then(
      () => { clearTimeout(timeoutId); return true; },
      () => { clearTimeout(timeoutId); return false; }
    );
};

const INTERNET_PROBE_HOSTS = [
  "https://www.gstatic.com/generate_204",
  "https://connectivitycheck.gstatic.com/generate_204",
  "https://www.cloudflare.com/generate_204",
];

const confirmNetworkIssue = async () => {
  // The OS itself reports no connectivity - no probe can contradict that.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;

  // Both probe sets run together: one round trip, one verdict.
  const [apiReachable, internetResults] = await Promise.all([
    // no-cors so an API that answers without CORS headers still counts as
    // reachable - the question is "did the network get an answer", not
    // "may this page read the body".
    probeOnce(`${API_BASE}/`, NETWORK_PROBE_TIMEOUT_MS, "no-cors"),
    Promise.all(
      INTERNET_PROBE_HOSTS.map((url) => probeOnce(url, NETWORK_PROBE_TIMEOUT_MS, "no-cors"))
    ),
  ]);

  if (apiReachable) return false;
  if (internetResults.some((reachable) => reachable)) return false;
  return true;
};

// Called from every failed request. The check is single-flight (concurrent
// failures share one probe) and the popup itself is rate limited.
const reportPossibleNetworkIssue = async () => {
  if (networkCheckInFlight || !globalNetworkErrorHandler) return;
  networkCheckInFlight = true;
  try {
    const isRealNetworkIssue = await confirmNetworkIssue();
    if (!isRealNetworkIssue) return;
    if (Date.now() - lastNetworkPopupAt < NETWORK_POPUP_COOLDOWN_MS) return;
    lastNetworkPopupAt = Date.now();
    if (globalNetworkErrorHandler) globalNetworkErrorHandler();
  } finally {
    networkCheckInFlight = false;
  }
};

// Only errors that mean "this request never completed" are worth verifying.
// Anything else (a bad JSON body, a bug in this file) is not a network
// question at all.
const isFetchConnectivityError = (error) =>
  Boolean(error) && (error.name === "AbortError" || error instanceof TypeError);

const fetchWithTimeout = async (url, options = {}, timeout = API_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (isFetchConnectivityError(error)) reportPossibleNetworkIssue();
    throw error;
  }
};

const dispatchSessionExpired = () => {
  window.dispatchEvent(new CustomEvent("session-expired"));
};

const isTokenError = (json) => {
  if (!json || json.status !== false || !json.message) return false;
  const msg = json.message.toLowerCase();
  return (msg.includes("invalid") && msg.includes("token"))
    || (msg.includes("expired") && msg.includes("token"))
    || msg.includes("token already logged out")
    || msg.includes("token missing");
};

const isGoalDomainQuestion = (q) => {
  const text = String(q?.question_text || q?.question || "").toLowerCase();
  const keyword = String(q?.keywords || "").toLowerCase();
  return keyword.includes("goal domain")
    || text.includes("domain of the new goal")
    || text.includes("domain of goal");
};

// ---------------------------------------------------------------------------
// API UTILITIES
// ---------------------------------------------------------------------------

const saveResponseAPI = async (sessionId, node, entity, entityId, answer = null, answers = null) => {
  if (!sessionId) { console.warn("Cannot save response: No Session ID"); return; }
  const token = localStorage.getItem("token");
  const url = `${API_BASE}/therapist_api/save_response.php`;
  const payload = { sessionId: parseInt(sessionId), node, entity, entityId: parseInt(entityId) };
  if (answers) { payload.answers = answers.map(id => parseInt(id)); } else { payload.answer = answer; }
  try {
    const response = await fetchWithTimeout(url, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(payload) });
    const json = await response.json();
    if (json.status) { console.log(`? Saved Node [${node}]:`, json.message); retryPendingResponses(); }
    else {
      console.error(`? Failed to save Node [${node}]:`, json.message);
      if (isTokenError(json)) { dispatchSessionExpired(); return; }
    }
  } catch (error) { console.error("Network error saving response:", error.message); queuePendingResponse(payload); }
};

const saveHineHaiAPI = async (sessionId, node, type, questionId, value, entries = []) => {
  if (!sessionId) { console.warn("Cannot save HINE/HAI: No Session ID"); return; }
  const token = localStorage.getItem("token");
  const url = `${API_BASE}/therapist_api/save_hine_hai.php`;
  const payload = { sessionId: parseInt(sessionId), node, type, questionId: parseInt(questionId), value };
  if (value === "Yes" && entries.length > 0) { payload.entries = entries; }
  try {
    const response = await fetchWithTimeout(url, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(payload) });
    const json = await response.json();
    if (json.status) { console.log(`? Saved ${type.toUpperCase()} Node [${node}]:`, json.message); }
    else {
      console.error(`? Failed to save ${type.toUpperCase()} Node [${node}]:`, json.message);
      if (isTokenError(json)) { dispatchSessionExpired(); return; }
    }
  } catch (error) { console.error("Network error saving HINE/HAI:", error.message); queuePendingResponse(payload); }
};

// ---------------------------------------------------------------------------
// NETWORK RESILIENCE ? queue failed requests & retry when possible
// ---------------------------------------------------------------------------

const PENDING_RESPONSES_KEY = "pendingResponses";

const queuePendingResponse = (payload) => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_RESPONSES_KEY) || "[]");
    pending.push({ payload, timestamp: Date.now() });
    localStorage.setItem(PENDING_RESPONSES_KEY, JSON.stringify(pending));
  } catch (e) { console.error("Failed to queue pending response:", e); }
};

const retryPendingResponses = async () => {
  let pending;
  try { pending = JSON.parse(localStorage.getItem(PENDING_RESPONSES_KEY) || "[]"); } catch { return; }
  if (!pending.length) return;
  const token = localStorage.getItem("token");
  if (!token) return;
  const remaining = [];
  for (const item of pending) {
    try {
      const response = await fetch(
        `${API_BASE}/therapist_api/save_response.php`,
        { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(item.payload) }
      );
      const json = await response.json();
      if (json.status) console.log(`? Retried Node [${item.payload.node}]`);
      else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
        remaining.push(item);
      }
    } catch { remaining.push(item); }
  }
  localStorage.setItem(PENDING_RESPONSES_KEY, JSON.stringify(remaining));
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

const getQuestionType = (question) => {
  if (!question) return { isHINE: false, isHAI: false };
  if (question.type) {
    const t = question.type.toString().toUpperCase().trim();
    return { isHINE: t === "HINE", isHAI: t === "HAI" };
  }
  const instructionString = question.instruction || "";
  const result = { isHINE: false, isHAI: false };
  if (!instructionString) return result;
  if (instructionString.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(instructionString);
      if (Array.isArray(parsed)) {
        parsed.forEach(item => {
          if (typeof item === "string") {
            const lower = item.trim().toLowerCase();
            if (lower === "hine") result.isHINE = true;
            if (lower === "hai") result.isHAI = true;
          }
        });
        return result;
      }
    } catch { /* fall through */ }
  }
  const parts = instructionString.split(",").map(s => s.trim().toLowerCase());
  if (parts.includes("hine")) result.isHINE = true;
  if (parts.includes("hai")) result.isHAI = true;
  return result;
};

const parseInstructions = (instructionString) => {
  const instructions = { isMulti: false, isVertical: false, isHINE: false, isHAI: false };
  if (!instructionString) return instructions;
  if (instructionString.trim().startsWith("[")) {
    try {
      const parsedArray = JSON.parse(instructionString);
      if (Array.isArray(parsedArray)) {
        parsedArray.forEach(item => {
          if (typeof item === "string") {
            const lowerItem = item.trim().toLowerCase();
            if (lowerItem === "multi") instructions.isMulti = true;
            if (lowerItem === "vertical") instructions.isVertical = true;
            if (lowerItem === "hine") instructions.isHINE = true;
            if (lowerItem === "hai") instructions.isHAI = true;
          }
        });
        return instructions;
      }
    } catch { /* fall through */ }
  }
  const parts = instructionString.split(",").map(s => s.trim().toLowerCase());
  if (parts.includes("multi")) instructions.isMulti = true;
  if (parts.includes("vertical")) instructions.isVertical = true;
  if (parts.includes("hine")) instructions.isHINE = true;
  if (parts.includes("hai")) instructions.isHAI = true;
  return instructions;
};

const parseSubOptionLimit = (instruction) => {
  if (!instruction) return 1;
  const match = instruction.match(/select\s+(\d+)\s+options/i);
  if (match && match[1]) return parseInt(match[1]);
  return 1;
};

const instructionHasEnd = (instructionString) => {
  if (!instructionString) return false;
  if (instructionString.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(instructionString);
      if (Array.isArray(parsed)) {
        return parsed.some(
          (item) => typeof item === "string" && item.trim().toLowerCase() === "end"
        );
      }
    } catch { /* fall through to comma-separated parse */ }
  }
  const parts = instructionString.split(",").map((s) => s.trim().toLowerCase());
  return parts.includes("end");
};

const hasEndInstructionDeep = (obj) => {
  if (!obj || typeof obj !== "object") return false;
  if (instructionHasEnd(obj.instruction)) return true;
  return [
    obj.options,
    obj.sub_options,
    obj.sub_questions,
    obj.questions,
    obj.groups,
  ].some((arr) => Array.isArray(arr) && arr.some(hasEndInstructionDeep));
};

// Given the current question and the user's answers, follow the *selected*
// option / sub-option chain and report whether any answered node carries the
// "End" instruction. Sub-questions are separate steps, so they are reached
// as their own question and checked by handleNext when it is their turn.
const selectedChainHasEnd = (question, answers, visited = new Set()) => {
  if (!question) return false;
  if (instructionHasEnd(question.instruction)) return true;
  const qNode = question.node_number;
  if (visited.has(qNode)) return false;
  visited.add(qNode);

  const ans = answers[qNode];
  if (!ans) return false;
  const selectedTexts = Array.isArray(ans) ? ans : [ans];

  const walk = (options, texts, pathTexts) => {
    for (const opt of options) {
      if (!texts.includes(opt.option_text)) continue;
      if (instructionHasEnd(opt.instruction)) return true;
      if (opt.sub_options && opt.sub_options.length > 0) {
        const nestedKey = [qNode, ...pathTexts, opt.option_text, "sub"].join("_");
        const nestedAns = answers[nestedKey];
        if (nestedAns) {
          const nestedTexts = Array.isArray(nestedAns) ? nestedAns : [nestedAns];
          if (walk(opt.sub_options, nestedTexts, [...pathTexts, opt.option_text])) return true;
        }
      }
    }
    return false;
  };

  return walk(question.options || [], selectedTexts, []);
};

// ---------------------------------------------------------------------------
// APPOINTMENT TYPES
// ---------------------------------------------------------------------------
// Three mutually-exclusive appointment options are offered on step 1:
//
//   Initial           - one-time first assessment for a brand new client.
//                       Server-enforced as once-per-client.
//   Review           - set a NEW goal for a client who already has an
//                       initial assessment. Repeatable any number of times.
//   Review Smart Goal - launched from the goalreview "Review Assessment"
//                       button. Reviews the goal that already exists on the
//                       originating pathway and revises it end-to-end.
//
// Both "Review" and "Review Smart Goal" run against the `review` pathway and
// store a review-appointment session, which is why the "Review Existing
// Goals" table only ever shows Initial / Review. "Review Smart Goal" is an
// additional UI pathway on top of a review session, carried through the URL
// together with the originating goal session id.
// ---------------------------------------------------------------------------

const APPT_INITIAL = "Initial";
const APPT_REVIEW = "Review";
const APPT_REVIEW_SMART_GOAL = "Review Smart Goal";

const APPOINTMENT_OPTIONS = [APPT_INITIAL, APPT_REVIEW, APPT_REVIEW_SMART_GOAL];

// Normalises every accepted spelling of the ?appointment= URL param so the
// header cards, goalreview and the in-form radios all resolve to one value.
const normaliseAppointmentParam = (raw) => {
  const value = String(raw || "").trim().toLowerCase();
  if (!value) return "";
  if (value === "initial") return APPT_INITIAL;
  if (value === "review smart goal" || value === "reviewsmartgoal" || value === "smart goal" || value === "smartgoal") {
    return APPT_REVIEW_SMART_GOAL;
  }
  if (value === "review") return APPT_REVIEW;
  return "";
};

// Review and Review Smart Goal both operate on an existing client, so the
// carried-over ?client= / ?goalSessionId= context stays valid for either.
const isReviewOption = (value) => value === APPT_REVIEW || value === APPT_REVIEW_SMART_GOAL;

// Exact wording required by the spec, used for EVERY step-1 blocker that stops
// the therapist before they can continue:
//
//   - no pathway exists for the age they picked or that was auto-filled, or -
//     for Review Smart Goal - the age group recorded on the selected session
//   - the pathway exists but an admin currently has it open in Edit mode
//
// One sentence for one situation. During an admin edit the pathway genuinely is
// unavailable, and "this pathway is in edit mode under user <name>" is an
// internal detail the therapist cannot act on.
//
// Deliberately MODULE scope: the Review Smart Goal pathway check (declared
// early in the component, before the step-1 save handler) raises this popup
// too, and a module constant keeps one wording for all of them. The API
// returns this exact sentence as well, and this local copy is what guarantees
// the popup still shows it if the response body is lost or the server sends
// the legacy wording.
const PATHWAY_UNAVAILABLE_MESSAGE =
  "The clinical pathway relevant to this age is not available at the moment. Try after sometime.";

// -- Keyword tolerance for cross-pathway answer inheritance -------------------
// A Review Smart Goal pathway is a copy of the Review/Initial pathway with
// re-worded questions, so an exact question_text match is often impossible and
// the keyword alone is too brittle:
//   Initial "Neuroimaging"      -> RSG "Neuroimaging results"
//   Initial "Communication"     -> RSG "Communication skill"   (Current Skills)
//   Initial "Communication"     -> RSG "Communication"          (Comorbidities)
// Normalising drops the filler words that only describe *freshness* of the
// answer rather than its subject, so the two variants above collapse onto the
// same key. Filler is deliberately narrow: only words that qualify "give me a
// Keyword normalisation and the tiered keyword -> inherited-answer lookup now
// live in assessmentHistory.js, shared with Review-assessment.js so both
// pathways resolve an inherited answer identically.

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------

export default function Startassessment() {
  const location = useLocation();
  const navigate = useNavigate();
  // Memoised on location.search so the identity is stable between renders.
  // Anything that lists urlParams in a dependency array (e.g. historyClientId
  // below) would otherwise see a brand-new object on every single render.
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlAppointment = urlParams.get("appointment");

  const [session, setSession] = useState(normaliseAppointmentParam(urlAppointment));
  // The entry point that loaded this page (?appointment=Initial / Review /
  // Review Smart Goal). The step-1 appointment radio group only offers the
  // option that matches the entry point: "Start Initial Client Assessment"
  // shows Initial alone, "Set New Goal for Existing Client" shows Review
  // alone, and the goalreview "Review Assessment" button shows Review Smart
  // Goal alone. A direct /start-assessment visit with no param keeps all
  // three selectable (normaliseAppointmentParam("") returns "").
  const [entryAppointment, setEntryAppointment] = useState(normaliseAppointmentParam(urlAppointment));
  const [client, setClient] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [showInfantInfo, setShowInfantInfo] = useState(false);
  const [infantAnnouncement, setInfantAnnouncement] = useState("");
  const [pathwaysData, setPathwaysData] = useState([]);
  // True once get_pathway_details.php has ANSWERED (even with no rows), so the
  // Review Smart Goal pathway check below can tell "still loading" apart from
  // "this appointment has no pathway at all". Never set on a network failure -
  // a dead endpoint must not raise the missing-pathway popup.
  const [pathwaysLoaded, setPathwaysLoaded] = useState(false);
  const [activePathway, setActivePathway] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isRestoringDraft, setIsRestoringDraft] = useState(false);

  const targetNodeRef = useRef(null);

  const [answers, setAnswers] = useState({});
  const isMobile = window.innerWidth <= 768;

  const [hineData, setHineData] = useState([
    { id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }
  ]);
  const [haiData, setHaiData] = useState([
    { id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }
  ]);

  // Remembers the value each HINE / HAI score row had when it FIRST appeared in
  // this session (from the client-wide history stack, a draft restore, or the
  // + button). When that row is then EDITED, the ORIGINAL value is demoted one
  // row below (kept as an earlier score) instead of being overwritten, so the
  // pre-edit data survives and every later Review keeps showing it - the
  // "edit A -> becomes C" rule applied the moment the edit happens, not just
  // across sessions.
  const hineOriginRef = useRef({});
  const haiOriginRef = useRef({});

  const [interventionsData, setInterventionsData] = useState([]);
  const [activeGoalTab, setActiveGoalTab] = useState(null);

  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [step, setStep] = useState(1);
  const [isGoalDetailStep, setIsGoalDetailStep] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState({});
  const [isFavourite, setIsFavourite] = useState(false);
  const [openSections, setOpenSections] = useState({ demographics: false, background: false, comorbidities: false });

  // -- NEW: Share modal state ------------------------------------------------
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const summaryRef = useRef(null);
  const [toast, setToast] = useState(null);

  // -- NEW: Network issue popup state ----------------------------------------
  const [networkError, setNetworkError] = useState(false);

  // -- NEW: Goal Domain "Eating and Drinking" single-choice popup state ------
  const [showGoalDomainPopup, setShowGoalDomainPopup] = useState(false);

  // -- Review-flow state (merged from Review-assessment.js) -----------------
  const [reviewPathwaysData, setReviewPathwaysData] = useState([]);
  // Client names present on the "Review Existing Goals" page (sessions that
  // actually have saved goals). The Review / Review Smart Goal client dropdown
  // shows ONLY these clients - a Review appointment reviews existing goals, so
  // a client with no saved goals has nothing to review. null = eligibility
  // still loading (dropdown keeps all clients), [] = loaded, no eligible
  // client, [names] = loaded and restricted to the table's clients.
  const [goalsEligibleClients, setGoalsEligibleClients] = useState(null);
  const [selectedReviewSessionId, setSelectedReviewSessionId] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showReviewGoalScreen, setShowReviewGoalScreen] = useState(false);

  // -- "Previously recorded information" interstitial ------------------------
  // Shown ONLY between step 1 (Appointment / Client / Age group) and the 4th
  // question onwards, for the two existing-client appointments:
  //
  //   Review            "Set New Goal for Existing Client"
  //   Review Smart Goal goalreview -> "Review Assessment"
  //
  // It is a read-and-continue page: the therapist reads the centred note and
  // presses Next (enabled as soon as the page renders) to land on the pathway
  // questions. `step` stays 1 while it is up, so every step-1 rule (session
  // save, client/age validation, draft position) is untouched - only this flag
  // decides which of the two views the right-hand pane shows.
  const [showInfoIntro, setShowInfoIntro] = useState(false);

  // -- NEW: Assessment history for the selected client. Serves two purposes:
  // 1. the data-continuity pre-fill (Background Information & Assessments ->
  //    Comorbidities are carried over from the last completed session), and
  // 2. the "Previous Data" panel opened from the folder icon on every
  //    question-card, which lists every past Initial + Review session. -------
  const [initialHistory, setInitialHistory] = useState(null);
  const [groupHistory, setGroupHistory] = useState(null);
  const [historyGroupName, setHistoryGroupName] = useState(null);
  const [showHistoryFiles, setShowHistoryFiles] = useState(false);
  const [loadingGroupHistory, setLoadingGroupHistory] = useState(false);
  const [isDownloadingGroupData, setIsDownloadingGroupData] = useState(false);
  const groupHistoryPrintRef = useRef(null);
  const historyViewRef = useRef(null);

  // -- Review Smart Goal state ---------------------------------------------
  // `goalSessionId` is the originating session that holds the goal being
  // reviewed. It is carried from goalreview -> start-assessment ->
  // Interventions -> Updategoal -> goalsetdownload so the revision always
  // lands on the correct goal row.
  const [goalSessionId, setGoalSessionId] = useState(
    urlParams.get("goalSessionId") || urlParams.get("goalSession") || null
  );
  const [smartGoalContext, setSmartGoalContext] = useState(null);
  const [smartGoalLoading, setSmartGoalLoading] = useState(false);

  // -- Initial-assessment eligibility (Initial can only be done once) ------
  const [initialBlocked, setInitialBlocked] = useState(null);

  // -- ACTIVE GOAL CAPACITY (Set New Goal for Existing Clients) -------------
  // A client may hold only ONE Initial appointment but any number of Review
  // appointments - with one hard limit: once the client owns
  // MAX_ACTIVE_GOALS goals that are still "In progress - on track" or
  // "In progress - delayed", a new goal journey cannot be started until one of
  // the existing journeys is closed.
  //
  // `goalCapacityBlocked` holds the blocked payload (client + the in-progress
  // goals) that drives the popup; `goalCapacityChecking` marks the client that
  // is currently being verified so the dropdown cannot be re-picked meanwhile.
  const [goalCapacityBlocked, setGoalCapacityBlocked] = useState(null);
  const [goalCapacityChecking, setGoalCapacityChecking] = useState(null);

  // -- "Save for Later" -------------------------------------------------------
  // Quick-save: keeps the incomplete session and goes straight to
  // /continue-assessment with NO confirmation popup. The Save/Delete choice
  // lives entirely on the "Leave guard" popup below, so a therapist who
  // navigates away mid-assessment still gets the option to abandon the session.
  // `saveLaterBusyRef` blocks a double click from racing two saves+navigates.
  const saveLaterBusyRef = useRef(false);

  // -- "Leave guard" prompt ------------------------------------------------
  // The Save/Delete question, raised when the therapist walks away from an
  // in-progress assessment. The App-level AssessmentLeaveGuard bounces the
  // route back here and stashes where the therapist was heading (localStorage
  // via leaveSignal.js); this popup only lets the page leave for real after
  // Save / Delete, so answering questions can never silently evaporate on
  // navigation. `leavePromptBusy` disables both buttons while the request is in
  // flight so a double click cannot save-or-delete twice.
  const [leavePromptOpen, setLeavePromptOpen] = useState(false);
  const [leavePromptBusy, setLeavePromptBusy] = useState(false);
  const pendingLeaveDestRef = useRef(null);   // stashed destination, if any
  const inProgressRef = useRef(false);        // fresh copy for the beforeunload guard

  // -- Step-1 blockers that need a POPUP, not a toast ------------------------
  // Two server failures used to arrive as a transient toast:
  //
  //   pathway_locked           the pathway exists, but an admin has it open in
  //                             Edit mode
  //   pathway_not_available    the age was picked or auto-filled, but no pathway
  //                             of this appointment type exists for it
  //
  // Both stop the therapist from continuing, and both now raise ONE modal showing
  // one sentence - PATHWAY_UNAVAILABLE_MESSAGE - because while an admin holds the
  // pathway in Edit mode the pathway IS unavailable to the therapist. The admin's
  // username is not shown; it is logged to the console instead.
  //
  // The client-side Review Smart Goal pathway check (rsgPathwayMissing below)
  // raises the same modal when the SELECTED SESSION's age group has no
  // `review smart goal` pathway at all - same situation, same sentence - and,
  // like the server's pathway_not_available handler, withdraws the auto-fill
  // instead of leaving an unusable band in the dropdown.
  //
  // An age group that DOES have the pathway is auto-filled silently.
  //
  // A toast auto-dismisses and is easy to miss on a page this long.
  //
  // Shape: { kind, message, ageGroup? }
  const [pathwayBlocked, setPathwayBlocked] = useState(null);

  // Review Smart Goal only. True while the Age Group recorded on the SELECTED
  // SESSION has no `review smart goal` clinical pathway (spec: the assessment
  // must STOP). The popup above explains it; this flag keeps step-1 Next dead
  // even after the popup is dismissed, until a band WITH a pathway is in
  // effect. Cleared automatically when the appointment is not Review Smart
  // Goal. The check effect also CLEARS the band itself in that case, so an
  // unusable band is never left auto-filled in the Age Group dropdown.
  const [rsgPathwayMissing, setRsgPathwayMissing] = useState(false);

  // The band already REJECTED for having no `review smart goal` pathway. The
  // auto-fill sources below (the ?age= prefill and the client picker) re-run
  // whenever the review client lists land - they are separate fetches from the
  // pathway list - and re-applying a rejected band would flash the Age Group
  // back and re-raise the popup its rejection had just shown.
  const rejectedRsgAgeRef = useRef(null);

  // "Review" and "Review Smart Goal" both run the `review` pathway.
  const isReviewMode = session === APPT_REVIEW || session === APPT_REVIEW_SMART_GOAL;
  const isSmartGoalMode = session === APPT_REVIEW_SMART_GOAL;

  const token = localStorage.getItem("token");
  const activeStepRef = useRef(null);
  const lastAppointmentSessionRef = useRef(session);

  // -- NEW: Register this component so any API call (including the ones in
  // saveResponseAPI / saveHineHaiAPI outside the component) can surface a
  // network-issue popup immediately when a request times out or fails ------
  useEffect(() => {
    globalNetworkErrorHandler = () => setNetworkError(true);
    return () => { globalNetworkErrorHandler = null; };
  }, []);

  // -- Retry pending responses on mount -------------------------------------
  useEffect(() => { retryPendingResponses(); }, []);

  // -- Native exit prompt -----------------------------------------------------
  // A custom Save/Delete popup is impossible in beforeunload, so a full page
  // exit (tab close / reload) only gets this native prompt - at least the exit
  // is deliberate. (In-app navigation uses the AssessmentLeaveGuard +
  // leave-prompt popup instead.) An in-progress run can be resumed later from
  // the server-side draft ("Save for Later" / leave-prompt Save ->
  // /continue-assessment), not from localStorage.
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (inProgressRef.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => { window.removeEventListener("beforeunload", handleBeforeUnload); };
  }, []);

  // -- Purge any legacy localStorage snapshot --------------------------------
  // This page used to persist an `assessmentState` snapshot and restore it on
  // the next visit. That resurrected a finished or abandoned run (the previous
  // client, answers, step and even showReviewGoalScreen) instead of a blank
  // form, and it leaked into the pages that still read `assessmentState` as a
  // fallback (summarypage / Interventions / goalreview / goalsetdownload).
  // The save + restore is gone; drop any snapshot still sitting in the browser
  // so it can never be picked up again. Resume is handled server-side through
  // /continue-assessment instead.
  useEffect(() => {
    try { localStorage.removeItem("assessmentState"); } catch (e) { /* private mode - best effort */ }
  }, []);

  // -- The appointment param pre-selects the initial/review option on step 1.
  // It is KEPT in the URL (normalised) so a browser reload restores the same
  // appointment page instead of an empty step-1 shell. This runs on every URL
  // change (not just mount) so switching between "Start Initial Client
  // Assessment" and "Review Session to Set New Goal" from the header/cards
  // while the component stays mounted also re-selects the right option.
  // Crossing between Initial and Review resets the step-1 form so no stale
  // state carries over. --
  useEffect(() => {
    lastAppointmentSessionRef.current = session;
  }, [session]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlAppointment = params.get("appointment");
    if (!urlAppointment) return;
    const desired = normaliseAppointmentParam(urlAppointment);
    if (!desired) return;
    // Lock the step-1 appointment radio group to this entry point - the
    // header/dashboard card that loaded the page already decided the session
    // type, so the other two options are hidden for the whole visit.
    setEntryAppointment(desired);
    const hasClientParam = params.get("client");
    // Keep the appointment so the URL is itself the reload handle; only
    // rewrite the address when the raw spelling needs normalising.
    if (urlAppointment !== desired) {
      params.set("appointment", desired);
      const qs = params.toString();
      navigate(qs ? `/start-assessment?${qs}` : "/start-assessment", { replace: true });
    }

    setSession(desired);
    // The Review Smart Goal entry point always carries the originating goal
    // session, so keep it in state when landing on that pathway.
    if (desired === APPT_REVIEW_SMART_GOAL) {
      const gsid = params.get("goalSessionId") || params.get("goalSession");
      if (gsid) setGoalSessionId(gsid);
    }
    if (lastAppointmentSessionRef.current !== desired) {
      setShowReviewGoalScreen(false);
      setStep(1);
      setClientSearchTerm("");
      setInitialBlocked(null);
      setGoalCapacityBlocked(null);
      setGoalCapacityChecking(null);
      if (desired !== APPT_REVIEW_SMART_GOAL) setGoalSessionId(null);
      if (!hasClientParam) {
        setClient("");
        setAgeRange("");
      } else {
        // A ?client= carry-over must NEVER auto-fill the Age Group for a NEW
        // appointment - the ?age= link param is deliberately ignored by the
        // ?client= prefill below for plain Review, and any band left over from
        // the appointment this page was showing before the URL changed (e.g. a
        // stale Initial or Review Smart Goal band on this mount) is dropped
        // here. Review Smart Goal's own band is then re-filled from ?age= by
        // the prefill effect (or by the draft when resuming).
        setAgeRange("");
      }
      setAnswers({});
      setHineData([{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
      setHaiData([{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleSessionChange = (e) => {
    const value = e.target.value;
    setSession(value);
    setClientSearchTerm("");
    setShowReviewGoalScreen(false);
    setInitialBlocked(null);
    setGoalCapacityBlocked(null);
    setGoalCapacityChecking(null);
    if (value !== APPT_REVIEW_SMART_GOAL) setGoalSessionId(null);

    // When arriving from the goalreview "Review Assessment" button the URL
    // carries a client name (?client=<name>). Switching away from the
    // review-based options must drop that prefilled carryover so the Initial
    // form starts fresh.
    if (!isReviewOption(value)) {
      const params = new URLSearchParams(location.search);
      if (params.get("client")) {
        setClient("");
        setAgeRange("");
        params.delete("client");
        params.delete("appointment");
        params.delete("goalSessionId");
        params.delete("goalSession");
        const qs = params.toString();
        navigate(qs ? `/start-assessment?${qs}` : `/start-assessment`, { replace: true });
      }
    }
  };
  // -- Fetch draft from API when sessionId is in URL ------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get("sessionId");
    if (!sessionId || !token) return;

    const fetchDraft = async () => {
      setIsRestoringDraft(true);
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_draft_session_detail.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ sessionId: parseInt(sessionId) })
          }
        );
        const json = await response.json();
        if (!json.status || !json.data) {
          if (isTokenError(json)) { dispatchSessionExpired(); return; }
          return;
        }

        const draftData = json.data;

        setCurrentSessionId(draftData.sessionId);

        setClient(draftData.client_name || "");
        setAgeRange(draftData.age || "");
        setIsFavourite(Boolean(draftData.is_favourite));

        // Restore the EXACT session mode. "Review" and "Review Smart Goal" both
        // run the `review` pathway, so the pathway value cannot tell them apart
        // - collapsing on `!== "initial"` reopened every Review Smart Goal draft
        // in plain Review mode. get_draft_session_detail.php returns the
        // per-session label persisted in node 1; appointment_key /
        // appointment_label are authoritative, with the old fields kept as
        // fallbacks for drafts written before the mode was recorded.
        let sessionType = APPT_INITIAL;
        const modeKey = String(draftData.appointment_key || "").trim().toLowerCase();
        if (modeKey === "review_smart_goal") {
          sessionType = APPT_REVIEW_SMART_GOAL;
        } else if (modeKey === "review") {
          sessionType = APPT_REVIEW;
        } else if (modeKey === "initial") {
          sessionType = APPT_INITIAL;
        } else {
          const modeLabel = String(draftData.appointment_label || "").trim();
          if (/smart/i.test(modeLabel)) {
            sessionType = APPT_REVIEW_SMART_GOAL;
          } else if (/review/i.test(modeLabel)) {
            sessionType = APPT_REVIEW;
          } else if (draftData.appointment_type) {
            sessionType = String(draftData.appointment_type).toLowerCase() === "initial"
              ? APPT_INITIAL
              : APPT_REVIEW;
          } else if (draftData.appt_id !== undefined) {
            sessionType = draftData.appt_id === 1 ? APPT_INITIAL : APPT_REVIEW;
          }
        }
        setSession(sessionType);

        // Resuming an existing REVIEW session should skip the step-1 setup
        // screen (whose "Next" would create a NEW review session and push the
        // user back to Goal Progress Update / goalreview). Jump straight into
        // the review questions instead; the last_node effect overrides this
        // step when the draft has a recorded position.
        //
        // BOTH review modes must skip step 1. This compared against the literal
        // "Review", so a "Review Smart Goal" draft fell through, dropped the
        // therapist back onto the step-1 form and re-rendered the appointment
        // selector as a plain Review.
        if (isReviewOption(sessionType)) {
          setShowReviewGoalScreen(false);
          setStep(2);
        }

        const newAnswers = {};
        const draftPathway = draftData.pathway || [];
        let restoredHineData = [];
        let restoredHaiData = [];

        const normaliseAnswer = (raw) => {
          if (raw && typeof raw === "object" && !Array.isArray(raw)) {
            return {
              value: raw.value ?? null,
              entries: Array.isArray(raw.entries) ? raw.entries : []
            };
          }
          if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
            return { value: "Yes", entries: raw };
          }
          if (raw !== null && raw !== undefined && !Array.isArray(raw)) {
            return { value: String(raw), entries: [] };
          }
          return { value: null, entries: [] };
        };

        const restoreSubOptions = (node, pathLabels, suboptions, parentInstruction = "") => {
          if (!suboptions || suboptions.length === 0) return;

          const instructions = parseInstructions(parentInstruction);
          const isMulti = instructions.isMulti;

          const selectedSubs = suboptions.filter(sub => sub.selected === true);
          if (selectedSubs.length === 0) return;

          const subKey = [node, ...pathLabels, "sub"].join("_");

          if (isMulti) {
            newAnswers[subKey] = selectedSubs.map(s => s.label);
          } else {
            if (selectedSubs.length === 1) {
              newAnswers[subKey] = selectedSubs[0].label;
            } else {
              newAnswers[subKey] = selectedSubs.map(s => s.label);
            }
          }

          selectedSubs.forEach(sub => {
            restoreSubOptions(node, [...pathLabels, sub.label], sub.suboptions, sub.instruction || "");
          });
        };

        draftPathway.forEach(p => {
          const node = p.node;

          const qTypeRaw = (p.type || "").toString().toUpperCase().trim();
          const isHINEQuestion = qTypeRaw === "HINE";
          const isHAIQuestion = qTypeRaw === "HAI";

          const { value: answerValue, entries } = normaliseAnswer(p.answer);

          if (isHINEQuestion) {
            if (entries.length > 0) {
              restoredHineData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                hineScore: item.hine_score !== undefined ? item.hine_score : 0,
                rightAsym: item.right_asymmetry !== undefined ? item.right_asymmetry : 0,
                leftAsym: item.left_asymmetry !== undefined ? item.left_asymmetry : 0
              }));
            }
            if (answerValue) newAnswers[node] = answerValue;
            return;
          }

          if (isHAIQuestion) {
            if (entries.length > 0) {
              restoredHaiData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                rightEahs: item.right_score !== undefined ? item.right_score : 0,
                leftEahs: item.left_score !== undefined ? item.left_score : 0,
                eachHandSumScore: item.sum !== undefined ? item.sum : 0,
                asymmetryIndex: item.asymmetry !== undefined ? item.asymmetry : 0
              }));
            }
            if (answerValue) newAnswers[node] = answerValue;
            return;
          }

          if (entries.length > 0) {
            const first = entries[0];
            if (Object.prototype.hasOwnProperty.call(first, "hine_score")) {
              restoredHineData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                hineScore: item.hine_score !== undefined ? item.hine_score : 0,
                rightAsym: item.right_asymmetry !== undefined ? item.right_asymmetry : 0,
                leftAsym: item.left_asymmetry !== undefined ? item.left_asymmetry : 0
              }));
              if (answerValue) newAnswers[node] = answerValue;
              return;
            }
            if (Object.prototype.hasOwnProperty.call(first, "right_score")) {
              restoredHaiData = entries.map((item, idx) => ({
                id: idx,
                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                rightEahs: item.right_score !== undefined ? item.right_score : 0,
                leftEahs: item.left_score !== undefined ? item.left_score : 0,
                eachHandSumScore: item.sum !== undefined ? item.sum : 0,
                asymmetryIndex: item.asymmetry !== undefined ? item.asymmetry : 0
              }));
              if (answerValue) newAnswers[node] = answerValue;
              return;
            }
          }

          if (p.options && Array.isArray(p.options)) {
            const activeOptions = p.options.filter(opt =>
              opt.selected === true ||
              (opt.suboptions &&
                Array.isArray(opt.suboptions) &&
                opt.suboptions.some(sub => sub.selected === true))
            );

            const goalDomainQuestion = isGoalDomainQuestion(p);

            if (activeOptions.length === 1) {
              const isAnswerArray = Array.isArray(p.answer);
              newAnswers[node] = (goalDomainQuestion && isAnswerArray)
                ? [activeOptions[0].label]
                : activeOptions[0].label;

              restoreSubOptions(node, [activeOptions[0].label], activeOptions[0].suboptions, activeOptions[0].instruction || "");
            } else if (activeOptions.length > 1) {
              newAnswers[node] = activeOptions.map(opt => opt.label);
              activeOptions.forEach(opt =>
                restoreSubOptions(node, [opt.label], opt.suboptions, opt.instruction || "")
              );
            }
          }
        });

        setAnswers(newAnswers);
        if (restoredHineData.length > 0) { recordHineOrigins(restoredHineData); setHineData(restoredHineData); }
        if (restoredHaiData.length > 0) { recordHaiOrigins(restoredHaiData); setHaiData(restoredHaiData); }

        if (draftData.last_node) {
          targetNodeRef.current = draftData.last_node;
          console.log("?? Draft last_node stored:", draftData.last_node);
        }
      } catch (error) {
        console.error("Failed to fetch draft:", error.message);
      } finally {
        setIsRestoringDraft(false);
      }
    };

    fetchDraft();
  }, [location.search, token]);

  // -- Which create_pathway.appointment_type the current appointment runs.
  // get_pathway_details.php filters the WHOLE response by this value, so both
  // the pathway that gets hydrated AND the age groups offered in the step-1
  // "Age group" dropdown are exactly the ones that have a pathway of the
  // active appointment type:
  //
  //   Initial          -> "initial"           (initialAgeGroups)
  //   Review           -> "review"            (reviewAgeGroups)
  //   Review Smart Goal-> "review smart goal" (reviewAgeGroups)
  //
  // The two review types are deliberately NOT merged: each one only ever shows
  // the age groups that actually have a pathway of ITS OWN kind, so Review
  // never offers an age group that can only be reviewed as a Smart Goal.
  //
  // An empty appointment (a direct /start-assessment visit with no
  // ?appointment= param, where the step-1 radio group still offers all three)
  // sends nothing, so the endpoint stays unfiltered for that visit and the
  // dropdown can be filled once the therapist picks a type.
  const pathwayAppointmentFilter = useMemo(() => {
    if (isSmartGoalMode) return "review smart goal";
    if (session === APPT_REVIEW) return "review";
    if (session === APPT_INITIAL) return "initial";
    return "";
  }, [session, isSmartGoalMode]);

  // -- Fetch pathway data ----------------------------------------------------
  useEffect(() => {
    const fetchPathways = async () => {
      // Refetching (the appointment filter changed) puts the list back in the
      // loading state, so Review Smart Goal's pathway check can never run
      // against the PREVIOUS appointment's list.
      setPathwaysLoaded(false);
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_pathway_details.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify(pathwayAppointmentFilter ? { appointment_type: pathwayAppointmentFilter } : {}),
          }
        );
        const json = await response.json();
        if (json.status && json.data && Array.isArray(json.data)) {
          setPathwaysData(json.data);
          setPathwaysLoaded(true);
        } else if (json.status && json.pathways && Array.isArray(json.pathways)) {
          setPathwaysData(json.pathways);
          setPathwaysLoaded(true);
        } else if (json.status) {
          // A successful answer with no rows: there genuinely are no pathways
          // for this appointment. Loaded-but-empty, NOT "still loading", so
          // Review Smart Goal raises its missing-pathway popup instead of
          // waiting forever behind a spinner-less blank form.
          setPathwaysData([]);
          setPathwaysLoaded(true);
        }
      } catch (error) { console.error("Fetch pathways failed:", error.message); }
    };
    if (token) fetchPathways();
  }, [token, pathwayAppointmentFilter]);

  // -- NEW: Fetch review pathway details (client-grouped, multi-session).
  // This powers the searchable client dropdown on step 1 when Review is
  // selected, and lets us resolve the correct sessionId for Goal Progress
  // Update. ----------------------------------------------------------------
  useEffect(() => {
    const fetchReviewPathways = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_review_pathway_details.php`,
          { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` } }
        );
        const json = await response.json();
        if (json.status && json.data && Array.isArray(json.data)) {
          setReviewPathwaysData(json.data);
        }
      } catch (error) { console.error("Fetch review pathways failed:", error.message); }
    };
    if (token) fetchReviewPathways();
  }, [token]);

  // -- Fetch the client list that the "Review Existing Goals" table shows ---
  // get_goals_page.php is the exact source of that table, so Review-flow
  // client eligibility can never drift from what the table actually displays.
  // While the list is still loading (null) the dropdown keeps all clients so
  // step 1 never unlocks empty; once loaded it is strictly limited to the
  // clients who have saved goals.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchGoalsClients = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_goals_page.php`,
          { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify({}) }
        );
        const json = await response.json();
        if (cancelled) return;
        if (json.status && Array.isArray(json.data)) {
          const names = json.data
            .map(row => row && row.client)
            .filter(Boolean);
          setGoalsEligibleClients([...new Set(names)].sort());
        } else {
          // Endpoint answered but unusable - keep the full list rather than
          // blanking the whole dropdown.
          setGoalsEligibleClients(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Fetch goals-eligible clients failed:", error.message);
          setGoalsEligibleClients(null);
        }
      }
    };
    fetchGoalsClients();
    return () => { cancelled = true; };
  }, [token]);

  // -- Age groups for the dropdown ? only include age groups that have
  // at least one pathway whose appointment_type is "initial" ---------------
  const initialAgeGroups = useMemo(() => {
    return pathwaysData.filter(
      (ageObj) =>
        Array.isArray(ageObj.pathways) &&
        ageObj.pathways.some((p) => p.appointment_type === "initial")
    );
  }, [pathwaysData]);

  // -- All age groups that have at least one "review" OR "review smart goal"
  // pathway (mirrored from Review-assessment.js), used in step 1 when Review or
  // Review Smart Goal is selected.
  //
  // STRICT per appointment type: Review lists only the age groups that have a
  // `review` pathway and Review Smart Goal only the ones that have a
  // `review smart goal` pathway. They used to share one list that accepted
  // either type, so a Review could be started on an age group that only has an
  // RSG pathway (and vice versa) - the pathway then silently fell back to the
  // other kind. `get_pathway_details.php` is already filtered by the active
  // appointment, so this only re-asserts the same rule on the client side.
  const reviewAgeGroups = useMemo(() => {
    const wanted = isSmartGoalMode ? "review smart goal" : "review";
    return pathwaysData.filter(
      (ageObj) =>
        Array.isArray(ageObj.pathways) &&
        ageObj.pathways.some((p) => (p.appointment_type || "").toLowerCase() === wanted)
    ).map(ageObj => ageObj.age_group);
  }, [pathwaysData, isSmartGoalMode]);

  // -- Review-client eligibility (only clients in "Review Existing Goals") --
  // While the eligibility list is still loading (null) the raw list is used so
  // the form stays usable; once loaded, every client picker / label / age /
  // session resolution below reads ONLY eligibleReviewPathwaysData.
  const eligibleClientSet = useMemo(() => {
    return new Set(
      Array.isArray(goalsEligibleClients)
        ? goalsEligibleClients.map(n => String(n).trim().toLowerCase())
        : []
    );
  }, [goalsEligibleClients]);

  const eligibleReviewPathwaysData = useMemo(() => {
    const raw = reviewPathwaysData || [];
    if (!Array.isArray(goalsEligibleClients)) {
      // Eligibility still loading - keep current behaviour.
      return raw;
    }
    return raw.filter(
      c => c && eligibleClientSet.has(String(c.client_name || "").trim().toLowerCase())
    );
  }, [reviewPathwaysData, goalsEligibleClients, eligibleClientSet]);

  // -- Derived list of the eligible client names from get_review_pathway_details.php,
  // used to power the searchable client dropdown on step 1 (Review flow). --
  const reviewClientOptions = useMemo(() => {
    if (!eligibleReviewPathwaysData || eligibleReviewPathwaysData.length === 0) return [];
    return eligibleReviewPathwaysData
      .map(c => c.client_name)
      .filter(Boolean);
  }, [eligibleReviewPathwaysData]);

  // -- Spec: the Review client dropdown is labelled "[Client Name - Client Code]"
  // so two clients sharing a name stay distinguishable, and so the therapist can
  // match the row against the ID printed on the summ-container1 header.
  //
  // `reviewClientOptions` deliberately stays name-only: the name is what the
  // `client` state, the `?client=` URL param and save_basic_details.php expect.
  // The code is presentation only. Sessions come back newest-first
  // (ORDER BY ps.saved_at DESC), so the first non-empty code is the current one.
  const reviewClientLabels = useMemo(() => {
    const labels = {};
    (eligibleReviewPathwaysData || []).forEach(c => {
      if (!c || !c.client_name) return;
      const sessions = Array.isArray(c.sessions) ? c.sessions : [];
      const code = sessions
        .map(s => (s && (s.client_code ?? s.clientCode)) || "")
        .map(v => String(v).trim())
        .find(Boolean) || String(c.client_code || "").trim();
      labels[c.client_name] = code ? `${c.client_name} - ${code}` : c.client_name;
    });
    return labels;
  }, [eligibleReviewPathwaysData]);

  // -- Resolve the client's existing REVIEW age group ---------------------
  // A review flow must keep the client's review age even if a different age
  // is picked, so duplicate review sessions aren't created. Returns the age
  // group of the client's most recently created session OF THIS APPOINTMENT
  // TYPE (or "").
  // If no session of that type exists, falls back to the client's original age
  // group from their Initial session, since the client may have aged into a new
  // group.
  //
  // Review and Review Smart Goal are resolved SEPARATELY. This used to accept
  // both `review` and `review smart goal` sessions, so opening Review Smart
  // Goal pre-selected the age group of a plain Review session (and vice versa)
  // - an age group the RSG dropdown may not even offer, which then resolved to
  // a different pathway. `type` defaults to the appointment currently running.
  const getReviewAgeForClient = (name, type = (isSmartGoalMode ? "review smart goal" : "review")) => {
    if (!name || !eligibleReviewPathwaysData || eligibleReviewPathwaysData.length === 0) return "";
    const clientObj = eligibleReviewPathwaysData.find(c => c.client_name === name);
    if (!clientObj) return "";
    const sessions = Array.isArray(clientObj.sessions) ? clientObj.sessions : [];

    // 1. THE CLIENT'S REGISTERED AGE BAND - authoritative and stable.
    //
    // This must come first. It used to be derived from the newest session, which
    // made the lookup self-perpetuating: pick the wrong age band once in the
    // step-1 dropdown, that session is created, and because it is now the newest
    // it reports its own age band back forever. Client "First Oct three" is
    // registered 06-12 months but one Review was saved against 02-03 years
    // (clpa 221), after which EVERY Review auto-selected 02-03 years and loaded
    // a completely different question set. clients.age_group is written once at
    // registration and never changes, so it cannot drift.
    const registered = String(clientObj.age_group || "").trim();
    if (registered) return registered;

    const newestOf = (list) => list.reduce((a, b) => {
      const sa = a.sessionId != null ? a.sessionId : a.session_id;
      const sb = b.sessionId != null ? b.sessionId : b.session_id;
      return Number(sb) > Number(sa) ? b : a;
    });

    // 2. Fall back to the Initial session's band. The Initial is run exactly once
    //    per client, so unlike a Review session its value cannot drift either.
    const initialSessions = sessions.filter(
      s => (s.appointment_type || s.type || "").toLowerCase() === "initial"
    );
    if (initialSessions.length > 0) {
      const age = newestOf(initialSessions).age_group || newestOf(initialSessions).age || "";
      if (age) return age;
    }

    // 3. Last resort: the newest session already on this appointment's pathway.
    const reviewSessions = sessions.filter(
      s => (s.appointment_type || s.type || "").toLowerCase() === type
    );
    if (reviewSessions.length > 0) {
      return newestOf(reviewSessions).age_group || newestOf(reviewSessions).age || "";
    }
    return "";
  };

  // NOTE: the review session is resumed server-side by save_basic_details.php
  // (it looks for an in-progress session for this client + appointment type), so
  // there is no client-side "find the review session id" helper here any more.

  // -- Prefill the client (and, for Review Smart Goal ONLY, the age) when
  // arriving with ?client=<name> from goalreview's "Review Assessment" button
  // (?appointment=Review Smart Goal) or from the one-time-Initial guard. The
  // client name needs reviewClientOptions to be loaded, so this runs once the
  // review pathway data is available. The appointment type already chosen in
  // the URL always wins - this effect only fills in the blanks, and a NEW
  // Review appointment's Age Group is never one of them (manual selection). --
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const urlClient = params.get("client");
    if (!urlClient) return;
    /*
     * A RESUMED SESSION OWNS ITS OWN AGE GROUP.
     *
     * The reviewClientOptions re-run below is an asynchronous fetch, so it can
     * land AFTER the draft-restore effect has already read the session's real
     * age group out of get_draft_session_detail.php. Writing ?age= over it
     * silently swapped the pathway: the session was created on one clpa (so all
     * of its answers sit at that pathway's node numbers) while the questions on
     * screen came from the other one. Every pre-filled answer then looked
     * unanswered - currentSessionNodes is keyed on the SESSION's nodes and none
     * of them line up - so the pre-fill stamped the source session's answers on
     * top of the therapist's own, and the next save wrote the wrong pathway's
     * node numbers into the session.
     *
     * The age band decides which pathway runs, so it is part of the session's
     * identity, not a display preference. When ?sessionId= is present the
     * session is authoritative and the link parameter is ignored.
     *
     * Deliberately runs BEFORE the client-match gates: the one-time Initial
     * guard hands over a client who may not be in the eligible dropdown yet
     * (no saved goals), and the Review Smart Goal band still has to come from
     * the ?age= of the exact session being reviewed in that case.
     */
    const resuming = Boolean(params.get("sessionId"));
    if (!resuming) {
      const requestedAppointment = normaliseAppointmentParam(params.get("appointment"));
      if (requestedAppointment === APPT_REVIEW_SMART_GOAL) {
        /*
         * REVIEW SMART GOAL: the band comes from the EXACT session the
         * therapist clicked "Review Assessment" on - goalreview sends it as
         * ?age=. It is NEVER derived from the client (getReviewAgeForClient
         * would return the registered band, which is a different session's
         * data). With no ?age= on the link the dropdown simply stays empty
         * and the therapist picks the band manually.
         *
         * This is an AUTO-FILL, so it is conditional: a band whose
         * `review smart goal` pathway does not exist is refused by the check
         * effect below (popup + value cleared) and recorded in
         * rejectedRsgAgeRef, and it is never written back here however often
         * this effect re-runs.
         */
        const urlAge = params.get("age");
        if (urlAge && rejectedRsgAgeRef.current !== urlAge) setAgeRange(urlAge);
      }
      // NEW REVIEW APPOINTMENT ("Set New Goal for Existing Clients"): the Age
      // Group must ALWAYS be selected by hand. ?age= (e.g. the one-time
      // Initial guard's "Review - Set New Goal" button) and every derived band
      // are deliberately ignored, so once the client arrives the dropdown is
      // empty until the therapist chooses - and step-1 Next stays disabled
      // until Client + Age Group are both set. Any stale band from a previous
      // appointment on this mount was cleared by the URL effect above.
    }

    if (reviewClientOptions.length === 0) return;
    const match = reviewClientOptions.find(
      (name) => String(name).trim().toLowerCase() === String(urlClient).trim().toLowerCase()
    );
    if (!match) return;
    // Default to Review when nothing was requested (backwards compatible with
    // the old "Review Assessment" link), otherwise keep the requested option.
    if (!isReviewOption(session)) setSession(APPT_REVIEW);
    setClient(match);
    // Show the same "Name - Code" label the dropdown renders, so an
    // auto-selected client does not leave a differently-formatted box behind.
    setClientSearchTerm(reviewClientLabels[match] || match);
    setShowClientDropdown(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, reviewClientOptions, reviewClientLabels, token]);

  // -- Resolve the correct sessionId for the selected client + age group
  // combination so "Goal Progress Update" navigates with the session that
  // actually holds the intervention-planning goals (Goal Domain, Target
  // Date, Selected Interventions). Those goals were saved against the
  // client's INITIAL session, so the goal review must open with that session
  // - NOT with a freshly created review session, which has no goals saved
  // yet and would render "No goals have been saved for this session."
  //
  // Prefers the initial session for the matching age group, but falls back
  // to any other session so the sessionId is never null as long as the
  // client has any session. -----------------------------------------------
  useEffect(() => {
    if (!eligibleReviewPathwaysData || eligibleReviewPathwaysData.length === 0 || !client) {
      setSelectedReviewSessionId(null);
      return;
    }
    const clientObj = eligibleReviewPathwaysData.find(c => c.client_name === client);
    if (!clientObj) { setSelectedReviewSessionId(null); setSelectedClientId(null); return; }
    setSelectedClientId(clientObj.client_id != null ? clientObj.client_id : null);
    const sessions = Array.isArray(clientObj.sessions) ? clientObj.sessions : [];
    const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();
    const getAge = (s) => norm(s.age_group || s.age);
    const getSid = (s) => (s.sessionId != null ? s.sessionId : s.session_id != null ? s.session_id : null);
    const isInitial = (s) =>
      (s.appointment_type || "").toLowerCase() === "initial" ||
      (s.type || "").toLowerCase() === "initial";
    const pick = (list) => list.find(isInitial) || list[0];
    const matchingSessions = ageRange ? sessions.filter(s => getAge(s) === norm(ageRange)) : [];
    // First try matching age, then fall back to all sessions
    let goalSession = (matchingSessions.length > 0 && pick(matchingSessions)) || pick(sessions);
    // If the picked session is a Review, prefer any Initial session instead
    // because goals/interventions are always stored in the Initial session
    if (goalSession && !isInitial(goalSession)) {
      const initialSession = sessions.find(isInitial);
      if (initialSession) goalSession = initialSession;
    }
    setSelectedReviewSessionId(goalSession ? getSid(goalSession) : null);
  }, [eligibleReviewPathwaysData, client, ageRange]);

  // -- NEW: Fetch the selected client's full assessment history once. Powers
  // both the data-continuity pre-fill and the "Previous Data" panel. --------
  useEffect(() => {
    let cancelled = false;
    const clientId = selectedClientId;
    if (!clientId || !token) { setInitialHistory(null); return; }
    (async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_historical_pathway_data.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ client_id: parseInt(clientId), group_name: "" }),
          }
        );
        const json = await response.json();
        if (json.status && !cancelled) setInitialHistory(json);
      } catch (error) {
        console.error("Fetch initial history failed:", error.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, token]);

  // -- PREVIOUS DATA: the client_id the history panel reads for, resolved from
  // the Review step-1 picker or from the goalreview hand-off in the URL. ----
  const historyClientId = useMemo(() => {
    if (selectedClientId) return parseInt(selectedClientId);
    const fromUrl = urlParams.get("clientId");
    const parsed = parseInt(fromUrl, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [selectedClientId, urlParams]);

  // -- PREVIOUS DATA: fetch the answers for one group across every past
  // Initial + Review session of this client. -------------------------------
  const fetchGroupHistory = async (clientId, groupName) => {
    if (!clientId || !token) return;
    setLoadingGroupHistory(true);
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/get_historical_pathway_data.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ client_id: parseInt(clientId), group_name: groupName || "" }),
        }
      );
      const json = await response.json();
      if (json.status) setGroupHistory(json);
      else if (isTokenError(json)) { dispatchSessionExpired(); return; }
    } catch (error) {
      console.error("Fetch group history failed:", error.message);
    } finally {
      setLoadingGroupHistory(false);
    }
  };

  const handleGroupHistoryClick = (groupName) => {
    const next = historyGroupName === groupName ? null : groupName;
    setHistoryGroupName(next);
    if (next && next !== historyGroupName) fetchGroupHistory(historyClientId, next);
  };

  // -- PREVIOUS DATA: "Previous Page" goes back one level WITHIN the view - from
  // an open group's tables to the group file bar. It must NOT call handleBack:
  // handleBack only moves the assessment step, so the click left the therapist
  // trapped in the history view on the same group - and in Review mode on the
  // first question (step 2) it did nothing at all, because handleBack returns
  // early there to avoid dropping into the step-1 setup screen.
  const handleHistoryBack = () => {
    setHistoryGroupName(null);
  };

  // -- PREVIOUS DATA: "X" closes the view entirely and hands the whole
  // assessment area back to the questionnaire. Always available while the pane
  // is open, including when only the group bar is showing, so the full-width
  // pane can never become a dead end.
  const handleCloseHistory = () => {
    setHistoryGroupName(null);
    setShowHistoryFiles(false);
  };

  // -- PREVIOUS DATA: ordered group list for the file-icon bar. Demographics
  // is hoisted to the front because it is the usual entry point. -----------
  const orderedHistoryGroups = useMemo(
    () => orderHistoryGroups(initialHistory, activePathway?.groups),
    [initialHistory, activePathway]
  );

  // -- PREVIOUS DATA: the folder icon on the question-card toggles the panel
  // and opens the first group immediately so it is never an empty view. ----
  const handleHistoryFileClick = () => {
    const willShow = !showHistoryFiles;
    setShowHistoryFiles(willShow);
    if (willShow && orderedHistoryGroups.length > 0) {
      const firstGroup = orderedHistoryGroups[0].group_name;
      setHistoryGroupName(firstGroup);
      if (historyClientId) fetchGroupHistory(historyClientId, firstGroup);
    } else {
      setHistoryGroupName(null);
    }
  };

  // -- PREVIOUS DATA: scroll the inline history into view when it opens. ----
  useEffect(() => {
    if (historyGroupName) {
      historyViewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [historyGroupName]);

  // -- NEW: Map question text ? the answer carried over from the session we
  // inherit from, so review steps can auto-select the same option. HINE / HAI
  // answers carry { value, entries } where entries hold the score rows (only
  // when the inherited answer was "Yes").
  //
  // Data Continuity: the source is the LAST COMPLETED session of any type -
  // once a client has a finished Review, that Review is what the next Review
  // inherits from. The Initial is only the fallback. ------------------------
  // The session being answered is excluded: it is normally the newest row in
  // initialHistory, so without this the "latest session to inherit from" is the
  // current one and nothing is actually inherited.
  //
  // ageRange is passed as the third argument so inheritance is restricted to
  // sessions on the SAME age band's pathway. A session's questions come from its
  // pathway, so inheriting across bands copies answers between two different
  // question sets - and since each appointment inherits from the newest one, a
  // single session saved against the wrong dropdown keeps poisoning the chain.
  const inheritSession = useMemo(
    () => pickInheritanceSession(initialHistory, currentSessionId, ageRange),
    [initialHistory, currentSessionId, ageRange]
  );

  // -- Data Continuity: which session this assessment inherits answers from.
  //
  // Plain Review / Initial keep the Data Continuity rule above (the last
  // completed session of any type wins).
  //
  // Review Smart Goal is different by definition: the therapist arrived from a
  // specific row of "Review Existing Goals", so ?goalSessionId names the session
  // that actually owns the goal being reviewed. Its Background Information &
  // Assessments / Current Skills / Comorbidities answers are the ones the goal
  // was written against, and inheriting from a newer unrelated Review instead
  // would silently pre-fill the wrong history.
  //
  // Everything downstream (the three lookup maps and the pre-fill pass itself)
  // reads this one value, so the maps and the answers can never disagree about
  // which session is the source.
  const sourceSession = useMemo(() => {
    if (!isSmartGoalMode || !goalSessionId) return inheritSession;
    const wanted = String(goalSessionId);
    const sessions = Array.isArray(initialHistory?.data) ? initialHistory.data : [];
    // Exact match on the originating session - whatever its appointment type.
    // This used to try `appointment_type === 'initial'` FIRST, which reads as if
    // the Initial session were the preferred source; because session_id is
    // unique that branch can only ever return the same row as the plain lookup,
    // so it was dead weight that hid where the answers actually come from.
    // The goal can live on an Initial session OR on a Review session (a
    // goal-review row), and in both cases that exact session's recorded
    // answers - Background Information & Assessments through Comorbidities - are
    // what the RSG pathway must inherit.
    //
    // NO FALLBACK: if ?goalSessionId is set but that row is not in the payload
    // yet (history still loading, or a stale id), the source is null rather than
    // some other session's history - inheriting a different session is exactly
    // the wrong-data bug this rule exists to prevent. The pre-fill effect waits
    // for a source instead of consuming its one-shot flag, so it runs as soon as
    // the right session appears.
    return sessions.find(s => String(s.session_id ?? s.sessionId) === wanted) || null;
  }, [initialHistory, inheritSession, isSmartGoalMode, goalSessionId]);

  // -- Nodes THIS session has already answered, from the server's copy of it. --
  //
  // pickInheritanceSession() deliberately EXCLUDES the session being answered (so
  // a draft cannot copy its own partial answers back onto itself). The
  // consequence, which was a real bug: re-opening a Review inherits from the
  // session BEFORE it - for this client the Initial - and the pre-fill stamped
  // that older session's answers over the Review's own, then auto-saved them into
  // the database. The therapist saw "Background Information & Assessments ->
  // Comorbidities" showing the Initial's data and could not tell it from their
  // own.
  //
  // Reading the already-answered set from the history payload instead of from
  // `answers` state matters: `answers` is restored from sessionStorage, so it is
  // empty after a plain reload or in a second tab, and the `answers[q.node] ===
  // undefined` guard the pre-fill already had then passed for EVERY node. The
  // server copy is the authoritative record and is always present.
  //
  // Both question nodes and selected OPTION nodes go in, because fillChain() and
  // saveSelectedSubOptions() write sub-options at their own nodes.
  const currentSessionNodes = useMemo(() => {
    const set = new Set();
    const id = currentSessionId == null ? "" : String(currentSessionId);
    if (!id) return set;
    const sessions = Array.isArray(initialHistory?.data) ? initialHistory.data : [];
    const own = sessions.find(s => String(s?.session_id ?? s?.sessionId ?? "") === id);
    if (!own || !Array.isArray(own.pathway)) return set;

    // Same "is this answered?" test as hasAnsweredQuestions() in
    // assessmentHistory.js, but per question. HINE/HAI arrive as
    // { value, entries }, so both shapes are checked.
    const hasAnswer = (a) => {
      if (a === null || a === undefined || a === "") return false;
      if (Array.isArray(a)) return a.length > 0;
      if (typeof a === "object") return Boolean(a.value) || (Array.isArray(a.entries) && a.entries.length > 0);
      return true;
    };
    const walkOptions = (opts) => (opts || []).forEach(o => {
      if (!o) return;
      if (o.selected && o.node != null) set.add(String(o.node));
      walkOptions(o.suboptions);
    });

    own.pathway.forEach(q => {
      if (!q) return;
      if (hasAnswer(q.answer) && q.node != null) set.add(String(q.node));
      walkOptions(q.options);
    });
    return set;
  }, [initialHistory, currentSessionId]);

  const initialAnswerMap = useMemo(() => {
    const map = new Map();
    if (!sourceSession || !Array.isArray(sourceSession.pathway)) return map;
    sourceSession.pathway.forEach(q => {
      if (q.question) map.set(String(q.question).trim().toLowerCase(), q);
    });
    return map;
  }, [sourceSession]);

  // Resolves an RSG/Review keyword against the source pathway. Shared with
  // Review-assessment.js so both pathways inherit identically - see
  // buildKeywordLookup in assessmentHistory.js for the tiering rules.
  const lookupKeyword = useMemo(() => buildKeywordLookup(sourceSession), [sourceSession]);

  useEffect(() => {
    if (pathwaysData.length === 0) return;
    let needsUpdate = false;
    const newAnswers = { ...answers };
    const nodeMap = {};
    pathwaysData.forEach(pData => {
      if (pData.pathways) {
        pData.pathways.forEach(p => {
          if (p.groups) {
            p.groups.forEach(g => {
              const process = (qs) => {
                qs.forEach(q => { if (q.options) nodeMap[q.node_number] = q.options; });
              };
              process(g.questions);
            });
          }
        });
      }
    });
    Object.keys(newAnswers).forEach(node => {
      const ans = newAnswers[node];
      if (Array.isArray(ans) && ans.length > 0 && typeof ans[0] === "number") {
        const options = nodeMap[node];
        if (options) {
          const texts = ans.map(id => { const opt = options.find(o => o.option_id === id); return opt ? opt.option_text : id; }).filter(t => typeof t === "string");
          if (texts.length > 0) { newAnswers[node] = texts; needsUpdate = true; }
        }
      } else if (typeof ans === "number") {
        const options = nodeMap[node];
        if (options) { const opt = options.find(o => o.option_id === ans); if (opt) { newAnswers[node] = opt.option_text; needsUpdate = true; } }
      }
    });
    if (needsUpdate) setAnswers(newAnswers);
    // answers is intentionally omitted: this only normalises legacy option-id
    // rows on load, and re-running on every answer change would rewrite state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathwaysData]);

  // -- Drop an age group the current appointment cannot run -------------------
  // Review and Review Smart Goal have separate pathways, so an age group that
  // one of them offers is not necessarily offered by the other. The selected
  // value can arrive from places that know nothing about the active
  // appointment - a restored draft or local snapshot - and switching the
  // step-1 appointment radio leaves the previous value in place too.
  //
  // A value that is not in the active list is cleared instead of being shown,
  // so the <select> can never display an option it does not offer and the
  // therapist is forced to pick a real one before Next. Skipped while the
  // pathway list is still loading or empty, so a valid value is never wiped
  // during the initial fetch.
  //
  // Review Smart Goal is deliberately NOT cleared here: its band belongs to
  // the exact session being reviewed, so the pathway check right below raises
  // the spec popup FIRST - and clears the band itself only once it has said
  // why - instead of this effect silently blanking the dropdown.
  useEffect(() => {
    if (!isReviewMode) return;
    if (!ageRange) return;
    if (reviewAgeGroups.length === 0) return;
    if (reviewAgeGroups.includes(ageRange)) return;
    if (isSmartGoalMode) return;
    setAgeRange("");
  }, [isReviewMode, ageRange, reviewAgeGroups, isSmartGoalMode]);

  // -- Review Smart Goal: clinical-pathway existence check (spec) -----------
  // The band is the one recorded on the session the therapist clicked Review
  // Assessment on (?age= from goalreview) - it is never derived and never
  // swapped for another band's pathway. When the admin has built no
  // `review smart goal` pathway for it, the exact PATHWAY_UNAVAILABLE_MESSAGE
  // popup is raised the moment the page opens and rsgPathwayMissing keeps
  // step-1 Next disabled (even after the popup is dismissed), so the
  // assessment cannot continue. Selecting a band that DOES have a pathway
  // clears the flag again.
  //
  // THE AUTO-FILL IS WITHDRAWN IN THE SAME BREATH. The band only reaches this
  // effect from an auto-fill (?age= prefill, draft restore, client picker), so
  // "no pathway for it" means "the auto-fill was wrong": the value is cleared,
  // exactly like the server's own pathway_not_available handler does further
  // down (same reasoning - a dropdown populated from the pathways that exist
  // cannot honour the band, and leaving it in place would make Next fail the
  // same way for ever with no way out). The band is remembered in
  // rejectedRsgAgeRef so no source re-fills it. The popup still names the band
  // (pathwayBlocked.ageGroup), so the therapist can see which one was refused.
  //
  // A band WITH a pathway is auto-filled and left alone - no popup.
  //
  // Deliberately gated on pathwaysLoaded so a slow fetch can never raise the
  // popup for a band that is perfectly fine, and on ageRange so nothing fires
  // before the session's band has arrived (URL prefill / draft restore).
  useEffect(() => {
    if (!isSmartGoalMode) {
      setRsgPathwayMissing(false);
      return;
    }
    if (!pathwaysLoaded || !ageRange) return;
    const missing = !reviewAgeGroups.includes(ageRange);
    setRsgPathwayMissing(missing);
    if (missing) {
      const band = ageRange;
      setPathwayBlocked({
        kind: "no_pathway",
        message: PATHWAY_UNAVAILABLE_MESSAGE,
        ageGroup: band,
      });
      rejectedRsgAgeRef.current = band;
      setAgeRange("");
    }
  }, [isSmartGoalMode, pathwaysLoaded, ageRange, reviewAgeGroups]);

  // -- Set active pathway when ageRange changes ------------------------------
  useEffect(() => {
    if (ageRange && pathwaysData.length > 0) {
      const selectedAgeObj = pathwaysData.find(p => p.age_group === ageRange);
      if (selectedAgeObj && selectedAgeObj.pathways && selectedAgeObj.pathways.length > 0) {
        // Review Smart Goal is matched ONLY against `review smart goal`, plain
        // Review ONLY against `review`, Initial ONLY against `initial`.
        //
        // The cross-appointment fallbacks that used to sit here (RSG -> review,
        // Review -> RSG, and finally "whatever pathway this band happens to
        // have") are gone: the spec is appointment_type + age_group, and
        // falling back to another appointment's question set - or another
        // band's - is exactly what produces the wrong assessment on screen.
        // A band with no pathway of its own type resolves to null here and
        // raises the missing-pathway popup instead.
        //
        // Keep in sync with $pathwayTypes / $isStrictBand in
        // therapist_api/save_basic_details.php so the clpa_id stored on the
        // session matches the pathway rendered on screen. get_pathway_details.php
        // now scopes the whole response to pathwayAppointmentFilter, so these
        // lists can only ever contain their own appointment type's rows anyway.
        const desiredTypes = isSmartGoalMode
          ? ["review smart goal"]
          : isReviewMode
            ? ["review"]
            : ["initial"];
        const pathway =
          desiredTypes
            .map(t => selectedAgeObj.pathways.find(p => (p.appointment_type || "").toLowerCase() === t))
            .find(Boolean) || null;
        setActivePathway(pathway);
      } else {
        setActivePathway(null);
      }
    }
  }, [ageRange, pathwaysData, session, isReviewMode, isSmartGoalMode]);

  // -- Fetch interventions ---------------------------------------------------
  const fetchInterventions = async () => {
    if (!currentSessionId) return;

    setInterventionsData(null);
    setActiveGoalTab("");
    setAccordionOpen({});

    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/get_test_intervention.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: parseInt(currentSessionId),
            // Review Smart Goal: the catalogue keys inherit the domains/skills
            // from the session that owns the goal being reviewed, so tell the
            // backend which one that is.
            ...(isSmartGoalMode && goalSessionId ? { goal_session_id: parseInt(goalSessionId) } : {}),
          }),
        }
      );

      const json = await response.json();

      if (json.status) {
        setInterventionsData(json);

        const goalTabs = Object.keys(json.data || {}).map((key) => {
          const items = json.data[key] || [];
          const firstItem = items[0] || {};

          const goalDomain = firstItem.goal_domain || "";
          const goalSkill = firstItem.goal_skill || "";

          const tabKey =
            goalSkill && goalSkill !== "null"
              ? goalSkill
              : goalDomain;

          const displayLabel =
            goalSkill && goalSkill !== "null"
              ? `${goalDomain} - ${goalSkill}`
              : goalDomain;

          return {
            key,
            tabKey,
            goalDomain,
            goalSkill,
            displayLabel,
          };
        });

        const uniqueGoalTabs = goalTabs.filter(
          (tab, index, self) =>
            index ===
            self.findIndex((t) => t.tabKey === tab.tabKey)
        );

        if (uniqueGoalTabs.length > 0) {
          const firstTab = uniqueGoalTabs[0];

          setActiveGoalTab(firstTab.tabKey);

          const firstTabInterventions =
            json.data[firstTab.key] || [];

          if (firstTabInterventions.length > 0) {
            const initialOpen = {};
            initialOpen[`int-${firstTabInterventions[0].id}`] = true;
            if (firstTabInterventions.length > 1) {
              initialOpen[`int-${firstTabInterventions[1].id}`] = true;
            }
            setAccordionOpen(initialOpen);
          }
        }
      } else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
      }
    } catch (error) {
      console.error(
        "Error fetching interventions:",
        error.message
      );
    }
  };

  // -- Derived: flat list of all questions ----------------------------------
  const allQuestionsFlat = useMemo(() => {
    const steps = [];
    if (activePathway && activePathway.groups) {
      activePathway.groups.forEach(group => {
        if (group.group_name.toLowerCase() === "demographics") return;

        function processSubOptionsForFlat(subOptions) {
          if (!subOptions || subOptions.length === 0) return;
          subOptions.forEach(subOpt => {
            if (subOpt.sub_options && subOpt.sub_options.length > 0) {
              processSubOptionsForFlat(subOpt.sub_options);
            }
            if (subOpt.sub_questions && subOpt.sub_questions.length > 0) {
              processOptions(subOpt.sub_questions);
            }
          });
        }

        function processOptions(questions) {
          questions.forEach(q => {
            steps.push({ ...q, groupName: group.group_name, groupNode: group.node_number, groupInfo: group.group_info });
            if (q.options && q.options.length > 0) {
              q.options.forEach(opt => {
                if (opt.sub_options && opt.sub_options.length > 0) {
                  processSubOptionsForFlat(opt.sub_options);
                }
                if (opt.sub_questions && opt.sub_questions.length > 0) processOptions(opt.sub_questions);
              });
            }
          });
        }

        processOptions(group.questions);
      });
    }
    return steps;
  }, [activePathway]);

  const groupNodeSet = useMemo(() => {
    const set = new Set();
    if (activePathway && activePathway.groups) activePathway.groups.forEach(g => set.add(g.node_number));
    return set;
  }, [activePathway]);

  const pathwayHasEndInstruction = useMemo(
    () => hasEndInstructionDeep(activePathway),
    [activePathway]
  );

  const visibleSteps = useMemo(() => {
    const selectedOptionNodes = new Set();

    function processSubOptionsChainForVisible(qNode, pathTexts, subOptions) {
      if (!subOptions || subOptions.length === 0) return;
      const chainKey = [qNode, ...pathTexts, "sub"].join("_");
      const chainAns = answers[chainKey];
      if (!chainAns) return;
      subOptions.forEach((subOpt) => {
        const isSelected = Array.isArray(chainAns) ? chainAns.includes(subOpt.option_text) : chainAns === subOpt.option_text;
        if (!isSelected) return;
        selectedOptionNodes.add(subOpt.node_number);
        if (subOpt.sub_options && subOpt.sub_options.length > 0) {
          processSubOptionsChainForVisible(qNode, [...pathTexts, subOpt.option_text], subOpt.sub_options);
        }
        if (subOpt.sub_questions) processQuestions(subOpt.sub_questions);
      });
    }

    function processQuestions(questions) {
      if (!questions) return;
      questions.forEach((q) => {
        const ans = answers[q.node_number];
        if (!ans || !q.options) return;
        q.options.forEach((opt) => {
          const isSelected = Array.isArray(ans) ? ans.includes(opt.option_text) : ans === opt.option_text;
          if (!isSelected) return;
          selectedOptionNodes.add(opt.node_number);
          if (opt.sub_options && opt.sub_options.length > 0) {
            processSubOptionsChainForVisible(q.node_number, [opt.option_text], opt.sub_options);
          }
          if (opt.sub_questions) processQuestions(opt.sub_questions);
        });
      });
    }

    processQuestions(allQuestionsFlat);

    return allQuestionsFlat.filter((q) => {
      if (groupNodeSet.has(q.parent_node)) return true;
      if (selectedOptionNodes.has(q.parent_node)) return true;
      return false;
    });
  }, [allQuestionsFlat, answers, groupNodeSet]);

  // -- Leave guard: arming + pending-destination ---------------------------------
  // Arming follows the same step rule handleNext uses: the questionnaire counts
  // as "in progress" once step > 1 (the client/age picker is behind us), and
  // stops just before the summary screen (step === 1 + visibleSteps.length), so
  // the summary-confirm jumps to /Interventions and /summarypage never prompt.
  useEffect(() => {
    const inProgress = step > 1 && step <= 1 + visibleSteps.length;
    inProgressRef.current = inProgress;
    setAssessmentArmed(inProgress);
  }, [step, visibleSteps]);

  // When AssessmentLeaveGuard bounces a leaving navigation back to this page it
  // stashes where the therapist was heading. Pick that up and open the same
  // Save/Delete popup with the destination on file. Runs on every location
  // change (not just mount) so a second navigation attempt while the popup is
  // open also refreshes the destination.
  useEffect(() => {
    const pending = takePendingLeave();
    if (pending.dest) {
      pendingLeaveDestRef.current = pending.dest;
      setLeavePromptOpen(true);
    }
  }, [location]);

  // -- NEW: Pre-fill the review questionnaire with the already-recorded
  // data from the session we inherit from ? auto-select the SAME option,
  // carry over the HINE/HAI score entries, and select sub-options too.
  // Runs once per client.
  const prefilledClientRef = useRef(null);
  const prefilledDoneRef = useRef(false);
  useEffect(() => {
    const clientId = selectedClientId;
    if (prefilledClientRef.current !== clientId) {
      prefilledClientRef.current = clientId;
      prefilledDoneRef.current = false;
    }
    if (!clientId || !currentSessionId || initialHistory === null || visibleSteps.length === 0) return;
    // Both review modes land on step 2 = the 4th question overall (Q1-Q3 are the
    // step-1 setup answers). Prefill from step 2 so Background Information &
    // Assessments -> Comorbidities are already answered by the time the therapist
    // reaches them. The Initial flow keeps the original step-3 timing.
    const minStep = isReviewMode ? 2 : 3;
    if (step < minStep) return;
    if (prefilledDoneRef.current) return;

    // For Review Smart Goal the source is the session that owns the goal
    // (the one named by ?goalSessionId), because that is the pathway the
    // therapist clicked through from "Review Existing Goals" - not merely the
    // latest completed session. sourceSession is the single place that picks.
    //
    // The one-shot flag is set only AFTER a source is known. It used to be set
    // before this guard, so the first firing that reached it while the history
    // was still resolving consumed the single attempt and the pre-fill never ran
    // again for the life of the page - the questions stayed blank even though
    // sourceSession became available moments later. That is the difference
    // between "the data is late" and "the data never arrives".
    if (!sourceSession || !Array.isArray(sourceSession.pathway)) return;
    prefilledDoneRef.current = true;

    const newAnswers = {};
    const hineRows = [];
    const haiRows = [];
    // Only used by Review Smart Goal: the pre-filled answers are written back
    // to the review session so the record is complete even if the therapist
    // only changes one question.
    const autoSaveResponses = [];

    // Walk the initial option tree and re-build the same chain keys that
    // renderSubOptionsChain / handleChainSubOptionSelect use, so the
    // selected sub-options show up inline on the review question.
    const fillChain = (qNode, initialOptions, ancestors) => {
      if (!Array.isArray(initialOptions)) return;
      initialOptions.forEach(opt => {
        if (!opt || !opt.selected) return;
        // Sub-options are written at their OWN node, so the question-level skip
        // below does not cover them: this used to run unconditionally and stamp
        // the older session's sub-choices over ones already made here.
        if (opt.node != null && currentSessionNodes.has(String(opt.node))) return;
        const children = Array.isArray(opt.suboptions) ? opt.suboptions : [];
        const selectedChildren = children.filter(c => c && c.selected);
        if (selectedChildren.length > 0) {
          const subKey = [qNode, ...ancestors, opt.label, "sub"].join("_");
          newAnswers[subKey] = selectedChildren.length === 1
            ? (selectedChildren[0].answer ?? selectedChildren[0].label)
            : selectedChildren.map(c => (c.answer ?? c.label));
        }
        selectedChildren.forEach(child => {
          if (child.suboptions && child.suboptions.length > 0) {
            fillChain(qNode, [child], [...ancestors, opt.label]);
          }
        });
      });
    };

    // â”€â”€ Writing pre-filled SUB-options back to the review session â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    //
    // fillChain() puts the selected sub-options into React state, so the summary
    // page can already display "Topography: Unilateral". Before this existed,
    // only the TOP-LEVEL option was pushed onto autoSaveResponses, so a
    // pre-filled sub-option lived on screen and nowhere else: the review
    // session stored "Yes" with no child row, and /Interventions - which reads
    // the session, not the screen - came back with Topography: null and an
    // empty comorbidities list even though the therapist had answered those
    // questions correctly.
    //
    // get_historical_pathway_data.php labels its options with `node` but no
    // `option_id`, so the ids are indexed from the pathway definition itself
    // (allQuestionsFlat), which does carry them - that keeps the write identical
    // to what the click handler sends.
    let optionIdsByNode = {};
    let optionsByLabel = {};
    const resetOptionIndex = () => {
      optionIdsByNode = {};
      optionsByLabel = {};
    };
    const indexOptionTree = (opts) => {
      (opts || []).forEach(o => {
        if (!o) return;
        if (o.node_number && o.option_id != null) optionIdsByNode[String(o.node_number)] = o.option_id;
        const lbl = String(o.option_text == null ? "" : o.option_text).trim().toLowerCase();
        if (lbl && o.option_id != null) {
          if (!optionsByLabel[lbl]) optionsByLabel[lbl] = [];
          // Keep tree order and drop duplicates, so a label repeated at two levels
          // resolves to the top-most one - which is the level the answer belongs to.
          if (!optionsByLabel[lbl].some(h => h.id === o.option_id)) {
            optionsByLabel[lbl].push({ id: o.option_id, node: o.node_number == null ? null : String(o.node_number) });
          }
        }
        if (o.sub_options && o.sub_options.length) indexOptionTree(o.sub_options);
      });
    };

    // Map an inherited label onto an option of the CURRENT pathway.
    //
    // The node number cannot be the primary key. A session's options are numbered
    // after ITS OWN pathway, so an Initial answer at 122.4.1.2 ("Communication
    // and Social") has no counterpart at all in the Review pathway, where the same
    // option is 123.5.1.2. Carrying data across pathways - which is the normal
    // Initial -> Review and Review -> Review Smart Goal flow, i.e. every step the
    // therapist is about to walk - therefore NEVER resolved, ids came back empty,
    // and the multi-label fallback wrote a comma-joined text_input row that
    // get_test_intervention.php could not read back. That is why Goal Domain and
    // Goal Skill kept failing to appear.
    //
    // The option TEXT is what is stable across pathways ("Communication and
    // Social" is spelled the same in clpa 122, 123 and 221), so node is tried
    // first and label is the fallback.
    const resolveOption = (label, sourceNode) => {
      if (sourceNode != null) {
        const byNode = optionIdsByNode[String(sourceNode)];
        if (byNode != null) return { id: byNode, node: String(sourceNode) };
      }
      const key = String(label == null ? "" : label).trim().toLowerCase();
      const hits = optionsByLabel[key];
      return hits && hits.length > 0 ? hits[0] : null;
    };

    // Same resolution, returning just the id, for the two places that push an
    // auto-save entry.
    const resolveOptionId = (label, sourceNode) => {
      const hit = resolveOption(label, sourceNode);
      return hit ? hit.id : null;
    };

    // Push one auto-save entry per selected sub-option, at its OWN node, exactly
    // as renderSubOptionsChain's click handler does. Multi-select levels send
    // `answers` (option ids) instead of a single `answer`.
    const saveSelectedSubOptions = (options) => {
      (options || []).forEach(o => {
        if (!o || !o.selected) return;
        const children = Array.isArray(o.suboptions) ? o.suboptions : [];
        const picked = children.filter(c => c && c.selected);
        if (picked.length === 0) return;

        // Resolve every sub-option against THIS pathway and keep BOTH halves of
        // the answer: the option id AND its node.
        //
        // The id was already resolved through resolveOption() rather than by
        // node alone (a node-only lookup missed: the sub-option's node belongs
        // to the SOURCE pathway, 122.1.6.1.1, while the index holds THIS
        // question's nodes, 1023.1.6.1.1). The ROW, however, was still written
        // at `picked[0].node` - the SOURCE node this pathway does not have.
        // Responses are keyed by node, so that row was invisible to
        // get_draft_session_detail.php and get_historical_pathway_data.php: the
        // choice showed on screen (fillChain keys React state by the current
        // question's node) and vanished on the next reload, which is how
        // Topography came back as a bare "Yes" with no laterality, and how
        // /Interventions read Topography back as null.
        //
        // The node now comes from the resolution, which walks THIS question's
        // own option tree; the source node survives only as a last resort when
        // the option definition carries no node at all.
        const resolved = picked.map(c => ({ child: c, opt: resolveOption(c.answer ?? c.label, c.node) }));
        const nodeOf = (x) => {
          if (x.opt && x.opt.node != null) return String(x.opt.node);
          return x.child.node != null ? String(x.child.node) : null;
        };
        // Skip what THIS session has already answered - judged on the node this
        // session stores it under, which the source node never matches.
        const pending = resolved.filter(x => {
          const node = nodeOf(x);
          return node === null || !currentSessionNodes.has(node);
        });
        const node = pending.length > 0 ? nodeOf(pending[0]) : null;
        if (pending.length > 0 && node !== null && pending.every(x => x.opt && x.opt.id != null)) {
          if (pending.length === 1) {
            autoSaveResponses.push({
              node,
              entity: "option",
              entityId: pending[0].opt.id,
              answer: String(pending[0].child.answer ?? pending[0].child.label ?? ""),
            });
          } else {
            autoSaveResponses.push({
              node,
              entity: "option",
              entityId: pending[0].opt.id,
              answers: pending.map(x => x.opt.id),
            });
          }
        }

        picked.forEach(child => {
          if (child.suboptions && child.suboptions.length) saveSelectedSubOptions([child]);
        });
      });
    };

    allQuestionsFlat.forEach(q => {
      let match = q.question_text
        ? initialAnswerMap.get(String(q.question_text).trim().toLowerCase()) || null
        : null;
      if (!match && q.keywords) {
        const nodeGroup = String(q.node_number || "").split(".")[1] || "";
        match = lookupKeyword(q.keywords, nodeGroup);
      }

      // -- HINE / HAI: CLIENT-WIDE history stack (no age filter) --------------
      // Scores are NOT inherited from a single source session. They are stacked
      // from EVERY session of this client (client-based, not age-based), newest
      // session first: row 0 renders as section A, the earlier ones as section
      // B/C/... one by one. buildHineHaiStack dedupes untouched copies back to
      // their original section, so a Review that just shows the fetched data
      // adds no duplicate section, and editing the current row becomes a new
      // section on top ("edit A -> becomes C", "edit C -> becomes D").
      const qIsHine = getQuestionType(q).isHINE;
      const qIsHai = getQuestionType(q).isHAI;
      if (qIsHine || qIsHai) {
        // Gap-fill only: this session's own answer/rows always win.
        if (answers[q.node_number] !== undefined || currentSessionNodes.has(String(q.node_number))) return;
        const { hine: hineStack, hai: haiStack } = buildHineHaiStack(initialHistory?.data || [], currentSessionId);
        const rows = qIsHine ? hineStack : haiStack;
        if (rows.length > 0) {
          if (qIsHine) hineRows.push(...rows); else haiRows.push(...rows);
          if (newAnswers[q.node_number] === undefined) newAnswers[q.node_number] = "Yes";
          if (isReviewMode) {
            autoSaveResponses.push({
              node: q.node_number,
              entity: "question",
              entityId: q.question_id || 0,
              answer: "Yes",
              type: qIsHine ? "hine" : "hai",
              entries: qIsHine ? toHineEntries(rows) : toHaiEntries(rows),
            });
          }
        } else if (match) {
          // NO SCORE ROWS EXIST ANYWHERE FOR THIS CLIENT.
          //
          // buildHineHaiStack only ever collects rows from an answered "Yes",
          // so a client whose previous session answered "No" ("HINE / HAI
          // scores are not available") produces an EMPTY stack - and this
          // branch then did nothing at all, which is why a new Review opened
          // with HINE and HAI blank even though the session it inherits from
          // had answered both. Blank is indistinguishable from "not fetched",
          // and the therapist had to re-answer two questions the record already
          // held.
          //
          // The source session's own Yes/No value is carried instead. It comes
          // from `match`, which was resolved with THIS question's exact text
          // ("Are Hammersmith Infant Neurological Examination (HINE) scores
          // available?" is spelled identically in both pathways) or its
          // keyword, so it is always this same question's answer. The score
          // table stays hidden for "No" because renderHINESection /
          // renderHAISection only draw when the value is "Yes".
          const raw = match.answer;
          const value = raw !== null && raw !== undefined && typeof raw === "object" && !Array.isArray(raw)
            ? raw.value
            : (Array.isArray(raw) ? null : raw);
          if (value) {
            newAnswers[q.node_number] = value;
            if (isReviewMode) {
              autoSaveResponses.push({
                node: q.node_number,
                entity: "question",
                entityId: q.question_id || 0,
                answer: String(value),
                type: qIsHine ? "hine" : "hai",
              });
            }
          }
        }
        return;
      }

      if (!match) return;
      const grp = (q.groupName || "").toLowerCase();
      const isHineHai = match.type === "HINE" || match.type === "HAI";
      // Groups whose questions carry over from the source session.
      //
      // SPEC (Review and Review Smart Goal): inherited data may span EXACTLY
      // "Background Information & Assessments" -> "Current Skills" ->
      // "Comorbidities", i.e. from the Background group through the Comorbidities
      // group and nothing beyond it.
      //
      // "goals" used to be in this list so the new-goal questions were pre-filled
      // too ("What is the domain of the new goal?" / "Have you identified any
      // goal limiting factors?" resolve through the shared "Goal Domain" /
      // "Goal Limiting Factors" keywords). That made the Goals group arrive
      // already answered, which is precisely what the therapist asked to stop:
      // the Goals group must be answered fresh in this session.
      //
      // "goal progression" stays excluded for the same reason - those questions
      // are about THIS review ("current goal status", "factors delaying
      // progress") and have no counterpart in the source session.
      const inheritGroups = ["background", "current skills", "comorbid"];
      if (!isHineHai && !inheritGroups.some(g => grp.includes(g))) return;

      // GAP-FILL ONLY. This session's own answer always wins, whatever the
      // source session says and however old the source is. Without this the
      // pre-fill inherited from the session BEFORE this one and overwrote the
      // Review with the Initial's answers - in state AND, via
      // save_review_auto_responses.php, in the database.
      if (currentSessionNodes.has(String(q.node_number))) return;

      if (isHineHai) {
        if (answers[q.node_number] !== undefined) return;
        const value = match.answer?.value;
        if (value) newAnswers[q.node_number] = value;
        const entries = Array.isArray(match.answer?.entries) ? match.answer.entries : [];
        if (String(value || "").toLowerCase() === "yes" && entries.length > 0) {
          if (match.type === "HINE") {
            entries.forEach(e => hineRows.push({
              id: Date.now() + Math.random(),
              timepoint: e.timepoint || "",
              hineScore: e.hine_score ?? 0,
              rightAsym: e.right_asymmetry ?? 0,
              leftAsym: e.left_asymmetry ?? 0,
            }));
            } else {
              entries.forEach(e => haiRows.push({
                id: Date.now() + Math.random(),
                timepoint: e.timepoint || "",
                rightEahs: e.right_score ?? 0,
                leftEahs: e.left_score ?? 0,
                eachHandSumScore: e.sum ?? 0,
                asymmetryIndex: e.asymmetry ?? 0,
              }));
            }
          }
          if (isReviewMode) {
            if (String(value || "").toLowerCase() === "yes" && entries.length > 0) {
              autoSaveResponses.push({
                node: q.node_number,
                entity: "question",
                entityId: q.question_id || 0,
                answer: value,
                type: match.type.toLowerCase(),
                entries: entries,
              });
            } else {
              autoSaveResponses.push({
                node: q.node_number,
                entity: "question",
                entityId: q.question_id || 0,
                answer: value || "No",
                type: match.type.toLowerCase(),
              });
            }
          }
        } else {
          const ans = match.answer;
          if (ans !== null && ans !== undefined && ans !== "" && answers[q.node_number] === undefined) {
            // The history API's `answer` for an answered option tree is the
            // FLATTENED list of every selected label - the top-level option and
            // its sub-options (e.g. ["Yes","Unilateral"]). Putting the whole
            // array into the main answer makes the step list resolve the
            // sub-option text a second time ("Unilateral, Unilateral") and
            // writes a mangled "Yes,Unilateral" string into the review session.
            // Only the top-level selected option(s) belong in the main answer;
            // sub-options are carried via the `_sub` chain keys fillChain()
            // writes below.
            let mainAnswer = ans;
            const topLevelSelected = Array.isArray(match.options)
              ? match.options.filter(o => o && o.selected).map(o => o.label)
              : [];
            if (topLevelSelected.length > 0) {
              mainAnswer = topLevelSelected.length === 1 ? topLevelSelected[0] : topLevelSelected;
            }
            if (mainAnswer !== null && mainAnswer !== undefined && mainAnswer !== "") {
              newAnswers[q.node_number] = mainAnswer;
              if (isReviewMode) {
                // Scoped to THIS question's own option tree. A single shared,
                // accumulating index lets a common label such as "Yes" resolve to
                // whichever question happened to be walked first, which would stamp
                // another question's option id onto this node.
                resetOptionIndex();
                indexOptionTree(q.options);
                // Resolve EACH selected label to its own option id.
                //
                // This used to match one option against String(mainAnswer), which
                // is fine for a single answer and broken for every multi-select:
                // String(["Communication and Social","Vision"]) is
                // "Communication and Social,Vision", no option label equals that,
                // so `selectedOpt` was always undefined. The auto-save then wrote
                // response_type=option_selected with
                // response_value="Communication and Social,Vision" and NULL
                // response_ids - the source session's domains flattened into one
                // unreadable answer, stored on the review. Multi-selects now send
                // `answers` (every id), the same shape the click handler and
                // saveSelectedSubOptions() already use.
                const labels = Array.isArray(mainAnswer) ? mainAnswer : [mainAnswer];
                const ids = labels
                  .map(label => {
                    const opt = (match.options || []).find(
                      o => String(o.label || "").toLowerCase() === String(label || "").toLowerCase()
                    );
                    return resolveOptionId(label, opt && opt.node != null ? opt.node : null);
                  })
                  .filter(id => id != null);
                if (ids.length === 1) {
                  autoSaveResponses.push({
                    node: q.node_number,
                    entity: "option",
                    entityId: ids[0],
                    answer: String(labels[0]),
                  });
                } else if (ids.length > 1) {
                  autoSaveResponses.push({
                    node: q.node_number,
                    entity: "option",
                    entityId: ids[0],
                    answers: ids,
                  });
                } else if (Array.isArray(mainAnswer)) {
                  // A MULTI-label answer where no label resolved to an option id.
                  //
                  // Writing this at all produces the worst possible row:
                  // save_review_auto_responses.php stores a non-array `answer`
                  // as response_type='text_input', and String(["Motor",
                  // "Communication and Social","Participation"]) comma-joins with
                  // NO space. The result is a Goal Domain row that is neither a
                  // multi_select (no response_ids) nor a readable option (no
                  // response_value anyone can match), which is exactly how session
                  // 100001 ended up with
                  //   221.5.1 / text_input / "Motor,Communication and Social,Participation"
                  // and why get_test_intervention.php could not read it back.
                  //
                  // A corrupt row is worse than no row: it poisons the goal
                  // extractor and the intervention filter for every later read. The
                  // value is already in React state (`newAnswers`), so the therapist
                  // still SEES the inherited selection on screen and can confirm it,
                  // which then saves it properly through the normal click handler.
                  console.warn(
                    "[prefill] skipped auto-save for multi-select",
                    q.node_number,
                    "- no label resolved to an option id:",
                    JSON.stringify(mainAnswer)
                  );
                } else {
                  // A single label that resolved to nothing. Saving at the question
                  // is an honest record and is safe: one label cannot be mangled by
                  // array stringification.
                  autoSaveResponses.push({
                    node: q.node_number,
                    entity: "question",
                    entityId: q.question_id || 0,
                    answer: String(mainAnswer),
                  });
                }
                // The sub-options fillChain() just put into state need the same
                // treatment, otherwise the review session keeps only "Yes".
                saveSelectedSubOptions(match.options);
              }
            }
          }
          fillChain(q.node_number, match.options, []);
        }
      });

    setAnswers(prev => {
      const merged = { ...prev };
      let changed = false;
      Object.keys(newAnswers).forEach(k => {
        if (merged[k] === undefined) { merged[k] = newAnswers[k]; changed = true; }
      });
      return changed ? merged : prev;
    });
    const hineEmpty = hineData.length === 0 || (hineData.length === 1 && !hineData[0].timepoint && !hineData[0].hineScore);
    const haiEmpty = haiData.length === 0 || (haiData.length === 1 && !haiData[0].timepoint && !haiData[0].rightEahs);
    if (hineRows.length > 0 && hineEmpty) { recordHineOrigins(hineRows); setHineData(hineRows); }
    if (haiRows.length > 0 && haiEmpty) { recordHaiOrigins(haiRows); setHaiData(haiRows); }

    if (isReviewMode && autoSaveResponses.length > 0) {
      fetchWithTimeout(
        `${API_BASE}/therapist_api/save_review_auto_responses.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ sessionId: parseInt(currentSessionId), responses: autoSaveResponses }),
        }
      )
        .then((res) => res.json())
        .then((json) => {
          if (!json.status && isTokenError(json)) dispatchSessionExpired();
        })
        .catch((error) => console.error("Failed to auto-save pre-filled review answers:", error.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, step, allQuestionsFlat, visibleSteps, sourceSession, currentSessionNodes, answers, hineData, haiData, selectedClientId, isReviewMode, token]);

  // -- Review Smart Goal: load the ORIGINATING pathway's Goal Domains / Goals
  // so summ-container1 can show what is being reviewed and revised. ---------
  useEffect(() => {
    if (!isSmartGoalMode) { setSmartGoalContext(null); return; }
    if (!token) return;
    if (!goalSessionId && !selectedClientId) return;

    let cancelled = false;
    setSmartGoalLoading(true);
    (async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/get_smart_goal_context.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({
              goal_session_id: goalSessionId ? parseInt(goalSessionId) : 0,
              client_id: selectedClientId ? parseInt(selectedClientId) : 0,
              // The band this Review Smart Goal is being run on. The endpoint's
              // goal_session_id = 0 fallback picks the client's newest goal
              // session, which without this could sit on a different band and
              // answer a different question set. Sent even when goal_session_id
              // is set, so the band is on record either way.
              age_group: ageRange || "",
            }),
          }
        );
        const json = await response.json();
        if (cancelled) return;
        if (json.status) {
          setSmartGoalContext(json);
          // Adopt the originating session as the goal context when the entry
          // point did not carry one explicitly.
          if (!goalSessionId && json.source?.sessionId) setGoalSessionId(String(json.source.sessionId));
        } else if (isTokenError(json)) {
          dispatchSessionExpired();
        }
      } catch (error) {
        if (!cancelled) console.error("Failed to load Review Smart Goal context:", error.message);
      } finally {
        if (!cancelled) setSmartGoalLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSmartGoalMode, goalSessionId, selectedClientId, ageRange, token]);

  // -- DRAFT RESTORE: calculate step from last_node once visibleSteps ready -
  useEffect(() => {
    const targetNode = targetNodeRef.current;
    if (!targetNode || visibleSteps.length === 0 || isRestoringDraft) return;

    console.log("?? Resolving draft last_node:", targetNode, "against", visibleSteps.length, "visible steps");

    const targetIndex = visibleSteps.findIndex(q => q.node_number === targetNode);

    if (targetIndex !== -1) {
      const calculatedStep = targetIndex + 2;
      setStep(calculatedStep);
      const targetQuestion = visibleSteps[targetIndex];
      if (targetQuestion && targetQuestion.groupName) {
        setOpenSections(prev => ({ ...prev, [targetQuestion.groupName]: true }));
      }
      console.log("? Draft restored to step:", calculatedStep, "node:", targetNode);
    } else {
      const lastIndex = visibleSteps.length - 1;
      if (lastIndex >= 0) {
        setStep(lastIndex + 2);
        const lastQ = visibleSteps[lastIndex];
        if (lastQ && lastQ.groupName) {
          setOpenSections(prev => ({ ...prev, [lastQ.groupName]: true }));
        }
        console.warn("?? last_node not found in visibleSteps. Defaulted to last step:", lastIndex + 2);
      }
    }

    targetNodeRef.current = null;
  }, [visibleSteps, isRestoringDraft]);

  // -- Auto-open section when navigating normally ----------------------------
  useEffect(() => {
    if (targetNodeRef.current) return;
    if (step >= 2 && visibleSteps[step - 2]) {
      const currentQ = visibleSteps[step - 2];
      if (currentQ.groupName) {
        setOpenSections(prev => ({ ...prev, [currentQ.groupName]: true }));
      }
    }
  }, [step, visibleSteps]);

  // -- Auto-scroll to active step --------------------------------------------
  useEffect(() => {
    if (activeStepRef.current) {
      activeStepRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [step, visibleSteps]);

  // -- Trigger interventions fetch on final step -----------------------------
  // Keyed on step/session/questions only: fetchInterventions is a stable
  // wrapper and re-running on every `answers` change would refetch per click.
  useEffect(() => {
    const totalSteps = 1 + (visibleSteps ? visibleSteps.length : 0);
    if (step === totalSteps + 1 && currentSessionId) fetchInterventions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, currentSessionId, visibleSteps, answers]);

  // -- Initialize accordion when interventions load --------------------------
  useEffect(() => {
    if (interventionsData && interventionsData.data && activeGoalTab && Object.keys(accordionOpen).length === 0) {
      const list = interventionsData.data[activeGoalTab];
      if (list && list.length > 0) {
        const initialOpen = {};
        initialOpen[`int-${list[0].id}`] = true;
        if (list.length > 1) {
          initialOpen[`int-${list[1].id}`] = true;
        }
        setAccordionOpen(initialOpen);
      }
    }
    // accordionOpen is read as a guard, not tracked: including it would make
    // the effect self-cancelling (it sets accordionOpen itself).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventionsData, activeGoalTab]);

  // ---------------------------------------------------------------------------
  // ANSWER HELPERS
  // ---------------------------------------------------------------------------

  // -- GROUP-SCOPED downstream clearing -------------------------------------
  // A pathway is answered group-name wise (30 questions may sit as 6-7
  // questions under each group_name). When the therapist CHANGES an answer,
  // only the questions that come after it INSIDE THE SAME group name may be
  // wiped - the answer to question 4 is what made questions 5, 6, ... of that
  // group answerable, so they are now stale and must go.
  //
  // Questions belonging to a DIFFERENT group name are deliberately left
  // untouched: they were answered under their own group's logic and are not
  // invalidated by an edit somewhere else. This is purely a local `answers`
  // state change - it does NOT call save_response.php, does not touch the
  // step/flow rules handleNext uses, and does not alter what the server holds
  // for any already-saved response.
  //
  // The group is identified by group_node (the structural id stamped on every
  // step by allQuestionsFlat), falling back to group_name for any legacy step
  // that reaches here without one.
  const clearDownstreamAnswers = (prevAnswers, currentNodeNumber) => {
    const newAnswers = { ...prevAnswers };
    const currentIndex = visibleSteps.findIndex(q => q.node_number === currentNodeNumber);
    if (currentIndex === -1) return newAnswers;

    const currentQ = visibleSteps[currentIndex];
    const sameGroup = (futureQ) =>
      futureQ.groupNode !== undefined && currentQ.groupNode !== undefined
        ? futureQ.groupNode === currentQ.groupNode
        : futureQ.groupName === currentQ.groupName;

    for (let i = currentIndex + 1; i < visibleSteps.length; i++) {
      const futureQ = visibleSteps[i];
      // Another group name - already answered there, leave it alone.
      if (!sameGroup(futureQ)) continue;
      delete newAnswers[futureQ.node_number];
      delete newAnswers[`${futureQ.node_number}_sub`];
      Object.keys(prevAnswers).forEach(key => {
        if (key.startsWith(`${futureQ.node_number}_`) && key !== `${futureQ.node_number}_sub`) delete newAnswers[key];
      });
    }
    return newAnswers;
  };

  const EATING_AND_DRINKING_OPTION = "Eating and Drinking";

  const handleMainOptionSelect = (qNode, opt, isMulti = false, isGoalDomain = false) => {
    // -- "Eating and Drinking" can only be selected as a single choice in the
    // Goal Domain question: block (with a popup) any combination of it with
    // other domains. ---------------------------------------------------------
    if (isGoalDomain) {
      const currentSelections = Array.isArray(answers[qNode]) ? answers[qNode] : [];
      const isEatingDrinking = opt.option_text === EATING_AND_DRINKING_OPTION;
      if (!currentSelections.includes(opt.option_text)) {
        if ((isEatingDrinking && currentSelections.length > 0) ||
          (!isEatingDrinking && currentSelections.includes(EATING_AND_DRINKING_OPTION))) {
          setShowGoalDomainPopup(true);
          return false;
        }
      }
    }
    setAnswers(prev => {
      let newAnswers = { ...prev };
      if (!isMulti) {
        Object.keys(prev).forEach(key => { if (key.startsWith(`${qNode}_`)) delete newAnswers[key]; });
        newAnswers[qNode] = opt.option_text;
      } else {
        // A multi-select question can hold a legacy single-string value (e.g.
        // saved while the question was still single-select). Treat a string as
        // a one-element selection so it is preserved, not dropped, when the
        // first additional option is ticked.
        const currentSelections = Array.isArray(prev[qNode])
          ? [...prev[qNode]]
          : (typeof prev[qNode] === "string" && prev[qNode] ? [prev[qNode]] : []);
        if (currentSelections.includes(opt.option_text)) {
          newAnswers[qNode] = currentSelections.filter(s => s !== opt.option_text);
          delete newAnswers[`${qNode}_${opt.option_text}_sub`];
        } else {
          if (isGoalDomain && currentSelections.length >= 2) {
            return prev;
          }
          newAnswers[qNode] = [...currentSelections, opt.option_text];
        }
      }
      newAnswers = clearDownstreamAnswers(newAnswers, qNode);
      return newAnswers;
    });
    return true;
  };

  const handleSubOptionSelect = (qNode, parentOptText, subOpt, isMulti = false, limit = 1) => {
    setAnswers(prev => {
      let newAnswers = { ...prev };
      const key = `${qNode}_${parentOptText}_sub`;
      if (isMulti) {
        let currentSelections = Array.isArray(prev[key]) ? [...prev[key]] : [];
        if (currentSelections.includes(subOpt.option_text)) {
          newAnswers[key] = currentSelections.filter(s => s !== subOpt.option_text);
        } else {
          if (currentSelections.length < limit) newAnswers[key] = [...currentSelections, subOpt.option_text];
        }
      } else { newAnswers[key] = subOpt.option_text; }
      newAnswers = clearDownstreamAnswers(newAnswers, qNode);
      return newAnswers;
    });
  };

  const handleChainSubOptionSelect = (qNode, pathTexts, subOpt, isMulti = false, limit = 1) => {
    setAnswers(prev => {
      let newAnswers = { ...prev };
      const key = [qNode, ...pathTexts, "sub"].join("_");
      if (isMulti) {
        let currentSelections = Array.isArray(prev[key]) ? [...prev[key]] : [];
        if (currentSelections.includes(subOpt.option_text)) {
          newAnswers[key] = currentSelections.filter(s => s !== subOpt.option_text);
        } else {
          if (currentSelections.length < limit) newAnswers[key] = [...currentSelections, subOpt.option_text];
        }
      } else {
        newAnswers[key] = subOpt.option_text;
      }
      newAnswers = clearDownstreamAnswers(newAnswers, qNode);
      return newAnswers;
    });
  };

  // ---------------------------------------------------------------------------
  // HINE HANDLERS
  // ---------------------------------------------------------------------------

  // Typing allows digits with ONE decimal point (e.g., 12.3)
  const sanitizeDecimalText = (value) => {
    let s = String(value).replace(/[^0-9.]/g, "");
    const dot = s.indexOf(".");
    if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
    return s.replace(/^0+(?=[0-9])/, "");
  };

  // Clear the default "0" on focus so the user can type directly; restore "0" on blur if left empty
  const clearZeroOnFocusHine = (e, index, field) => {
    if (["hineScore", "rightAsym", "leftAsym"].includes(field)) {
      const cur = hineData[index]?.[field];
      if (cur === 0 || cur === "0" || cur === "" || cur == null) {
        handleHineChange(index, field, "");
      }
    }
  };
  const restoreZeroOnBlurHine = (e, index, field) => {
    if (["hineScore", "rightAsym", "leftAsym"].includes(field) && (hineData[index]?.[field] === "" || hineData[index]?.[field] == null)) {
      handleHineChange(index, field, "0");
    }
  };
  // Same for HAI (Each-hand + Asymmetry) fields
  const clearZeroOnFocusHai = (e, index, field) => {
    if (["rightEahs", "leftEahs", "eachHandSumScore", "asymmetryIndex"].includes(field)) {
      const cur = haiData[index]?.[field];
      if (cur === 0 || cur === "0" || cur === "" || cur == null) {
        handleHaiChange(index, field, "");
      }
    }
  };
  const restoreZeroOnBlurHai = (e, index, field) => {
    if (["rightEahs", "leftEahs", "eachHandSumScore", "asymmetryIndex"].includes(field) && (haiData[index]?.[field] === "" || haiData[index]?.[field] == null)) {
      handleHaiChange(index, field, "0");
    }
  };

  // ---------------------------------------------------------------------------
  // HINE / HAI ORIGINAL-VALUE PRESERVATION HELpers
  // ---------------------------------------------------------------------------
  const hineSnapshotOf = (row) => ({
    timepoint: row?.timepoint || "",
    hineScore: Number(row?.hineScore) || 0,
    rightAsym: Number(row?.rightAsym) || 0,
    leftAsym: Number(row?.leftAsym) || 0,
  });
  const haiSnapshotOf = (row) => ({
    timepoint: row?.timepoint || "",
    rightEahs: Number(row?.rightEahs) || 0,
    leftEahs: Number(row?.leftEahs) || 0,
    eachHandSumScore: Number(row?.eachHandSumScore) || 0,
    asymmetryIndex: Number(row?.asymmetryIndex) || 0,
  });
  const hineRowMeaningful = (s) => Boolean(s.timepoint) || s.hineScore !== 0 || s.rightAsym !== 0 || s.leftAsym !== 0;
  const haiRowMeaningful = (s) => Boolean(s.timepoint) || s.rightEahs !== 0 || s.leftEahs !== 0 || s.eachHandSumScore !== 0 || s.asymmetryIndex !== 0;
  const hineRowsEqual = (a, b) => a.timepoint === b.timepoint && a.hineScore === b.hineScore && a.rightAsym === b.rightAsym && a.leftAsym === b.leftAsym;
  const haiRowsEqual = (a, b) => a.timepoint === b.timepoint && a.rightEahs === b.rightEahs && a.leftEahs === b.leftEahs && a.eachHandSumScore === b.eachHandSumScore && a.asymmetryIndex === b.asymmetryIndex;
  const recordHineOrigins = (rows) => {
    (rows || []).forEach(r => {
      if (r && r.id != null && hineOriginRef.current[r.id] === undefined) hineOriginRef.current[r.id] = hineSnapshotOf(r);
    });
  };
  const recordHaiOrigins = (rows) => {
    (rows || []).forEach(r => {
      if (r && r.id != null && haiOriginRef.current[r.id] === undefined) haiOriginRef.current[r.id] = haiSnapshotOf(r);
    });
  };

  const handleHineChange = (index, field, value) => {
    const updated = [...hineData];
    const row = updated[index];
    if (!row) return;
    if (["hineScore", "rightAsym", "leftAsym"].includes(field)) {
      updated[index][field] = sanitizeDecimalText(value);
    } else { updated[index][field] = value; }

    // EDITED DATA KEEPS ITS PRE-EDIT ROW: when a row that carried a real score
    // in from the history stack is changed, do not simply erase the old value.
    // Demote the ORIGINAL value one row below the edited row (exactly once, on
    // the first edit), so a following Review shows the last edited data on top
    // AND the previous data still there as one row underneath - and it keeps
    // accumulating over every successive Review.
    const editedId = row.id;
    const origin = editedId != null ? hineOriginRef.current[editedId] : undefined;
    if (origin) {
      const nextSnap = hineSnapshotOf(updated[index]);
      const alreadyBelow = updated.some(
        r => r.id !== editedId && hineRowsEqual(hineSnapshotOf(r), origin)
      );
      if (hineRowMeaningful(origin) && !alreadyBelow && !hineRowsEqual(nextSnap, origin)) {
        const copy = {
          id: Date.now() + Math.random(),
          timepoint: origin.timepoint,
          hineScore: origin.hineScore,
          rightAsym: origin.rightAsym,
          leftAsym: origin.leftAsym,
        };
        updated.splice(index + 1, 0, copy);
        // The copy becomes a protected row itself; the edited row's original
        // mapping is dropped so further keystrokes never demote it again.
        hineOriginRef.current[copy.id] = { ...origin };
        delete hineOriginRef.current[editedId];
      }
    }

    setHineData(updated);
    const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
    if (hineQuestion && answers[hineQuestion.node_number] === "Yes") {
      saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
        updated.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
    }
  };
  // + / - steps ONLY the whole-number (left) part; decimal part stays unchanged (12.3 -> 13.3)
  const handleHineIncrement = (index, field) => { const cur = parseFloat(hineData[index]?.[field]) || 0; const intPart = Math.trunc(cur); const frac = Math.round((cur - intPart) * 100) / 100; handleHineChange(index, field, String(intPart + 1 + frac)); };
  const handleHineDecrement = (index, field) => { const cur = parseFloat(hineData[index]?.[field]) || 0; const intPart = Math.trunc(cur); if (intPart > 0) { const frac = Math.round((cur - intPart) * 100) / 100; handleHineChange(index, field, String(intPart - 1 + frac)); } };
  const addHineRow = () => {
    setHineData(prev => {
      // The new "earlier score" row is inserted at the TOP of the earlier list
      // (index 1, directly under the + / - bar), so the score added last is the
      // first one shown. It used to be appended at the bottom, which pushed the
      // newest entry to the end of the column.
      const newRows = [...prev];
      newRows.splice(1, 0, { id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 });
      recordHineOrigins(newRows);
      const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
      if (hineQuestion && answers[hineQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
      return newRows;
    });
  };
  const removeHineRow = (index) => {
    setHineData(prev => {
      const removed = prev[index];
      if (removed && removed.id != null) delete hineOriginRef.current[removed.id];
      const newRows = prev.filter((_, i) => i !== index);
      const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
      if (hineQuestion && answers[hineQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
      return newRows;
    });
  };

  // ---------------------------------------------------------------------------
  // HAI HANDLERS
  // ---------------------------------------------------------------------------

  const handleHaiChange = (index, field, value) => {
    const updated = [...haiData];
    const row = updated[index];
    if (!row) return;
    if (["rightEahs", "leftEahs", "asymmetryIndex", "eachHandSumScore"].includes(field)) {
      updated[index][field] = sanitizeDecimalText(value);
    } else { updated[index][field] = value; }

    // Same as HINE: editing a carried-over row demotes its ORIGINAL value one
    // row below (once), so the pre-edit HAI row survives into every later Review.
    const editedId = row.id;
    const origin = editedId != null ? haiOriginRef.current[editedId] : undefined;
    if (origin) {
      const nextSnap = haiSnapshotOf(updated[index]);
      const alreadyBelow = updated.some(
        r => r.id !== editedId && haiRowsEqual(haiSnapshotOf(r), origin)
      );
      if (haiRowMeaningful(origin) && !alreadyBelow && !haiRowsEqual(nextSnap, origin)) {
        const copy = {
          id: Date.now() + Math.random(),
          timepoint: origin.timepoint,
          rightEahs: origin.rightEahs,
          leftEahs: origin.leftEahs,
          eachHandSumScore: origin.eachHandSumScore,
          asymmetryIndex: origin.asymmetryIndex,
        };
        updated.splice(index + 1, 0, copy);
        haiOriginRef.current[copy.id] = { ...origin };
        delete haiOriginRef.current[editedId];
      }
    }

    setHaiData(updated);
    const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
    if (haiQuestion && answers[haiQuestion.node_number] === "Yes") {
      saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
        updated.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
    }
  };
  // + / - steps ONLY the whole-number (left) part; decimal part stays unchanged (12.3 -> 13.3)
  const handleHaiIncrement = (index, field) => { const cur = parseFloat(haiData[index]?.[field]) || 0; const intPart = Math.trunc(cur); const frac = Math.round((cur - intPart) * 100) / 100; handleHaiChange(index, field, String(intPart + 1 + frac)); };
  const handleHaiDecrement = (index, field) => { const cur = parseFloat(haiData[index]?.[field]) || 0; const intPart = Math.trunc(cur); if (intPart > 0) { const frac = Math.round((cur - intPart) * 100) / 100; handleHaiChange(index, field, String(intPart - 1 + frac)); } };
  const addHaiRow = () => {
    setHaiData(prev => {
      // Same as HINE: the newest "earlier score" row lands at the TOP of the
      // earlier list (index 1), not at the bottom of the column.
      const newRows = [...prev];
      newRows.splice(1, 0, { id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 });
      recordHaiOrigins(newRows);
      const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
      if (haiQuestion && answers[haiQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
      return newRows;
    });
  };
  const removeHaiRow = (index) => {
    setHaiData(prev => {
      const removed = prev[index];
      if (removed && removed.id != null) delete haiOriginRef.current[removed.id];
      const newRows = prev.filter((_, i) => i !== index);
      const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
      if (haiQuestion && answers[haiQuestion.node_number] === "Yes")
        saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
          newRows.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
      return newRows;
    });
  };

  // ---------------------------------------------------------------------------
  // MISC HELPERS
  // ---------------------------------------------------------------------------

  const getContinuousNumber = (targetQuestion) => {
    const targetIndex = visibleSteps.findIndex(q => q.node_number === targetQuestion.node_number);
    return 3 + targetIndex + 1;
  };

  // Left-panel keyword text for a question.
  // The Goal Domain question is the ONLY one that used to get a special label
  // ("Goals - <skills>"), which both renamed the keyword and moved the skills
  // in front of the answer value. Combined with the row's own " - " separator
  // that produced "Goals - Sitting - Sitting, Play and Cognition" - the domain
  // ("Motor") was lost entirely. The keyword is now shown as stored
  // ("Goal Domain") and the skills are rendered as part of the answer instead,
  // so the row reads "Goal Domain - Motor - Sitting, Play and Cognition".
  const getPanelKeywordLabel = (q) => {
    if (q.keywords) return q.keywords;
    return isGoalDomainQuestion(q) ? "Goal Domain" : "Keywords";
  };

  const getAnswerDisplayText = (q) => {
    const mainAns = answers[q.node_number];
    if (mainAns === undefined || mainAns === null || mainAns === "") return "";
    const mainAnsArr = Array.isArray(mainAns) ? mainAns : [mainAns];
    const parts = [];
    const isGoalDomain = isGoalDomainQuestion(q);
    mainAnsArr.forEach(ansText => {
      const selectedOpt = q.options?.find(o => o.option_text === ansText);
      const hasSubOptions = selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0;
      // "Motor-Sitting" style values arrive glued from a few older paths.
      const domain = cleanGoalDomainText(ansText);
      if (hasSubOptions) {
        const domainKey = `${q.node_number}_${ansText}_sub`;
        const subAns = answers[domainKey];
        const stdSubAns = answers[`${q.node_number}_sub`];
        const rawSub = subAns || stdSubAns;
        const subArr = rawSub == null || rawSub === "" ? [] : (Array.isArray(rawSub) ? rawSub : [rawSub]);
        if (subArr.length > 0) {
          if (isGoalDomain) {
            // Goal Domain: keep the domain in front of its own skill, otherwise a
            // skill shared by two domains (or a domain with no skill list) is
            // impossible to tell apart - "Sitting, Play and Cognition" says
            // nothing about what "Sitting" belongs to.
            subArr.forEach(s => {
              const skill = String(s || "").trim();
              if (skill) parts.push(`${domain} - ${skill}`);
            });
          } else {
            const subText = subArr.map(s => String(s || "").trim()).filter(Boolean).join(", ");
            if (subText) parts.push(subText);
            else parts.push(ansText);
          }
        } else {
          parts.push(isGoalDomain ? domain : ansText);
        }
      } else {
        parts.push(isGoalDomain ? domain : ansText);
      }
    });
    // Guard: a part can be resolved both from a sub-option chain AND from the
    // raw main-answer element (older data held the flattened ["Yes","Unilateral"]
    // array / "Yes,Unilateral" string). Collapse so the label never repeats.
    return Array.from(new Set(parts)).join(", ");
  };

  // -- ACTIVE GOAL CAPACITY -------------------------------------------------
  // Business rule: "A client can have only one Initial appointment, but can
  // have multiple Review appointments over time." The limit is on goals, not
  // appointments - a client with 4 goals whose status is `In progress - on
  // track` or `In progress - delayed` cannot start another goal journey until
  // one of those journeys is closed.
  //
  // The 4 is configurable per call (the API defaults to it) so the threshold
  // lives in one place if the business rule ever changes.
  const MAX_ACTIVE_GOALS = 4;

  // Exact wording required by the spec. The API returns the same sentence; this
  // is the local fallback so the popup always shows it, even if a network
  // hiccup means the response body was lost.
  const GOAL_CAPACITY_MESSAGE =
    "This client has 4 goals in progress already. You will not be able to Set a new goal unless any of the existing Goal journey is closed";

  // PATHWAY_UNAVAILABLE_MESSAGE now lives at module scope (see next to
  // isReviewOption above) because the Review Smart Goal pathway check raises it
  // from an effect declared earlier in the component than this handler.

  // Asks the server how many live goals the client has. Returns the parsed JSON
  // (or null when the call itself failed - the caller then stays permissive and
  // lets save_basic_details.php make the authoritative decision, so a flaky
  // network can never trap the therapist on the step-1 form).
  const checkGoalCapacity = async (clientName, ageGroup, clientId = null) => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/check_goal_capacity.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({
            client_name: clientName,
            age_group: ageGroup,
            client_id: clientId ? parseInt(clientId) : 0,
            limit: MAX_ACTIVE_GOALS,
          }),
        }
      );
      return await response.json();
    } catch (error) {
      console.error("Failed to check active goal capacity:", error.message);
      return null;
    }
  };

  // Only the plain "Review" appointment (Set New Goal for Existing Clients)
  // creates a NEW goal journey, so only it is capacity-checked. Review Smart
  // Goal revises the existing goal and Initial is already one-per-client.
  const isGoalCapacityGuarded = () => session === APPT_REVIEW;

  // Selecting a client on the Review step 1. Returns true when the client was
  // accepted, false when it was blocked - the caller must not apply any state
  // in the blocked case, which is what keeps the selection from sticking.
  const verifyClientCapacity = async (clientName, ageGroup = "", clientId = null) => {
    if (!isGoalCapacityGuarded() || !clientName) return true;

    setGoalCapacityChecking(clientName);
    try {
      const json = await checkGoalCapacity(clientName, ageGroup, clientId);
      // A failed check never blocks: save_basic_details.php re-enforces the
      // rule server-side and shows the popup there.
      if (!json || typeof json !== "object") return true;
      if (json.blocked) {
        setGoalCapacityBlocked({
          client_name: clientName,
          age_group: ageGroup,
          active_count: json.active_count ?? MAX_ACTIVE_GOALS,
          limit: json.limit ?? MAX_ACTIVE_GOALS,
          message: json.message || GOAL_CAPACITY_MESSAGE,
          goals: Array.isArray(json.goals) ? json.goals : [],
        });
        return false;
      }
      return true;
    } finally {
      setGoalCapacityChecking(null);
    }
  };

  // -- Initial can only be performed ONCE per client. Asks the server before
  // creating anything so the therapist gets an actionable message instead of a
  // duplicate client/session. save_basic_details.php enforces the same rule
  // server-side, so this is a fast-fail convenience rather than the guard.
  const checkInitialEligibility = async (clientName, ageGroup) => {
    const response = await fetchWithTimeout(
      `${API_BASE}/therapist_api/check_initial_eligibility.php`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ appointment_type: "initial", client_name: clientName, age_group: ageGroup }),
      }
    );
    return response.json();
  };

  // -- Step 1 "Next" for every appointment type.
  // Single API for all three: save_basic_details.php. It is the same endpoint
  // the Initial flow has always used, and it now also handles the review
  // appointment types by accepting client_id (reuse the existing client instead
  // of inserting a duplicate), resuming an in-progress session for the same
  // client + appointment type, and persisting Q1-Q3 for review sessions.
  // Returns the parsed JSON on success, or null (with the error already shown).
  const handleSaveBasicDetails = async () => {
    if (!token || !session || !client || !ageRange) return null;

    // Both review modes run against an EXISTING client, so the client id is
    // mandatory - it is what tells the server to reuse the client row.
    if (isReviewMode && !selectedClientId) {
      setToast({ message: "Please select a client first", type: "error" });
      return null;
    }

    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/save_basic_details.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({
            // Send the EXACT mode, not a generic "review". Both review modes run
            // the same `review` pathway, so this is the only place the
            // distinction is recorded - save_basic_details.php persists it in
            // node 1, and /continue-assessment reads it back for the Appointment
            // Type column and to restore the right mode on resume.
            appointment_type: isSmartGoalMode
              ? "review smart goal"
              : isReviewMode
                ? "review"
                : "initial",
            client_name: client,
            age_group: ageRange,
            client_id: isReviewMode && selectedClientId ? parseInt(selectedClientId) : undefined,
            // For Review Smart Goal, pass the originating goal session id so
            // the revision context is preserved across Save for Later.
            goal_session_id: isSmartGoalMode && goalSessionId ? parseInt(goalSessionId) : undefined,
          })
        }
      );
      const json = await response.json();
      if (json.status) {
        const sid = json.sessionId || json.session_id || json.data?.sessionId || json.data?.session_id || null;
        if (sid) setCurrentSessionId(sid);
        return json;
      }
      if (isTokenError(json)) { dispatchSessionExpired(); return null; }
      // Server-side one-time-per-client guard (mirrors check_initial_eligibility).
      if (json.code === "initial_already_completed") {
        setInitialBlocked({ ...(json.existing || {}), message: json.message });
        return null;
      }
      // Server-side active-goal-capacity guard. This is the authoritative check:
      // it also fires when the client was picked before it reached 4 goals and
      // a parallel session took it over, or when the step-1 capacity pre-check
      // could not run. Clear the selection so the therapist cannot press Next
      // again on a client that is not allowed.
      if (json.code === "goal_capacity_reached") {
        setGoalCapacityBlocked({
          client_name: client,
          age_group: ageRange,
          active_count: json.active_count ?? MAX_ACTIVE_GOALS,
          limit: json.limit ?? MAX_ACTIVE_GOALS,
          message: json.message || GOAL_CAPACITY_MESSAGE,
          goals: Array.isArray(json.goals) ? json.goals : [],
        });
        setClient("");
        setClientSearchTerm("");
        setAgeRange("");
        setSelectedReviewSessionId(null);
        setSelectedClientId(null);
        setInitialHistory(null);
        return null;
      }
      /*
       * EDIT LOCK - an admin has this pathway open in Edit mode.
       *
       * Detected two ways on purpose. `locked` is what the server has always
       * sent; `code` is the structured field added alongside it. The live server
       * may not have the newer save_basic_details.php yet, so matching only on
       * `code` would silently drop back to a toast there.
       */
      if (json.locked === true || json.code === "pathway_locked") {
        /*
         * Show the therapist the SAME sentence as the missing-pathway case.
         * While an admin has the pathway open in Edit mode the pathway is, from
         * here, unavailable - and "under User ID: admin_x" is an internal detail
         * the therapist can do nothing with. `admin_message` / `lock_holder` are
         * still returned by the server and logged to the console so support can
         * find out who is holding it.
         *
         * Nothing is cleared: the lock is transient, and the admin's edit is not
         * the age's fault, so the client's own age band stays selected.
         */
        console.warn(
          "Pathway blocked by an edit lock.",
          json.admin_message || json.message,
          json.lock_holder ? `(holder: ${json.lock_holder})` : ""
        );
        setPathwayBlocked({
          kind: "locked",
          message: PATHWAY_UNAVAILABLE_MESSAGE,
          ageGroup: ageRange || null,
        });
        return null;
      }

      /*
       * NO PATHWAY FOR THIS AGE.
       *
       * Reached when the age was picked or auto-filled but the admin has not
       * built a pathway of this appointment type for that band, and the
       * any-age-group fallback found nothing either. Same two-way detection as
       * above: the structured `code` when the server sends it, and the legacy
       * message when it does not.
       */
      const msgText = typeof json.message === "string" ? json.message : "";
      if (json.code === "pathway_not_available" || /no pathway found/i.test(msgText)) {
        setPathwayBlocked({
          kind: "no_pathway",
          message: PATHWAY_UNAVAILABLE_MESSAGE,
          ageGroup: json.age_group || ageRange || null,
        });
        /*
         * Clear ONLY the age, not the client. The age group dropdown is
         * populated from the pathways that exist, so an age with no pathway can
         * only have come from an auto-fill or a stale list - and leaving it in
         * place would make Next fail the same way forever with no way out.
         * Clearing the client as well would throw away work the therapist did
         * not ask to lose.
         */
        setAgeRange("");
        return null;
      }

      setToast({ message: json.message || "Failed to save basic details", type: "error" });
      return null;
    } catch (error) {
      setToast({ message: error.message || "Network error", type: "error" });
      return null;
    }
  };

  const handleSaveDraft = async () => {
    if (!currentSessionId) return;
    try {
      const url = isReviewMode
        ? `${API_BASE}/therapist_api/save_draft_session.php`
        : `${API_BASE}/therapist_api/save_draft.php`;
      const currentQ = step >= 2 && !isGoalDetailStep && !showReviewGoalScreen ? visibleSteps[step - 2] : null;
      const lastNode = currentQ ? currentQ.node_number : (targetNodeRef.current || null);
      const body = isReviewMode
        ? {
            sessionId: parseInt(currentSessionId),
            // Exact mode, so "Save for Later" keeps node 1 accurate and
            // /continue-assessment labels the row correctly.
            appointment_type: isSmartGoalMode ? "review smart goal" : "review",
            client_name: client,
            age_group: ageRange,
            answers, hine_data: hineData, hai_data: haiData, last_node: lastNode
          }
        : { sessionId: parseInt(currentSessionId), last_node: lastNode };
      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const json = await response.json();
      if (json.status) console.log("? Draft Saved:", json.message);
      else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
        console.error("? Failed to save draft:", json.message);
      }
    } catch (error) { console.error("Network error saving draft:", error.message); }
  };

  // -- "Save for Later" -------------------------------------------------------
  // Direct quick-save (no popup): keep the incomplete session, go to
  // /continue-assessment. `saveLaterBusyRef` blocks a second click while the
  // first save is still in flight.
  const handleKeepDraftAndLeave = async () => {
    if (saveLaterBusyRef.current) return;
    saveLaterBusyRef.current = true;
    setAssessmentArmed(false); // approved exit - the leave guard must not bounce this
    await handleSaveDraft();
    saveLaterBusyRef.current = false;
    navigate("/continue-assessment");
  };

  const handleKeywordClick = (targetStep) => { setIsGoalDetailStep(false); setShowReviewGoalScreen(false); setStep(targetStep); };

  // -- Leave-prompt choices (raised by AssessmentLeaveGuard, not the button) --
  // Same two choices as Save for Later, but they take the therapist to the
  // destination they were actually heading for (the stashed URL) instead of
  // /continue-assessment, plus a Cancel that keeps them right here.
  const handleLeavePromptSave = async () => {
    setLeavePromptBusy(true);
    await handleSaveDraft(); // same "keep the incomplete session" call, mirrors handleKeepDraftAndLeave
    const dest = pendingLeaveDestRef.current || "/continue-assessment";
    pendingLeaveDestRef.current = null;
    setLeavePromptOpen(false);
    setAssessmentArmed(false); // approved exit: let the guard pass
    navigate(dest);
  };

  const handleLeavePromptDelete = async () => {
    const dest = pendingLeaveDestRef.current || "/continue-assessment";
    if (!currentSessionId) {
      // Nothing was created yet - there is no server row to delete, just leave.
      pendingLeaveDestRef.current = null;
      setLeavePromptOpen(false);
      setAssessmentArmed(false);
      navigate(dest);
      return;
    }
    setLeavePromptBusy(true);
    try {
      const response = await fetchWithTimeout(`${API_BASE}/therapist_api/delete_session.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ sessionId: parseInt(currentSessionId) })
      });
      const json = await response.json();

      if (isTokenError(json)) { dispatchSessionExpired(); return; }

      if (!json.status) {
        setLeavePromptBusy(false);
        setToast({ message: json.message || "Could not delete this session", type: "error" });
        return;
      }

      setCurrentSessionId(null);
      pendingLeaveDestRef.current = null;
      setLeavePromptOpen(false);
      setAssessmentArmed(false);
      navigate(dest);
    } catch (error) {
      setLeavePromptBusy(false);
      setToast({ message: error.message || "Network error", type: "error" });
    }
  };

  const handleLeavePromptCancel = () => {
    pendingLeaveDestRef.current = null;
    setLeavePromptOpen(false);
  };

  // -- Target for /summarypage ------------------------------------------------
  // The summary page needs to know whether this is a Review Smart Goal run and,
  // if so, which session holds the goal being revised. It used to be reached by
  // a bare navigate("/summarypage"), which carried neither - so the smart-goal
  // context was dropped and "Save and View Intervention" opened a normal
  // Intervention page that creates a new goal rather than revising the old one.
  const summaryHref = () => {
    const params = new URLSearchParams();
    if (currentSessionId) params.set("sessionId", String(currentSessionId));
    if (isSmartGoalMode) {
      params.set("smartGoal", "1");
      // Fall back to the assessment session itself: get_smart_goal_context.php
      // treats goal_session_id = 0 as "use the client's latest goal session", so
      // an empty value here still resolves server-side rather than being lost.
      params.set("goalSessionId", goalSessionId ? String(goalSessionId) : String(currentSessionId || ""));
    }
    return `/summarypage?${params.toString()}`;
  };

  const handleNext = () => {
    if (showInfoIntro) {
      // Read-and-continue page. Next is enabled as soon as the page renders,
      // and it hands over to the pathway's own first question - step 2, i.e.
      // the 4th question onwards (1 Appointment / 2 Client / 3 Age group).
      setShowInfoIntro(false);
      setStep(2);
      return;
    }
    if (showReviewGoalScreen) {
      navigate(summaryHref());
      return;
    }
    if (step === 1) {
      if (!isFormValid()) return;

      // Initial is one-time per client - verify with the server before
      // creating the client + session. Review and Review Smart Goal are
      // repeatable and skip this check entirely.
      if (session === APPT_INITIAL) {
        setInitialBlocked(null);
        checkInitialEligibility(client, ageRange)
          .then((json) => {
            if (json.status && json.eligible) {
              return handleSaveBasicDetails().then((saved) => { if (saved) setStep(2); });
            }
            if (json.code === "initial_already_completed") {
              setInitialBlocked({ ...(json.existing || {}), message: json.message });
              return;
            }
            setToast({ message: json.message || "Could not verify Initial Assessment eligibility", type: "error" });
          })
          .catch((error) => {
            setToast({ message: error.message || "Network error", type: "error" });
          });
        return;
      }

      // Review and Review Smart Goal ("Set New Goal for Existing Client"):
      // save_basic_details.php creates/resumes the review session and writes
      // Q1-Q3 (Review / Client / Age). Instead of jumping straight to step 2,
      // both existing-client paths show the "previously recorded information"
      // read-and-continue page first; its Next opens step 2 - the 4th question
      // onwards - where the previous assessment's Background Information &
      // Assessments through Comorbidities are pre-filled by the pre-fill
      // effect and the therapist carries on from there.
      handleSaveBasicDetails().then((saved) => {
        if (!saved) return;
        if (isReviewMode) setShowInfoIntro(true);
        else setStep(2);
      });
    } else {
      // If the selected option/sub-option chain of the current step carries
      // the "End" instruction, skip ahead straight to the summary page.
      if (step >= 2 && visibleSteps[step - 2]) {
        const currentQ = visibleSteps[step - 2];
        if (currentQ && selectedChainHasEnd(currentQ, answers)) {
          setStep(1 + visibleSteps.length + 1);
          return;
        }
      }
      if (isGoalDetailStep) { setIsGoalDetailStep(false); }
      else if (step >= 2 && visibleSteps[step - 2]) {
        const currentQ = visibleSteps[step - 2];
        const isGoalDomainQ = isGoalDomainQuestion(currentQ);
        const ans = answers[currentQ.node_number];
        if (isGoalDomainQ && Array.isArray(ans) && ans.length > 0) {
          const hasSubOptions = currentQ.options.some(opt => ans.includes(opt.option_text) && opt.sub_options && opt.sub_options.length > 0);
          if (hasSubOptions) { setIsGoalDetailStep(true); return; }
        }
      }
      const totalSteps = 1 + visibleSteps.length;
      if (step < totalSteps) { setStep(prev => prev + 1); }
      else if (step === totalSteps) { setStep(totalSteps + 1); }
      else if (step === totalSteps + 1) { navigate(summaryHref()); }
    }
  };

  const handleRestart = () => {
    localStorage.removeItem("assessmentState");
    localStorage.removeItem("reviewCarryOver");
    // The appointment type is KEPT (deliberately no setSession("")). Restarting
    // means re-running the SAME pathway, so "1. Select the appointment type"
    // must come up with the current type already checked - exactly like a
    // browser reload. Blanking session forced a pointless radio re-pick every
    // restart. Everything else (client, answers, step, screens) starts fresh.
    setClient(""); setAgeRange(""); setAnswers({});
    setHineData([{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
    setHaiData([{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
    setStep(1); setActivePathway(null); setIsGoalDetailStep(false);
    setOpenSections({ demographics: false, background: false, comorbidities: false });
    setCurrentSessionId(null); setInterventionsData([]); setActiveGoalTab(null);
    setShowReviewGoalScreen(false); setSelectedReviewSessionId(null); setClientSearchTerm("");
    setShowInfoIntro(false);
    setGoalSessionId(null); setSmartGoalContext(null);
    setInitialBlocked(null);
    targetNodeRef.current = null;
  };

  // -- Capture the three step-1 answers (Review / Client / Age) so goalreview's
  // "Continue Pathway" can pass them to the review page. These are the exact
  // values the therapist selected, stored so the review carries over even when
  // the client has no review session yet.
  //
  // Uses isReviewOption() rather than `=== APPT_REVIEW` so a Review Smart Goal
  // draft is captured too, and the real mode string is stored (not a
  // hardcoded "Review") so the consumer can restore the correct mode. -------
  useEffect(() => {
    if (isReviewOption(session) && client && ageRange) {
      localStorage.setItem("reviewCarryOver", JSON.stringify({ session, client, ageRange }));
    }
  }, [session, client, ageRange]);

  // -- Fetch favourite status when sessionId is available -------------------
  useEffect(() => {
    if (!currentSessionId || !token) return;
    const fetchFavourite = async () => {
      try {
        const response = await fetchWithTimeout(
          `${API_BASE}/therapist_api/pathway_favorites.php`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify({ sessionId: parseInt(currentSessionId), action: "status" })
          }
        );
        const json = await response.json();
        if (json.status !== undefined) setIsFavourite(json.is_favourite === true);
      } catch (error) { console.error("Failed to fetch favourite status:", error.message); }
    };
    fetchFavourite();
  }, [currentSessionId, token]);

  const handleToggleFavourite = async () => {
    if (!currentSessionId) return;
    try {
      const response = await fetchWithTimeout(
        `${API_BASE}/therapist_api/pathway_favorites.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify({ sessionId: parseInt(currentSessionId), action: "toggle" })
        }
      );
      const json = await response.json();
      if (json.status) {
        setIsFavourite(json.is_favourite === true);
        console.log(json.message);
      }
    } catch (error) {
      console.error("Failed to toggle favourite:", error.message);
    }
  };

  const handleBack = () => {
    if (showInfoIntro) { setShowInfoIntro(false); return; }
    if (showReviewGoalScreen) { setShowReviewGoalScreen(false); return; }
    if (isGoalDetailStep) { setIsGoalDetailStep(false); return; }
    if (step > 1) {
      // Review questions start at step 2. Back must not drop into the step-1
      // Review setup screen, whose "Next" would create a NEW review session
      // and send the user back to Goal Progress Update / goalreview.
      if (isReviewMode && step === 2) return;
      setStep(prev => prev - 1);
    }
  };

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const isFormValid = () => {
    // The read-and-continue page has nothing to answer: Next is live as soon
    // as it is on screen.
    if (showInfoIntro) return true;
    if (step === 1) {
      // Review Smart Goal whose selected session's band has no clinical
      // pathway: the spec popup is up and the assessment must STOP - Next
      // stays dead even after the popup is dismissed, until a band WITH a
      // pathway is in effect.
      if (rsgPathwayMissing) return false;
      return session && client.trim() !== "" && ageRange !== "";
    }
    if (isGoalDetailStep) {
      const currentQ = visibleSteps[step - 2];
      if (!currentQ) return false;
      const mainAns = answers[currentQ.node_number];
      if (!mainAns || !Array.isArray(mainAns) || mainAns.length === 0) return false;
      for (const selectedOptText of mainAns) {
        const selectedOpt = currentQ.options?.find(o => o.option_text === selectedOptText);
        if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
          const subKey = `${currentQ.node_number}_${selectedOptText}_sub`;
          if (!answers[subKey]) return false;
        }
      }
      return true;
    }
    const totalSteps = 1 + visibleSteps.length;
    if (step === totalSteps + 1) return true;
    const currentDynamicIndex = step - 2;
    const currentQ = visibleSteps[currentDynamicIndex];
    if (!currentQ) return false;
    const isGoalDomainQ = isGoalDomainQuestion(currentQ);
    if (isGoalDomainQ) { const ans = answers[currentQ.node_number]; return Array.isArray(ans) && ans.length > 0; }
    if (!currentQ.options || currentQ.options.length === 0) return true;
    const answer = answers[currentQ.node_number];
    if (!answer) return false;
    const selectedOpt = currentQ.options?.find(o => o.option_text === answer);
    if (selectedOpt?.sub_options?.length > 0) {
      const subAnswerKey = `${currentQ.node_number}_${answer}_sub`;
      const subAnswer = answers[subAnswerKey];
      if (Array.isArray(subAnswer)) return subAnswer.length > 0;
      return subAnswer !== undefined;
    }
    const instructions = parseInstructions(currentQ.instruction);
    if (instructions.isMulti) { const multiAns = answers[currentQ.node_number]; return Array.isArray(multiAns) && multiAns.length > 0; }
    return true;
  };

  // ---------------------------------------------------------------------------
  // DOWNLOAD & SHARE HELPERS
  // ---------------------------------------------------------------------------

  const openAllAccordions = () => {
    if (!interventionsData || !interventionsData.data) return () => { };
    const prevState = { ...accordionOpen };
    const allOpen = {};
    Object.keys(interventionsData.data).forEach(key => {
      (interventionsData.data[key] || []).forEach(intervention => {
        allOpen[`int-${intervention.id}`] = true;
      });
    });
    setAccordionOpen(allOpen);
    return () => setAccordionOpen(prevState);
  };

  const loadPdfLibraries = () => {
    return new Promise((resolve) => {
      const loadScript = (src) =>
        new Promise((res) => {
          if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
          const s = document.createElement("script");
          s.src = src;
          s.onload = res;
          document.head.appendChild(s);
        });

      Promise.all([
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"),
        loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"),
      ]).then(resolve);
    });
  };

  const captureSummaryAsPdfBlob = async () => {
    if (!summaryRef.current) throw new Error("Summary element not found");

    await loadPdfLibraries();

    await new Promise(r => setTimeout(r, 600));

    const element = summaryRef.current;
    const canvas = await window.html2canvas(element, {
      scale: 1.5,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const { jsPDF } = window.jspdf;
    const pdfWidth = 210;
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    const pdf = new jsPDF({ orientation: pdfHeight > pdfWidth ? "portrait" : "landscape", unit: "mm", format: "a4" });

    const pages = paginateCanvasToPdf({
      canvas,
      element,
      pdfWidth,
      pageHeightMm: pdf.internal.pageSize.getHeight(),
    });

    pages.forEach((p, i) => {
      if (i > 0) pdf.addPage();
      pdf.addImage(p.sliceImgData, "JPEG", 0, 0, pdfWidth, p.sliceHeightMm);
    });

    return pdf.output("blob");
  };

  // -- PREVIOUS DATA: download the open group's Initial + Review comparison
  // tables as a PDF. Captures the off-screen printable node, paginates it
  // onto A4 sheets with symmetric 12mm margins, and saves it. --------------
  const handleDownloadGroupData = async () => {
    setIsDownloadingGroupData(true);
    try {
      await loadPdfLibraries();
      await new Promise(r => setTimeout(r, 300));
      const node = groupHistoryPrintRef.current;
      if (!node) throw new Error("Printable node not found");

      const canvas = await window.html2canvas(node, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const { jsPDF } = window.jspdf;
      const pdfWidth = 210;
      const marginMm = 12;
      const contentWidthMm = pdfWidth - (marginMm * 2);
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pdf = new jsPDF({
        orientation: pdfHeight > pdfWidth ? "portrait" : "landscape",
        unit: "mm",
        format: "a4",
      });

      const pages = paginateCanvasToPdf({
        canvas,
        element: node,
        pdfWidth,
        pageHeightMm: pdf.internal.pageSize.getHeight(),
      });

      pages.forEach((p, i) => {
        if (i > 0) pdf.addPage();
        const scaledHeight = (p.sliceHeightMm / pdfWidth) * contentWidthMm;
        const pageHeight = pdf.internal.pageSize.getHeight();
        const yOffset = marginMm + Math.max(0, (pageHeight - (marginMm * 2) - scaledHeight) / 2);
        pdf.addImage(p.sliceImgData, "JPEG", marginMm, yOffset, contentWidthMm, scaledHeight);
      });

      const safeName = (historyGroupName || "group").replace(/[^a-zA-Z0-9-]/g, "-");
      pdf.save(`previous-data-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("Group data PDF generation failed:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloadingGroupData(false);
    }
  };

  const handleShareMail = async () => {
    setShowShareModal(false);
    setIsGeneratingPdf(true);
    const restoreAccordions = openAllAccordions();

    try {
      const blob = await captureSummaryAsPdfBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const subject = encodeURIComponent(`Assessment Summary ? ${client || "Client"}`);
      const body = encodeURIComponent(
        `Please find the assessment summary PDF attached.\n\nClient: ${client || "-"}\nAge Group: ${ageRange || "-"}\nSession Type: ${session || "-"}\n\nThe PDF has been downloaded to your device. Please attach it to this email before sending.`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } catch (err) {
      console.error("Mail share failed:", err);
      alert("Failed to generate PDF for email. Please try again.");
    } finally {
      restoreAccordions();
      setIsGeneratingPdf(false);
    }
  };

  const handleShareWhatsApp = async () => {
    setShowShareModal(false);
    setIsGeneratingPdf(true);
    const restoreAccordions = openAllAccordions();

    try {
      const blob = await captureSummaryAsPdfBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const message = encodeURIComponent(
        `Assessment Summary for ${client || "Client"}\nAge Group: ${ageRange || "-"} | Session: ${session || "-"}\n\nThe PDF summary has been downloaded to your device. Please share it via WhatsApp by attaching the file.`
      );
      window.open(`https://wa.me/?text=${message}`, "_blank");
    } catch (err) {
      console.error("WhatsApp share failed:", err);
      alert("Failed to generate PDF for WhatsApp. Please try again.");
    } finally {
      restoreAccordions();
      setIsGeneratingPdf(false);
    }
  };

  const handleSummaryConfirm = async () => {
    if (!currentSessionId) return;

    try {
      // Review / Review Smart Goal confirm through the review endpoint, which
      // (like the rest of the Review flow) scopes the update to sessions owned
      // by the signed-in therapist. The Initial flow keeps using the original
      // endpoint unchanged.
      const summaryConfirmUrl = isReviewMode
        ? `${API_BASE}/therapist_api/review_summary_confirm.php`
        : `${API_BASE}/therapist_api/summary_confirm.php`;
      const response = await fetchWithTimeout(
        summaryConfirmUrl,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: parseInt(currentSessionId),
          }),
        }
      );

      const json = await response.json();

      if (json.status) {
        // The assessment is finished. Drop any snapshot another page could
        // still read as a fallback and the review step-1 carry-over, so neither
        // leaks into the next appointment.
        try {
          localStorage.removeItem("assessmentState");
          localStorage.removeItem("reviewCarryOver");
        } catch (e) { /* private mode - best effort */ }

        setToast({ message: "Summary Saved Successfully.", type: "success" });
        setTimeout(() => {
          setAssessmentArmed(false); // completed + saved - the leave guard must not bounce the hand-off
          if (isSmartGoalMode) {
            // Continue the Review Smart Goal pathway: Intervention page ->
            // "Revise Selected Intervention" -> Updategoal -> goalsetdownload.
            // fromReview=1 flips the button label/flow; goalSessionId keeps the
            // revision pointed at the goal that is actually being reviewed.
            const params = new URLSearchParams({ sessionId: String(currentSessionId), fromReview: "1" });
            if (goalSessionId) params.set("goalSessionId", String(goalSessionId));
            if (selectedClientId) params.set("clientId", String(selectedClientId));
            params.set("smartGoal", "1");
            navigate(`/Interventions?${params.toString()}`);
            return;
          }
          if (isReviewMode) {
            // Plain Review ("Set New Goal for Existing Client"): same Intervention
            // hand-off the Review flow has always used.
            navigate(`/Interventions?sessionId=${currentSessionId}&fromReview=1`);
            return;
          }
          navigate(`/Interventions?sessionId=${currentSessionId}`);
        }, 1500);
      } else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
        alert(json.message || "Failed to confirm summary");
      }
    } catch (error) {
      console.error(error);
      alert("Network error");
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER HELPERS
  // ---------------------------------------------------------------------------
  const DescriptionContent = ({ html, color = "#323C43" }) => (
    <div
      className="description-content"
      style={{
        fontSize: "14px",
        color: "#323C43",
        textAlign: "left",
        lineHeight: "1.8",
        margin: 0,
      }}
      dangerouslySetInnerHTML={{
        __html: html || "",
      }}
    />
  );
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;
    const colorOrder = { purple: 1, grey: 2, yellow: 3, orange: 4, red: 5, cyan: 6, blue: 7, green: 8 };
    const sorted = [...attachments].sort((a, b) => (colorOrder[a.color] || 99) - (colorOrder[b.color] || 99));
    return (
      <div style={{ marginTop: "15px" }}>
        {sorted.map((att, idx) => (
          <div key={idx} style={{ marginBottom: "10px" }}>
            {att.color === "green" && (
              <div>
                {att.descriptions && att.descriptions.length > 0 ? (
                  att.descriptions.map((desc, dIdx) => {
                    const url = desc.link && desc.link.trim() !== "" ? (desc.link.startsWith("http") ? desc.link : "https://" + desc.link) : null;
                    const extractUrlFromHtml = (html) => {
                      if (!html) return null;
                      const match = html.match(/href=["']([^"']+)["']/i);
                      return match ? match[1] : null;
                    };
                    return (
                      <div className="" key={dIdx}>
                        <button className="fact-btn10" onClick={() => {
                          if (url) {
                            window.open(url, "_blank");
                          } else if (desc.description) {
                            const href = extractUrlFromHtml(desc.description);
                            if (href) {
                              window.open(href.startsWith("http") ? href : "https://" + href, "_blank");
                            }
                          }
                        }}>
                          <FontAwesomeIcon icon={faDownload} className="header-icon10" aria-hidden="true" /> {desc.title || att.title || "Resource Link"}
                        </button>
                      </div>
                    );
                  })
                ) : att.file_path ? (
                  <div>
                    <button className="fact-btn10" onClick={() => {
                      const fileUrl = att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path;
                      window.open(fileUrl, "_blank");
                    }}>
                      <FontAwesomeIcon icon={faDownload} className="header-icon10" aria-hidden="true" /> {att.title || "Resource Link"}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
            {att.color === "blue" && att.file_path && (
              <div style={{ marginTop: "10px", background: "#E6F7FC", borderRadius: "8px", padding: "10px" }}>
                <a
                  href={att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="attachment-image-link"
                  aria-label={att.title || "Open attachment image in new tab"}
                >
                  <img
                    src={att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path}
                    alt={att.title || "Attachment"}
                    style={{ maxWidth: "100%", maxHeight: "250px", borderRadius: "8px", objectFit: "contain", border: "1px solid #e0e0e0", display: "block" }}
                  />
                </a>
              </div>
            )}
            {att.color === "purple" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box0" key={dIdx}>
                {desc.title ? (
                  <>
                    <p style={{ fontSize: "14px", color: "#323C43", textAlign: "start", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                      <FontAwesomeIcon icon={faBookOpenReader} aria-hidden="true" style={{ marginTop: "5px" }} />
                      <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#323C43",
                        textAlign: "left",
                        paddingLeft: "22px",
                        lineHeight: "1.8",
                        whiteSpace: "pre-line",
                        margin: 0,
                      }}
                    >
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>                </>)
                  :
                  (<p style={{ fontSize: "14px", color: "#323C43", textAlign: "start", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faBookOpenReader} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "grey" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box" key={dIdx}>
                {desc.title ? (
                  <>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#323C43",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                      }}
                    >
                      <img
                        src={infoIcon}
                        alt="" aria-hidden="true"
                        className="infoicon"
                        style={{
                          marginTop: "5px",
                        }}
                      />

                      <span style={{ fontWeight: "bold" }}>
                        {desc.title}
                      </span>
                    </p>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "#323C43",
                        textAlign: "left",
                        paddingLeft: "22px",
                        lineHeight: "1.8",
                        whiteSpace: "pre-line",
                        margin: 0,
                      }}
                    >
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}
                  >
                    <img
                      src={infoIcon}
                      alt="" aria-hidden="true"
                      className="infoicon"
                      style={{
                        marginTop: "5px",
                        minWidth: "13px",
                      }}
                    />

                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>
                )}
              </div>
            ))}
            {att.color === "yellow" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box1" key={dIdx}>
                {desc.title ? (<><p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <img src={rightarrow} alt="" aria-hidden="true" className="infoicon" style={{ width: "20px", height: "20px" }} /><span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "27px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <img src={rightarrow} alt="" aria-hidden="true" className="infoicon" style={{ width: "20px", height: "20px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "orange" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box2" key={dIdx}>
                {desc.title ? (<>
                  <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faCopy} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "25px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faCopy} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "red" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box3" key={dIdx}>
                {desc.title ? (<>
                  <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "26px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>
                  </p>)}
              </div>
            ))}
            {att.color === "cyan" && att.descriptions && att.descriptions.map((desc, dIdx) => (
              <div className="info-box4" key={dIdx}>
                {desc.title ? (<>
                  <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faLink} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                  <div
                    style={{
                      paddingLeft: "24px",
                      marginTop: "5px",
                    }}
                  >
                    <DescriptionContent
                      html={desc.description}
                      color="#323C43"
                    />
                  </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <FontAwesomeIcon icon={faLink} aria-hidden="true" style={{ marginTop: "5px" }} />
                    <div style={{
                      fontSize: "14px",
                      color: "#323C43",
                      textAlign: "start",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                      lineHeight: "1.8",
                      whiteSpace: "pre-line",
                    }}>
                      <DescriptionContent
                        html={desc.description}
                        color="#323C43"
                      />
                    </div>

                  </p>)}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderSubOptionsChain = (qNode, pathTexts, subOptions, parentInstruction) => {
    if (!subOptions || subOptions.length === 0) return null;
    const subOptionLimit = parseSubOptionLimit(parentInstruction);
    const subInstructions = parseInstructions(parentInstruction);
    const isMultiSub = subOptionLimit > 1 || subInstructions.isMulti;
    const multiLimit = subOptionLimit > 1 ? subOptionLimit : null;
    const chainKey = [qNode, ...pathTexts, "sub"].join("_");
    const selectedValRaw = answers[chainKey];
    const selectedArr = Array.isArray(selectedValRaw) ? selectedValRaw : (selectedValRaw ? [selectedValRaw] : []);
    const selectedSubs = subOptions.filter(sub => selectedArr.includes(sub.option_text));
    const selectedSubAttachments = selectedSubs.reduce((acc, s) => acc.concat(s.attachments || []), []);

    return (
      <>
        {isMultiSub ? (
          multiLimit ? (
            <p className="question" style={{ marginBottom: "5px" }}>
              Please select up to <strong>{multiLimit}</strong> options:
              <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>(Selected: {selectedArr.length}/{multiLimit})</span>
            </p>
          ) : (
            <p className="question" style={{ marginBottom: "5px" }}>
              Please select more than one option:
              <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>(Selected: {selectedArr.length})</span>
            </p>
          )
        ) : (
          <p className="question">Please select from the list below:</p>
        )}
        <div className={`radio-group2 ${parentInstruction === "Vertical" ? "vertical" : ""}`}>
          {subOptions.map(sub => {
            const hasDeeper = sub.sub_options && sub.sub_options.length > 0;
            if (isMultiSub) {
              const isChecked = selectedArr.includes(sub.option_text);
              const isDisabled = multiLimit !== null && !isChecked && selectedArr.length >= multiLimit;
              return (
                <div key={sub.option_id} style={{ marginBottom: hasDeeper ? "10px" : "0" }}>
                  <label style={{ opacity: isDisabled ? 0.6 : 1, display: "block" }}>
                    <input type="checkbox" name={chainKey} value={sub.option_text} checked={isChecked} disabled={isDisabled}
                      onChange={() => {
                        let currSel = Array.isArray(answers[chainKey]) ? [...answers[chainKey]] : [];
                        let nextSel = isChecked ? currSel.filter(s => s !== sub.option_text) : [...currSel, sub.option_text];
                        handleChainSubOptionSelect(qNode, pathTexts, sub, true, multiLimit ?? subOptions.length);
                        let allSubIds = subOptions.filter(o => nextSel.includes(o.option_text)).map(o => o.option_id);
                        const targetNode = sub.node_number || qNode;
                        const parentIdForSave = sub.parent_option_id || sub.option_id;
                        saveResponseAPI(currentSessionId, targetNode, "option", parentIdForSave, null, allSubIds.length === 0 ? [] : allSubIds);
                      }} /> <RichText inline html={sub.option_text} />
                  </label>
                  {isChecked && hasDeeper && (
                    <div style={{ marginLeft: "30px", marginTop: "10px", borderLeft: "2px solid #ddd", paddingLeft: "10px" }}>
                      {renderSubOptionsChain(qNode, [...pathTexts, sub.option_text], sub.sub_options, sub.instruction || "")}
                    </div>
                  )}
                </div>
              );
            }
            const isSelectedRadio = selectedArr[0] === sub.option_text;
            return (
              <div key={sub.option_id} style={{ marginBottom: hasDeeper ? "10px" : "0" }}>
                <label style={{ display: "block" }}>
                  <input type="radio" name={chainKey} value={sub.option_text} checked={isSelectedRadio}
                    onChange={() => {
                      handleChainSubOptionSelect(qNode, pathTexts, sub, false, 1);
                      if (!isSelectedRadio) saveResponseAPI(currentSessionId, sub.node_number, "option", sub.option_id, sub.option_text);
                      else saveResponseAPI(currentSessionId, sub.node_number, "question", sub.option_id, "");
                    }} /> <RichText inline html={sub.option_text} />
                </label>
                {isSelectedRadio && (
                  <div style={{ marginLeft: "30px" }}>
                    {hasDeeper && (
                      <div style={{ borderLeft: "2px solid #ddd", paddingLeft: "10px", marginTop: "10px" }}>
                        {renderSubOptionsChain(qNode, [...pathTexts, sub.option_text], sub.sub_options, sub.instruction || "")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {renderAttachments(selectedSubAttachments)}
      </>
    );
  };

  // -- HINE UI ---------------------------------------------------------------
  const renderHINESection = (selectedVal) => {
    if (selectedVal !== "Yes") return null;
    return (
      <div className="hine-section">
        <div className="hine-row-single">
          <div className="hine-field time-field">
            <label htmlFor="hine-timepoint-0">Timepoint</label>
            <input
              type="text"
              id="hine-timepoint-0"
              placeholder="Enter age in months"
              value={hineData[0]?.timepoint || ""}
              className="hine-input"
              onChange={e => handleHineChange(0, "timepoint", e.target.value)}
            />
          </div>
          <div className="hine-field">
            <label htmlFor="hine-score-0">HINE Score</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHineDecrement(0, "hineScore")} aria-label="Decrease HINE Score">-</button>
              <input
                type="text"
                id="hine-score-0"
                value={hineData[0]?.hineScore ?? ""}
                onChange={e => handleHineChange(0, "hineScore", e.target.value)}
                onFocus={e => clearZeroOnFocusHine(e, 0, "hineScore")}
                onBlur={e => restoreZeroOnBlurHine(e, 0, "hineScore")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHineIncrement(0, "hineScore")} aria-label="Increase HINE Score">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hine-right-asym-0">Right Asymmetries</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHineDecrement(0, "rightAsym")} aria-label="Decrease Right Asymmetries">-</button>
              <input
                type="text"
                id="hine-right-asym-0"
                value={hineData[0]?.rightAsym ?? ""}
                onChange={e => handleHineChange(0, "rightAsym", e.target.value)}
                onFocus={e => clearZeroOnFocusHine(e, 0, "rightAsym")}
                onBlur={e => restoreZeroOnBlurHine(e, 0, "rightAsym")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHineIncrement(0, "rightAsym")} aria-label="Increase Right Asymmetries">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hine-left-asym-0">Left Asymmetries</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHineDecrement(0, "leftAsym")} aria-label="Decrease Left Asymmetries">-</button>
              <input
                type="text"
                id="hine-left-asym-0"
                value={hineData[0]?.leftAsym ?? ""}
                onChange={e => handleHineChange(0, "leftAsym", e.target.value)}
                onFocus={e => clearZeroOnFocusHine(e, 0, "leftAsym")}
                onBlur={e => restoreZeroOnBlurHine(e, 0, "leftAsym")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHineIncrement(0, "leftAsym")} aria-label="Increase Left Asymmetries">+</button>
            </div>
          </div>
        </div>
        <div className="hine-sub">
          <p className="hine-subtext">Please provide earlier HINE scores if any</p>
          <div className="hine-actions">
            <button type="button" className="add-btn" onClick={addHineRow} aria-label="Add HINE row">+</button>
            {hineData.length > 1 && <button type="button" className="remove-btn" onClick={() => removeHineRow(1)} aria-label="Remove last HINE row">-</button>}
          </div>
        </div>
        {hineData.slice(1).map((item, index) => {
          const rowId = index + 1;
          return (
            <div key={item.id || index} className="hine-row-single">
              <div className="hine-field time-field">
                <label htmlFor={`hine-timepoint-${rowId}`}>Timepoint</label>
                <input
                  type="text"
                  id={`hine-timepoint-${rowId}`}
                  placeholder="Enter age in months"
                  value={item.timepoint || ""}
                  className="hine-input"
                  onChange={e => handleHineChange(rowId, "timepoint", e.target.value)}
                />
              </div>
              <div className="hine-field">
                <label htmlFor={`hine-score-${rowId}`}>HINE Score</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHineDecrement(rowId, "hineScore")} aria-label="Decrease HINE Score">-</button>
                  <input
                    type="text"
                    id={`hine-score-${rowId}`}
                    value={item.hineScore ?? ""}
                    onChange={e => handleHineChange(rowId, "hineScore", e.target.value)}
                    onFocus={e => clearZeroOnFocusHine(e, rowId, "hineScore")}
                    onBlur={e => restoreZeroOnBlurHine(e, rowId, "hineScore")}
                    inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHineIncrement(rowId, "hineScore")} aria-label="Increase HINE Score">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hine-right-asym-${rowId}`}>Right Asymmetries</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHineDecrement(rowId, "rightAsym")} aria-label="Decrease Right Asymmetries">-</button>
                  <input
                    type="text"
                    id={`hine-right-asym-${rowId}`}
                    value={item.rightAsym ?? ""}
                    onChange={e => handleHineChange(rowId, "rightAsym", e.target.value)}
                    onFocus={e => clearZeroOnFocusHine(e, rowId, "rightAsym")}
                    onBlur={e => restoreZeroOnBlurHine(e, rowId, "rightAsym")}
                    inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHineIncrement(rowId, "rightAsym")} aria-label="Increase Right Asymmetries">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hine-left-asym-${rowId}`}>Left Asymmetries</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHineDecrement(rowId, "leftAsym")} aria-label="Decrease Left Asymmetries">-</button>
                  <input
                    type="text"
                    id={`hine-left-asym-${rowId}`}
                    value={item.leftAsym ?? ""}
                    onChange={e => handleHineChange(rowId, "leftAsym", e.target.value)}
                    onFocus={e => clearZeroOnFocusHine(e, rowId, "leftAsym")}
                    onBlur={e => restoreZeroOnBlurHine(e, rowId, "leftAsym")}
                    inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHineIncrement(rowId, "leftAsym")} aria-label="Increase Left Asymmetries">+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // -- HAI UI ----------------------------------------------------------------
  const renderHAISection = (selectedVal) => {
    if (selectedVal !== "Yes") return null;
    return (
      <div className="hai-section1">
        <div className="hine-row-single">
          <div className="hine-field time-field">
            <label htmlFor="hai-timepoint-0">Timepoint</label>
            <input
              type="text"
              id="hai-timepoint-0"
              placeholder="Enter age in months"
              value={haiData[0]?.timepoint || ""}
              className="hine-input"
              onChange={e => handleHaiChange(0, "timepoint", e.target.value)}
            />
          </div>
          <div className="hine-field">
            <label htmlFor="hai-right-eahs-0">Each hand score (Right)</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "rightEahs")} aria-label="Decrease Each hand score Right">-</button>
              <input
                type="text"
                id="hai-right-eahs-0"
                value={haiData[0]?.rightEahs ?? ""}
                onChange={e => handleHaiChange(0, "rightEahs", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "rightEahs")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "rightEahs")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "rightEahs")} aria-label="Increase Each hand score Right">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hai-left-eahs-0">Each hand score (Left)</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "leftEahs")} aria-label="Decrease Each hand score Left">-</button>
              <input
                type="text"
                id="hai-left-eahs-0"
                value={haiData[0]?.leftEahs ?? ""}
                onChange={e => handleHaiChange(0, "leftEahs", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "leftEahs")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "leftEahs")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "leftEahs")} aria-label="Increase Each hand score Left">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hai-sum-score-0">Both Hands Sum Score (BoHS)</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "eachHandSumScore")} aria-label="Decrease Both Hands Sum Score (BoHS)">-</button>
              <input
                type="text"
                id="hai-sum-score-0"
                value={haiData[0]?.eachHandSumScore ?? ""}
                onChange={e => handleHaiChange(0, "eachHandSumScore", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "eachHandSumScore")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "eachHandSumScore")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "eachHandSumScore")} aria-label="Increase Both Hands Sum Score (BoHS)">+</button>
            </div>
          </div>
          <div className="hine-field">
            <label htmlFor="hai-asymmetry-0">Asymmetry (%) difference between hands</label>
            <div className="hine-counter">
              <button type="button" onClick={() => handleHaiDecrement(0, "asymmetryIndex")} aria-label="Decrease Asymmetry percentage">-</button>
              <input
                type="text"
                id="hai-asymmetry-0"
                value={haiData[0]?.asymmetryIndex ?? ""}
                onChange={e => handleHaiChange(0, "asymmetryIndex", e.target.value)}
                onFocus={e => clearZeroOnFocusHai(e, 0, "asymmetryIndex")}
                onBlur={e => restoreZeroOnBlurHai(e, 0, "asymmetryIndex")}
                inputMode="decimal"
              />
              <button type="button" onClick={() => handleHaiIncrement(0, "asymmetryIndex")} aria-label="Increase Asymmetry percentage">+</button>
            </div>
          </div>
        </div>
        <div className="hine-sub">
          <p className="hine-subtext">Please provide earlier HAI scores if any</p>
          <div className="hine-actions">
            <button type="button" className="add-btn" onClick={addHaiRow} aria-label="Add HAI row">+</button>
            {haiData.length > 1 && <button type="button" className="remove-btn" onClick={() => removeHaiRow(1)} aria-label="Remove last HAI row">-</button>}
          </div>
        </div>
        {haiData.slice(1).map((item, index) => {
          const rowId = index + 1;
          return (
            <div key={item.id || index} className="hine-row-single">
              <div className="hine-field time-field">
                <label htmlFor={`hai-timepoint-${rowId}`}>Timepoint</label>
                <input
                  type="text"
                  id={`hai-timepoint-${rowId}`}
                  placeholder="Enter age in months"
                  value={item.timepoint || ""}
                  className="hine-input"
                  onChange={e => handleHaiChange(rowId, "timepoint", e.target.value)}
                />
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-right-eahs-${rowId}`}>Each hand score (Right)</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "rightEahs")} aria-label="Decrease Each hand score Right">-</button>
                  <input
                    type="text"
                    id={`hai-right-eahs-${rowId}`}
                                        value={item.rightEahs ?? ""}
                                        onChange={e => handleHaiChange(rowId, "rightEahs", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "rightEahs")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "rightEahs")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "rightEahs")} aria-label="Increase Each hand score Right">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-left-eahs-${rowId}`}>Each hand score (Left)</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "leftEahs")} aria-label="Decrease Each hand score Left">-</button>
                  <input
                    type="text"
                    id={`hai-left-eahs-${rowId}`}
                                        value={item.leftEahs ?? ""}
                                        onChange={e => handleHaiChange(rowId, "leftEahs", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "leftEahs")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "leftEahs")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "leftEahs")} aria-label="Increase Each hand score Left">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-sum-score-${rowId}`}>Both Hands Sum Score (BoHS)</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "eachHandSumScore")} aria-label="Decrease Both Hands Sum Score (BoHS)">-</button>
                  <input
                    type="text"
                    id={`hai-sum-score-${rowId}`}
                                        value={item.eachHandSumScore ?? ""}
                                        onChange={e => handleHaiChange(rowId, "eachHandSumScore", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "eachHandSumScore")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "eachHandSumScore")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "eachHandSumScore")} aria-label="Increase Both Hands Sum Score (BoHS)">+</button>
                </div>
              </div>
              <div className="hine-field">
                <label htmlFor={`hai-asymmetry-${rowId}`}>Asymmetry (%) difference between hands</label>
                <div className="hine-counter">
                  <button type="button" onClick={() => handleHaiDecrement(rowId, "asymmetryIndex")} aria-label="Decrease Asymmetry percentage">-</button>
                  <input
                    type="text"
                    id={`hai-asymmetry-${rowId}`}
                                        value={item.asymmetryIndex ?? ""}
                                        onChange={e => handleHaiChange(rowId, "asymmetryIndex", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "asymmetryIndex")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "asymmetryIndex")}
                                        inputMode="decimal"
                  />
                  <button type="button" onClick={() => handleHaiIncrement(rowId, "asymmetryIndex")} aria-label="Increase Asymmetry percentage">+</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // PREVIOUS DATA - SHARED TABLE BUILDER
  // ---------------------------------------------------------------------------
  // Turns the raw sessions returned by get_historical_pathway_data.php into one
  // table per group. Kept separate from the markup so the on-screen panel and
  // the off-screen print copy below can never drift apart.
  //
  // Row order: the historical endpoint returns its questions ordered lexically
  // by node_number, so "1.10" can arrive before "1.2" and the table rows look
  // shuffled. The canonical order is the one get_pathway_details.php returns -
  // the same order the therapist answers those questions in the assessment - so
  // an order map is built from the pathway definitions for the open group.
  const historyKeywordOrder = useMemo(() => {
    const order = new Map();
    const groupKey = String(historyGroupName || "").trim().toLowerCase();
    if (!groupKey || groupKey === "demographics") return order;

    let seq = 0;
    const register = (rawKeyword, node) => {
      const keywordKey = normaliseKeyword(rawKeyword);
      if (keywordKey && !order.has("k:" + keywordKey)) {
        order.set("k:" + keywordKey, seq++);
      }
      const nodeKey = String(node || "").trim();
      if (nodeKey && !order.has("n:" + nodeKey)) order.set("n:" + nodeKey, seq++);
    };

    const walkQuestions = (questions) => {
      (Array.isArray(questions) ? questions : []).forEach((q) => {
        if (!q) return;
        register(q.keywords || q.keyword || q.question_text || q.question, q.node_number);
        const options = Array.isArray(q.options) ? q.options : [];
        options.forEach((opt) => {
          const walkSubOptions = (subs) => {
            (Array.isArray(subs) ? subs : []).forEach((sub) => {
              if (!sub) return;
              walkSubOptions(sub.sub_options);
              walkQuestions(sub.sub_questions);
            });
          };
          if (!opt) return;
          walkSubOptions(opt.sub_options);
          walkQuestions(opt.sub_questions);
        });
      });
    };

    // Prefer the pathway currently on screen (same age band + appointment),
    // then any pathway in the API response that carries the same group name.
    const groups = [];
    const addGroups = (pathway) => {
      (pathway && Array.isArray(pathway.groups) ? pathway.groups : []).forEach((g) => {
        if (g && String(g.group_name || "").trim().toLowerCase() === groupKey) groups.push(g);
      });
    };
    addGroups(activePathway);
    if (groups.length === 0) {
      (pathwaysData || []).forEach((pData) => {
        (pData && Array.isArray(pData.pathways) ? pData.pathways : []).forEach(addGroups);
      });
    }
    groups.forEach((g) => walkQuestions(g.questions));

    return order;
  }, [activePathway, pathwaysData, historyGroupName]);

  const buildHistoryTables = () => {
    const gh = groupHistory;
    if (!gh || !Array.isArray(gh.data) || gh.data.length === 0) return null;

    const sessions = [...gh.data].sort(
      (a, b) => new Date(a.assessment_date || 0) - new Date(b.assessment_date || 0)
    );
    const shades = ["#A0C7E8", "#BED8EE", "#D3E4F5", "#E7F0FA"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const ordinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    const formatDate = (ds) => {
      if (!ds) return "-";
      const d = new Date(ds);
      if (isNaN(d.getTime())) return String(ds);
      return `${ordinal(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    // Label each column Initial / Review 1 / Review 2 ... in chronological
    // order, then display newest first (latest on the left).
    let reviewCount = 0;
    const labeled = sessions.map((s) => {
      const isReview = !isInitialAppointmentType(s.appointment_type || s.type);
      if (isReview) reviewCount += 1;
      return {
        session: s,
        label: isReview ? `Review ${reviewCount}` : "Initial",
        date: formatDate(s.assessment_date),
      };
    });
    const colMeta = labeled.reverse().map((c, i) => ({
      ...c,
      shade: shades[i % shades.length],
    }));

    const sessionQ = (s, type) =>
      (Array.isArray(s.pathway) ? s.pathway : []).find(
        (x) => String(x.type || "").trim().toUpperCase() === type
      );
    const anyQ = (type) =>
      sessions.some((s) =>
        (Array.isArray(s.pathway) ? s.pathway : []).some(
          (x) => String(x.type || "").trim().toUpperCase() === type
        )
      );
    const joinBracketed = (vals) => {
      const kept = vals.filter((v) => v !== "" && v !== null && v !== undefined);
      return kept.length ? kept.map((v, i) => (i === 0 ? String(v) : `(${v})`)).join(", ") : "-";
    };

    const tables = [];

    if (historyGroupName === "Demographics") {
      tables.push({
        title: "Demographics",
        rows: [
          { label: "Appointment", cell: (s) => formatAppointmentLabel(s.appointment_type) },
          { label: "Client", cell: (s) => s.client_name || gh.client_name || "-" },
          { label: "Age Group", cell: (s) => s.age_group || gh.age_group || "-" },
        ],
      });
    } else {
      // One row per distinct question across every session, so a question
      // answered later still lines up against its earlier answers.
      const seen = new Set();
      const qRows = [];
      sessions.forEach((s) => {
        (Array.isArray(s.pathway) ? s.pathway : []).forEach((q) => {
          const type = String(q.type || "").trim().toUpperCase();
          if (type === "HINE" || type === "HAI") return;
          const rawKeyword = q.keyword || q.question || "";
          if (!rawKeyword) return;
          const kLower = rawKeyword.toLowerCase().trim();
          if (kLower.includes("hine") || kLower.includes("hai")) return;
          const keyword = (kLower === "neuroimaging results" || kLower === "neuroimaging") ? "Neuroimaging" : rawKeyword;
          if (seen.has(keyword)) return;
          seen.add(keyword);
          qRows.push({
            label: keyword,
            node: q.node,
            cell: (ss) => {
              const match = (Array.isArray(ss.pathway) ? ss.pathway : []).find((x) => {
                const xRaw = x.keyword || x.question || "";
                const xLower = xRaw.toLowerCase().trim();
                const xKw = (xLower === "neuroimaging results" || xLower === "neuroimaging") ? "Neuroimaging" : xRaw;
                return xKw === keyword;
              });
              return match ? formatHistoryAnswer(match.answer) : "-";
            },
          });
        });
      });
      // Order the rows the way get_pathway_details.php lists the questions for
      // this group. Rows the current definition no longer carries keep their
      // historical order at the end (sort is stable).
      const orderOf = (row) => {
        const keywordKey = normaliseKeyword(row.label);
        if (keywordKey && historyKeywordOrder.has("k:" + keywordKey)) {
          return historyKeywordOrder.get("k:" + keywordKey);
        }
        const nodeKey = String(row.node || "").trim();
        if (nodeKey && historyKeywordOrder.has("n:" + nodeKey)) {
          return historyKeywordOrder.get("n:" + nodeKey);
        }
        return Number.MAX_SAFE_INTEGER;
      };
      qRows.sort((a, b) => orderOf(a) - orderOf(b));
      if (qRows.length > 0) tables.push({ title: historyGroupName, rows: qRows });
    }

    // HINE / HAI keep their own tables: their answers are multi-entry score
    // sheets, not single values, and they do not belong to one group.
    if (anyQ("HINE")) {
      const entries = (s) => (sessionQ(s, "HINE")?.answer?.entries || []);
      tables.push({
        title: "HINE",
        rows: [
          { label: "Status", cell: (s) => (sessionQ(s, "HINE")?.answer?.value ? String(sessionQ(s, "HINE").answer.value) : "-") },
          { label: "Time Point", cell: (s) => joinBracketed(entries(s).map((e) => e.timepoint)) },
          { label: "HINE Score", cell: (s) => joinBracketed(entries(s).map((e) => e.hine_score)) },
          { label: "Right Asym.", cell: (s) => joinBracketed(entries(s).map((e) => e.right_asymmetry)) },
          { label: "Left Asym.", cell: (s) => joinBracketed(entries(s).map((e) => e.left_asymmetry)) },
        ],
      });
    }

    if (anyQ("HAI")) {
      const entries = (s) => (sessionQ(s, "HAI")?.answer?.entries || []);
      tables.push({
        title: "HAI",
        rows: [
          { label: "Status", cell: (s) => (sessionQ(s, "HAI")?.answer?.value ? String(sessionQ(s, "HAI").answer.value) : "-") },
          { label: "Time Point", cell: (s) => joinBracketed(entries(s).map((e) => e.timepoint)) },
          { label: "Right Score", cell: (s) => joinBracketed(entries(s).map((e) => e.right_score)) },
          { label: "Left Score", cell: (s) => joinBracketed(entries(s).map((e) => e.left_score)) },
          { label: "Sum (EAHS)", cell: (s) => joinBracketed(entries(s).map((e) => e.sum)) },
          { label: "Asymmetry", cell: (s) => joinBracketed(entries(s).map((e) => e.asymmetry)) },
        ],
      });
    }

    return { colMeta, tables };
  };

  const renderHistoryTables = (forPrint) => {
    const built = buildHistoryTables();
    if (!built) return null;
    if (built.tables.length === 0) {
      return (
        <p style={{ color: "var(--cpa-gray-2, #626A6F)", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>
          No previous data found for this group.
        </p>
      );
    }

    const { colMeta, tables } = built;
    const cellBorder = "1px solid #d9e2ec";

    return tables.map((t, ti) => (
      <div
        key={ti}
        style={{
          marginBottom: "24px",
          overflow: forPrint ? "visible" : "auto",
          WebkitOverflowScrolling: "touch",
          borderRadius: "8px",
          border: cellBorder,
          /* A Review session adds ONE more column per review and a column is
             never removed, so the table only ever grows to the right. The
             wrapper therefore scrolls BOTH ways on screen (the printable copy
             stays untouched), and the header + label column are sticky below so
             nothing has to be scrolled back into view. */
          maxHeight: forPrint ? "none" : "66vh",
        }}
      >
        <table
          style={{
            /* SEPARATE rather than collapse: a collapsed border detaches from a
               sticky cell (the frozen label column loses its edge line while
               the rest of the table slides under it), so every cell carries its
               own 1px edge instead. */
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: "13px",
            width: "100%",
            /* MIN WIDTH is what makes the frozen first column actually freeze.
               With width:100% alone the browser just squeezed every session
               column narrower as reviews were added, the table never overflowed
               its wrapper, and `left: sticky` had nothing to stick against - so
               the label column looked unfrozen. Each session column now keeps a
               readable width; past that the wrapper scrolls sideways and the
               label column stays pinned on EVERY row (header included). */
            minWidth: forPrint
              ? "100%"
              : `${220 + colMeta.length * 170}px`,
            tableLayout: "fixed",
            background: "#fff",
          }}
        >
          <colgroup>
            {/* Label column kept deliberately narrow: it is frozen on screen, so
                its width is what the session columns have to share out. */}
            <col style={{ width: "20%" }} />
            {colMeta.map((c, ci) => (
              <col key={ci} style={{ width: `${80 / Math.max(colMeta.length, 1)}%` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                scope="colgroup"
                colSpan={colMeta.length + 1}
                style={{
                  position: forPrint ? "static" : "sticky",
                  top: 0,
                  zIndex: 5,
                  height: "34px",
                  lineHeight: "34px",
                  padding: "0 12px",
                  background: "#0063A5",
                  color: "#fff",
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "14px",
                  borderBottom: cellBorder,
                }}
              >
                {t.title}
              </th>
            </tr>
            <tr>
              <th
                scope="col"
                style={{
                  /* Frozen on BOTH axes: the row label stays put while the
                     session columns scroll sideways, and stays under the group
                     title bar while the table scrolls down. */
                  position: forPrint ? "static" : "sticky",
                  top: forPrint ? "auto" : "34px",
                  left: 0,
                  zIndex: 8,
                  background: "#0063A5",
                  color: "#fff",
                  textAlign: "left",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  borderBottom: cellBorder,
                  borderRight: cellBorder,
                }}
              >
                Assessment date
              </th>
              {colMeta.map((c, ci) => (
                <th key={ci} scope="col" style={{ position: forPrint ? "static" : "sticky", top: forPrint ? "auto" : "34px", zIndex: 6, background: c.shade, color: "#1A1A1A", textAlign: "center", padding: "8px 10px", borderBottom: cellBorder, borderRight: cellBorder }}>
                  <div>{c.date}</div>
                  <div style={{ fontSize: "11px", opacity: 0.85, fontWeight: "normal" }}>[{c.label}]</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {t.rows.map((row, r) => (
              <tr key={r}>
                <td role="rowheader" style={{ position: forPrint ? "static" : "sticky", left: 0, zIndex: 2, background: "#0063A5", color: "#fff", fontWeight: "bold", textAlign: "left", padding: "8px 10px", verticalAlign: "top", borderBottom: cellBorder, borderRight: cellBorder }}>
                  {row.label}
                </td>
                {colMeta.map((c, ci) => (
                  <td key={ci} style={{ background: c.shade, color: "#1A1A1A", verticalAlign: "top", wordBreak: "break-word", padding: "8px 10px", borderBottom: cellBorder, borderRight: cellBorder }}>
                    {row.cell(c.session) || "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ));
  };

  const renderPreviousDataTablesForPrint = () => renderHistoryTables(true);

  // ---------------------------------------------------------------------------
  // RENDER PREVIOUS DATA PANEL
  // ---------------------------------------------------------------------------
  // Opened from the folder icon on the question-card during a Review session.
  // Lists every past completed Initial + Review session for this client, one
  // column per session, so a Review answer can be compared against the full
  // timeline rather than only the last session.
  const renderPreviousDataPanel = () => {
    if (!showHistoryFiles && !historyGroupName) return null;

    return (
      <div ref={historyViewRef} className="group-history-inline" style={{ position: "relative", background: "#fff", borderTop: "1px solid #ececec", padding: "4px 18px 18px", minHeight: "300px" }}>
        {/* -- Top bar: back / title / download PDF / close --
            Rendered for the WHOLE pane, not only for an open group. The pane
            is full width, so while only the group file bar was showing (folder
            opened, nothing picked yet) this bar did not exist and there was no
            Previous Page and no X - the only way out was the folder icon. */}
        {(showHistoryFiles || historyGroupName) && (
          <div style={{ position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", background: "#fff", border: "0px solid #ececec", borderRadius: "0px", padding: "4px 42px 4px 10px", margin: "4px 0 4px", flexWrap: "wrap" }}>
            {/* CONDITION: "Previous Page" only exists once a group is open.
                With just the group bar there is no previous page to go back to
                inside Previous Data - "X" is the way out of the pane. */}
            {historyGroupName && (
              <button className="backbutton"  onClick={handleCloseHistory} style={{ marginTop: 0, marginBottom: 0, padding: "6px 12px", fontSize: "14px" }}>
                <FontAwesomeIcon icon={faArrowLeft} /> Previous Page
              </button>
            )}
            <div style={{ textAlign: "center", flex: "1", minWidth: "0", margin: "0 4px" }}>
              <h3 style={{ margin: 0, fontSize: "clamp(14px, 3.2vw, 18px)", fontWeight: "bold", color: "#1F7A26" }}>
                {historyGroupName ? "Client History" : "Previous Data"}
              </h3>
              {groupHistory?.client_name && (
                <p style={{ margin: "1px 0 0", fontSize: "clamp(12px, 2.6vw, 13px)", color: "#333", wordBreak: "break-word" }}>
                   <span style={{ fontWeight: "bold" }}>{groupHistory.client_name}</span>
                </p>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              {/* CONDITION: the PDF is built from the open group's tables, so it
                  is only offered once a group with data is actually on screen. */}
              {historyGroupName && (
                <button
                  className="group-history-download-btn"
                  onClick={handleDownloadGroupData}
                  disabled={isDownloadingGroupData || !groupHistory || !groupHistory.data?.length}
                  title="Download as PDF"
                  aria-label="Download as PDF"
                  style={{ background: "#fff", border: "1.5px solid #1F7A26", color: "#1F7A26", fontFamily: "inherit", fontSize: "13px", fontWeight: "bold", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", flexShrink: 0 }}
                >
                  <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                  {isDownloadingGroupData ? "Generating..." : "Download  PDF"}
                </button>
              )}
              {/* CONDITION: "X" closes the whole Previous Data view, so it is
                  always available while the pane is open. Pinned to the very
                  top-right CORNER of the bar: the bar is position: sticky, so
                  it is also the positioning anchor - the X travels with the
                  sticky bar and stays reachable at any scroll depth. The bar
                  reserves room on the right (padding-right: 42px), so the corner
                  X can never land on top of the Download PDF button. */}
              <button
                onClick={handleCloseHistory}
                aria-label="Close history view"
                title="Close Previous Data"
                style={{ position: "absolute", top: "2px", right: "0px", zIndex: 25, flexShrink: 0, padding: "0px", lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: "18px", borderRadius: "50%" }}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* -- Group names (files) bar -- */}
        {showHistoryFiles && (
          <div className="previous-data-wrapper" style={{ marginTop: 0 }}>
            <div className="previous-steps">
              {orderedHistoryGroups.map((group, gIdx) => {
                const isHistoryOpen = historyGroupName === group.group_name;
                const stepColors = ["active-purple", "active-gray", "active-orange", "active-red", "active-blue"];
                return (
                  <div
                    key={group.node_number || gIdx}
                    className={`step-item ${stepColors[gIdx % stepColors.length] || "active-purple"}`}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isHistoryOpen}
                    style={{ cursor: "pointer", borderRadius: "8px", border: isHistoryOpen ? "1px solid var(--cpa-primary, #247A3D)" : "1px solid transparent", background: isHistoryOpen ? "#f0f0f0" : "transparent", transition: "all 0.2s ease" }}
                    onClick={() => handleGroupHistoryClick(group.group_name)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleGroupHistoryClick(group.group_name);
                      }
                    }}
                    title={`View previous data for ${group.group_name}`}
                  >
                    <FaFileAlt className="step-icon" aria-hidden="true" />
                    <span style={{ color: isHistoryOpen ? "#1F7A26" : "#111" }}>{group.group_name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* -- Inline group history tables -- */}
        {historyGroupName && (
          <div style={{ paddingTop: "14px" }}>
            {loadingGroupHistory && (
              <p style={{ color: "var(--cpa-gray-2, #626A6F)", fontSize: "14px", textAlign: "center", padding: "40px 0" }} role="status" aria-live="polite">Loading previous answers...</p>
            )}

            {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length === 0 && historyGroupName === "Demographics" && (
              <div style={{ padding: "12px 16px" }}>
                <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Appointment</span>
                  <span> - {formatAppointmentLabel(groupHistory.appointment_type)}</span>
                </p>
                <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Client</span>
                  <span> - {groupHistory.client_name || "-"}</span>
                </p>
                <p style={{ margin: "0 0 0px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                  <span style={{ fontWeight: 700 }}>Age group</span>
                  <span> - {groupHistory.age_group || "-"}</span>
                </p>
              </div>
            )}

            {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length === 0 && historyGroupName !== "Demographics" && (
              <p style={{ color: "var(--cpa-gray-2, #626A6F)", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>No previous data found for this group.</p>
            )}

            {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length > 0 && renderHistoryTables(false)}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // PREVIOUS DATA: full-width state
  // ---------------------------------------------------------------------------
  // Opening the Previous Data folder swaps the WHOLE assessment area - the left
  // panel AND the right content - for the history view. The session-by-session
  // tables are far wider than a single question, and rendering them inside the
  // 75% right-hand column squeezed every table into a sideways scroll.
  const isHistoryPaneOpen = isReviewMode && (showHistoryFiles || historyGroupName);

  // Off-screen printable copy of the open group's history tables. html2canvas can
  // only capture what is laid out, so the node is rendered (but visually hidden)
  // rather than reusing the interactive table above.
  //
  // Rendered OUTSIDE both layout branches on purpose: the interactive panel is
  // replaced by the full-width pane, so leaving this copy inside `.right-content`
  // would unmount it and break "Download as PDF".
  const renderHistoryPrintCopy = () =>
    isReviewMode && historyGroupName && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length > 0 ? (
      <div
        ref={groupHistoryPrintRef}
        aria-hidden="true"
        style={{ position: "absolute", left: "-10000px", top: "0", width: "1100px", background: "#fff", padding: "24px" }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: "20px", color: "#1F7A26" }}>{historyGroupName}</h2>
        <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#333" }}>
          <strong>{groupHistory?.client_name || client || "-"}</strong>
          {groupHistory?.client_code ? ` - ${groupHistory.client_code}` : ""}
        </p>
        {renderPreviousDataTablesForPrint()}
      </div>
    ) : null;

  // ---------------------------------------------------------------------------
  // RENDER CURRENT DYNAMIC QUESTION
  // ---------------------------------------------------------------------------

  const renderCurrentQuestion = () => {
    if (step < 2 || isGoalDetailStep) return null;
    if (step > 1 + visibleSteps.length) return null;

    const currentQ = visibleSteps[step - 2];
    if (!currentQ) return null;

    const prevQ = visibleSteps[step - 3];
    const isNewGroup = !prevQ || prevQ.groupNode !== currentQ.groupNode;
    const demographicsInfoText = "Understanding the infant's family context, supports, environment, and available resources is essential when selecting interventions, as these factors directly influence what is realistic, feasible, and sustainable. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life.";
    let groupInfoText = null;
    if (isNewGroup) {
      groupInfoText = currentQ.groupName === "Demographics" ? demographicsInfoText : currentQ.groupInfo;
    }

    const continuousNumber = getContinuousNumber(currentQ);
    const instructions = parseInstructions(currentQ.instruction);
    const { isHINE, isHAI } = getQuestionType(currentQ);
    const isGoalDomain = isGoalDomainQuestion(currentQ);
    const selectedVal = answers[currentQ.node_number];

    let selectedOptionsList = [];
    if (selectedVal && currentQ.options) {
      if (Array.isArray(selectedVal)) {
        selectedOptionsList = currentQ.options.filter(o => selectedVal.includes(o.option_text));
      } else {
        const opt = currentQ.options.find(o => o.option_text === selectedVal);
        if (opt) selectedOptionsList = [opt];
      }
    }

    const hasOptions = currentQ.options && currentQ.options.length > 0;

    const goalDomainSelectedCount = isGoalDomain
      ? (Array.isArray(selectedVal) ? selectedVal.length : 0)
      : 0;

    return (
      <>
        {/* Group heading. The appointment type is part of the heading so a
            Review is never mistaken for the Initial that produced the same
            group of questions. */}
       
        {groupInfoText && (
          <div className="question-cardtop" style={{ borderLeft: "5px solid #288537", marginBottom: "0px" }}>
            <RichText inline html={groupInfoText} style={{ fontWeight: "bold", fontSize: "16px", color: "#288537", margin: 0 }} as="p" />
          </div>
        )}
        <div className="question-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
            <p className="question">{continuousNumber}. <RichText inline html={currentQ.question_text} /></p>
            {/* PREVIOUS DATA: folder icon, top-right of the question-card.
                Review sessions only - an Initial is the first session, so it
                has nothing prior to compare against. */}
            {isReviewMode && (
              <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                <span className="previous-data-label">Previous Data</span>
                <button
                  className="group-history-file-btn"
                  onClick={handleHistoryFileClick}
                  title="View all previous Initial and Review data for this client"
                  aria-label="View all previous Initial and Review data for this client"
                  aria-expanded={showHistoryFiles}
                >
                  <FaFolder aria-hidden="true" />
                </button>
              </div>
            )}
          </div>

          {instructions.isMulti && !isGoalDomain && (
            <p style={{ fontSize: "16px", marginBottom: "15px", fontWeight: 500, paddingLeft: "3px" }}>
              Please select more than one option
            </p>
          )}

          {hasOptions && (
            <div className={`radio-group ${instructions.isVertical ? "vertical" : ""}`}>
              {currentQ.options.map(opt => {
                if (isGoalDomain) {
                  // Domains ONLY. The skills under a domain are deliberately NOT
                  // rendered here: handleNext (~line 2697) notices that a selected
                  // domain has sub_options and routes to renderGoalDetailStep - a
                  // dedicated NEXT page ("Select the specific option from the list
                  // that supports the caregiver-led goal") which lists each selected
                  // domain's skills as radio buttons and stores one
                  // `option_selected` row per skill at the skill's own node. Drawing
                  // them inline here too duplicated the skills on a single page.
                  const isSelected = Array.isArray(selectedVal) && selectedVal.includes(opt.option_text);
                  const isDisabled = !isSelected && goalDomainSelectedCount >= 2;
                  return (
                    <label key={opt.option_id} style={{ opacity: isDisabled ? 0.5 : 1 }}>
                      <input type="checkbox" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => {
                          const allowed = handleMainOptionSelect(currentQ.node_number, opt, true, true);
                          if (!allowed) return;
                          let curr = Array.isArray(answers[currentQ.node_number]) ? [...answers[currentQ.node_number]] : [];
                          let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                          let allIds = currentQ.options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                          saveResponseAPI(currentSessionId, currentQ.node_number, "option", currentQ.question_id, null, allIds.length === 0 ? [] : allIds);
                        }} /> <RichText inline html={opt.option_text} />
                    </label>
                  );
                }

                if (instructions.isMulti) {
                  const multiVals = Array.isArray(selectedVal)
                    ? selectedVal
                    : (selectedVal !== undefined && selectedVal !== null && selectedVal !== ""
                      ? [selectedVal]
                      : []);
                  const isSelected = multiVals.includes(opt.option_text);
                  return (
                    <label key={opt.option_id}>
                      <input type="checkbox" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                        onChange={() => {
                          handleMainOptionSelect(currentQ.node_number, opt, true);
                          let curr = Array.isArray(answers[currentQ.node_number])
                            ? [...answers[currentQ.node_number]]
                            : (answers[currentQ.node_number] ? [answers[currentQ.node_number]] : []);
                          let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                          let allIds = currentQ.options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                          saveResponseAPI(currentSessionId, currentQ.node_number, "option", currentQ.question_id, null, allIds.length === 0 ? [] : allIds);
                        }} /> <RichText inline html={opt.option_text} />
                    </label>
                  );
                }

                if (isHINE || isHAI) {
                  const type = isHINE ? "hine" : "hai";
                  const isSelected = selectedVal === opt.option_text;
                  return (
                    <label key={opt.option_id}>
                      <input type="radio" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                        onChange={() => {
                          handleMainOptionSelect(currentQ.node_number, opt, false);
                          if (!isSelected) {
                            saveResponseAPI(currentSessionId, currentQ.node_number, "option", opt.option_id, opt.option_text);
                            saveHineHaiAPI(
                              currentSessionId, currentQ.node_number, type, currentQ.question_id, opt.option_text,
                              opt.option_text === "Yes"
                                ? (isHINE
                                  ? hineData.map(r => ({ timepoint: r.timepoint, hine_score: r.hineScore, right_asymmetry: r.rightAsym, left_asymmetry: r.leftAsym }))
                                  : haiData.map(r => ({ timepoint: r.timepoint, right_score: r.rightEahs, left_score: r.leftEahs, sum: r.eachHandSumScore, asymmetry: r.asymmetryIndex })))
                                : []
                            );
                          } else {
                            saveResponseAPI(currentSessionId, currentQ.node_number, "question", opt.option_id, "");
                          }
                        }} /> <RichText inline html={opt.option_text} />
                    </label>
                  );
                }

                const isSelected = selectedVal === opt.option_text;
                return (
                  <label key={opt.option_id}>
                    <input type="radio" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                      onChange={() => {
                        handleMainOptionSelect(currentQ.node_number, opt, false);
                        if (!isSelected) {
                          saveResponseAPI(currentSessionId, currentQ.node_number, "option", opt.option_id, opt.option_text);
                        } else {
                          saveResponseAPI(currentSessionId, currentQ.node_number, "question", opt.option_id, "");
                        }
                      }} /> <RichText inline html={opt.option_text} />
                  </label>
                );
              })}
            </div>
          )}

          {!instructions.isMulti && !isGoalDomain && selectedOptionsList.length > 0 && (
            <div style={{ marginTop: "15px" }}>
              {selectedOptionsList.map(selectedOpt => {
                if (!selectedOpt.sub_options || selectedOpt.sub_options.length === 0) return null;
                return (
                  <div key={selectedOpt.option_id}>
                    {renderSubOptionsChain(currentQ.node_number, [selectedOpt.option_text], selectedOpt.sub_options, selectedOpt.instruction || "")}
                  </div>
                );
              })}
            </div>
          )}

          {isHINE && renderHINESection(selectedVal)}
          {isHAI && renderHAISection(selectedVal)}

          {selectedOptionsList.map(opt => renderAttachments(opt.attachments))}
          {renderAttachments(currentQ.attachments)}
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------------------
  // LAYOUT SUB-RENDER HELPERS
  // ---------------------------------------------------------------------------
  // These are kept as functions inside the component (rather than top-level
  // components) so they can read every state value and handler with no prop
  // drilling. They simply break the giant JSX below into small, named pieces.

  // -- Small primitive shared by every summary row in the left panel --------
  const renderSummaryItem = ({ label, value, num, onClick, ariaLabel }) => (
    <li
      style={{
        color: "#2a2b2a",
        position: "relative",
        zIndex: 1,
        cursor: "pointer",
      }}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      role="button"
      tabIndex="0"
      aria-label={ariaLabel || `${label}: ${value || 'not set'}`}
    >
      <span
        className="step-number"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          background: "#fff",
          borderRadius: "50%",
          fontSize: "14px",
          margin: "5px",
        }}
        aria-hidden="true"
      >
        {num}
      </span>
      <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
        <span className="keyword-text">{label}</span>
        <span
          style={{
            color: "var(--cpa-gray-2, #626A6F)",
            backgroundColor: "white",
            border: "none",
            borderRadius: "0%",
            fontWeight: "bold",
            marginLeft: "4px",
            marginRight: "4px",
          }}
          aria-hidden="true"
        >
          -
        </span>
        <p style={{ fontWeight: "normal" }}><RichText html={value || ""} /></p>
      </div>
    </li>
  );

  // -- Left-panel "Demographics" block (used on review-goal + question steps) --
  const renderDemographicsPanel = ({ refCircle, connector }) => (
    <div className="panel-block expanded">
      <div
        className={`circle ${step > 1 ? "done" : ""}`}
        ref={refCircle}
        aria-hidden="true"
      >
        {step > 1 ? <FontAwesomeIcon icon={faCheck} /> : "I"}
      </div>
      <h3
        className="section-title"
        onClick={() => toggleSection("demographics")}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection("demographics"); } }}
        role="button"
        aria-expanded={openSections.demographics}
        tabIndex="0"
      >
        Demographics{" "}
        <span className="arrow" aria-hidden="true">
          {openSections.demographics ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp} />}
        </span>
      </h3>
      {openSections.demographics && (
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: "16px",
              top: "19px",
              bottom: "10px",
              width: "3px",
              background: "var(--cpa-primary, #247A3D)",
              zIndex: 0,
            }}
            aria-hidden="true"
          />
          <ul className="sub-list1" aria-label="Demographics summary">
          {[
            { label: "Appointment", value: session, num: 1 },
            { label: "Client", value: client, num: 2 },
            { label: "Age", value: ageRange, num: 3 },
          ].map((item) => renderSummaryItem({ ...item, onClick: () => handleKeywordClick(1) }))}
          </ul>
        </div>
      )}
      {connector && step > 1 && <div className="connector11 done" aria-hidden="true" />}
    </div>
  );

  // -- Left-panel group (II, III, ...) blocks with their summary questions --
  const renderPathwayGroupPanels = () => (
    activePathway?.groups?.map((group, gIdx) => {
      if (group.group_name.toLowerCase() === "demographics") return null;
      const groupQuestions = visibleSteps.filter(q => q.groupNode === group.node_number);
      const firstQIdx = visibleSteps.findIndex(q => q.groupNode === group.node_number);
      const lastQIdx = visibleSteps.findLastIndex(q => q.groupNode === group.node_number);
      const isGroupActive = firstQIdx !== -1 && (step - 2 >= firstQIdx && step - 2 <= lastQIdx);
      const hasGroupAnswers = groupQuestions.some(q => answers[q.node_number]);
      const isGroupDone = lastQIdx !== -1 && (step - 2 > lastQIdx);
      if (!isGroupActive && !hasGroupAnswers) return null;
      const isLastGroup = gIdx === activePathway.groups.length - 1;
      const totalSteps = 1 + visibleSteps.length;
      const isSummaryPageActive = step === totalSteps + 1;
      const romanNumerals = ["II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
      return (
        <div key={group.node_number} className="panel-block expanded">
          <div
            className={`circle ${isGroupDone || isSummaryPageActive ? "done" : ""}`}
            ref={(isGroupDone || isSummaryPageActive) ? activeStepRef : null}
            aria-hidden="true"
          >
            {(isGroupDone || isSummaryPageActive) ? <FontAwesomeIcon icon={faCheck} /> : (romanNumerals[gIdx] || <FontAwesomeIcon icon={faDotCircle} />)}
          </div>
          <h3
            className="section-title"
            onClick={() => toggleSection(group.group_name)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(group.group_name); } }}
            role="button"
            aria-expanded={!!openSections[group.group_name]}
            tabIndex="0"
          >
            {group.group_name}{" "}
            <span className="arrow" aria-hidden="true">
              {openSections[group.group_name] ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp} />}
            </span>
          </h3>
          {openSections[group.group_name] && (
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  left: "16px",
                  top: "29px",
                  bottom: "5px",
                  width: "2.5px",
                  background: "var(--cpa-primary, #247A3D)",
                  zIndex: 0,
                  margin: "5px",
                }}
                aria-hidden="true"
              />
              <ul className="sub-list1" aria-label={`${group.group_name} questions`}>
              {groupQuestions.map(q => {
                const dynIdx = visibleSteps.findIndex(dq => dq.node_number === q.node_number);
                if (dynIdx === -1) return null;
                const qStep = 2 + dynIdx;
                const isQActive = step === qStep;
                const continuousNumber = getContinuousNumber(q);
                const mainAns = answers[q.node_number];
                let displayText = getAnswerDisplayText(q);
                const isAnswered = mainAns !== undefined;
                const isNavigable = isQActive || isAnswered;
                return (
                  <li
                    key={q.node_number}
                    ref={isQActive ? activeStepRef : null}
                    style={{
                      color: isQActive ? "#323C43" : (isAnswered ? "#080808" : "var(--cpa-gray-2, #626A6F)"),
                      cursor: isNavigable ? "pointer" : "not-allowed",
                      fontWeight: "bold",
                      position: "relative",
                      zIndex: 1,
                    }}
                    onClick={isNavigable ? () => handleKeywordClick(qStep) : null}
                    onKeyDown={isNavigable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleKeywordClick(qStep); } } : null}
                    role={isNavigable ? "button" : undefined}
                    tabIndex={isNavigable ? "0" : undefined}
                    aria-label={`${q.keywords || 'Question'}: ${displayText || 'not answered'}`}
                  >
                    <span
                      className="step-number"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                        background: "#fbfcfc",
                        borderRadius: "50%",
                        fontSize: "14px",
                        margin: "9px 3px 4px 10px",
                      }}
                      aria-hidden="true"
                    >
                      {continuousNumber}
                    </span>
                    <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                      <span className="keyword-text">{getPanelKeywordLabel(q)}</span>
                      <span
                        style={{ color: "var(--cpa-gray-2, #626A6F)", backgroundColor: "#fbfcfc", border: "none", fontWeight: "bold", marginLeft: "4px", marginRight: "4px" }}
                        aria-hidden="true"
                      >
                        -
                      </span>
                      <p style={{ fontWeight: "normal" }}><RichText html={displayText || ""} /></p>
                    </div>
                  </li>
                );
              })}
              {isLastGroup && !pathwayHasEndInstruction && (
                <li
                  style={{ color: isSummaryPageActive ? "#323C43" : "var(--cpa-gray-2, #626A6F)", cursor: "pointer", fontWeight: "bold", position: "relative", zIndex: 1, marginTop: "10px" }}
                  onClick={() => setStep(totalSteps + 1)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStep(totalSteps + 1); } }}
                  role="button"
                  tabIndex="0"
                  aria-label="Go to Suggested Interventions"
                >
                  <span
                    className="step-number"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      background: "#fff",
                      borderRadius: "50%",
                      fontSize: "14px",
                      margin: "9px 3px 4px 10px",
                    }}
                    aria-hidden="true"
                  >
                    {visibleSteps.length + 4}
                  </span>
                  <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0, paddingRight: '15px' }}>
                    <span className="keyword-text">Review your answers</span>
                    <span style={{ color: "var(--cpa-gray-2, #626A6F)", backgroundColor: "white", border: "none", fontWeight: "bold", marginLeft: "4px", marginRight: "4px" }} aria-hidden="true"></span>
                    <p style={{ fontWeight: "normal" }}>{isSummaryPageActive ? "    " : ""}</p>
                  </div>
                </li>
              )}
            </ul>
            </div>
          )}
          {(isGroupDone || isSummaryPageActive) && <div className="connector11 done" aria-hidden="true" />}
        </div>
      );
    })
  );

  // -- Left-panel "Infant & caregiver context" block (step 1) ---------------
  const renderInfantContextPanel = () => (
    <div className="final-section">
      <div className={`infant-panel ${showInfantInfo ? "expanded" : ""}`}>
        <button
          type="button"
          className="infant-header"
          onClick={() => {
            const next = !showInfantInfo;
            setShowInfantInfo(next);
            setInfantAnnouncement(
              next
                ? "Infant and caregiver context expanded. Understanding the infant, family and their context, including available supports and resources, is essential to selecting realistic, feasible and sustainable interventions. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life."
                : "Infant and caregiver context collapsed."
            );
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              const next = !showInfantInfo;
              setShowInfantInfo(next);
              setInfantAnnouncement(
                next
                  ? "Infant and caregiver context expanded. Understanding the infant, family and their context, including available supports and resources, is essential to selecting realistic, feasible and sustainable interventions. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life."
                  : "Infant and caregiver context collapsed."
              );
            }
          }}
          aria-expanded={showInfantInfo}
          aria-controls="infant-context-content"
          aria-label={`Infant and caregiver context, ${showInfantInfo ? "expanded" : "collapsed"}. Press Enter or Space to toggle details.`}
        >
          <span className="infant-info-btn" aria-hidden="true">
            <FaInfo size={14} aria-hidden="true" />
          </span>
          <div className="infant-heading">
            <span className="heading-line" aria-hidden="true" />
            <span className="heading-text">
              Infant and
              <br />
              caregiver
              <br />
              context
            </span>
          </div>
        </button>
        {showInfantInfo && (
          <div
            id="infant-context-content"
            className="infant-content"
            role="region"
            aria-label="Infant and caregiver context details"
            tabIndex={0}
          >
            <p>
              Understanding the infant, family and their context, including available
              supports and resources, is essential to selecting realistic, feasible
              and sustainable interventions. Effective intervention planning must
              integrate best-available evidence with family-centred practice and
              shared decision-making to ensure the chosen approach fits the infant's
              goals and everyday life.
            </p>
          </div>
        )}
        <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          {infantAnnouncement}
        </span>
      </div>
    </div>
  );

  // -- Left panel (all three states: review-goal, step 1, question steps) ----
  const renderLeftPanel = () => {
    if (showReviewGoalScreen) {
      return <div className="panel-block expanded">{renderDemographicsPanel({ refCircle: null, connector: false })}</div>;
    }
    if (step === 1) {
      return <>{renderInfantContextPanel()}</>;
    }
    return (
      <>
        {renderDemographicsPanel({ refCircle: step > 1 ? activeStepRef : null, connector: true })}
        {renderPathwayGroupPanels()}
      </>
    );
  };

  // -- Step 1: appointment radio group (Initial / Review / Review Smart Goal)
  // The three options are mutually exclusive: picking one immediately
  // disables the other two, so a session can never be started under two
  // appointment types at once. When the page was opened from a specific
  // entry point (?appointment=...), ONLY that option is rendered - same
  // condition applies to all three entry points (Initial, Review, Review
  // Smart Goal) - so e.g. "Start Initial Client Assessment" never shows the
  // Review options. A direct /start-assessment visit (no param) keeps all
  // three. -------------------------------------------------------------------
  const offeredAppointments = entryAppointment
    ? [entryAppointment]
    : APPOINTMENT_OPTIONS;

  // `optionLabelSuffix` optionally appends extra text to a specific option's
  // label (keyed by appointment value) - rendered bold + italic while the
  // appointment name stays plain. No caller uses it right now; it is kept
  // because the two existing-client entry points still need the "name + extra
  // data" shape for any future appointment that only has a short value to show.
  //
  // `optionLabelContent` replaces an option's plain appointment name with a
  // custom React node (keyed by appointment value). The two existing-client
  // entry points use it so the radio reads as the therapist's decision rather
  // than as an internal appointment name:
  //
  //   Review            "You are setting an additional caregiver-led SMART
  //                      goal for this infant..."
  //   Review Smart Goal "This infant's current caregiver-led SMART goal is:
  //                      <goal>  /  Target date: 10 Oct 2026"
  //
  // The radio's VALUE is never touched - it stays the appointment string the
  // backend already expects ("Review" / "Review Smart Goal"), so only what is
  // DISPLAYED changes.
  const renderAppointmentOptions = (heading, optionLabelSuffix, optionLabelContent) => (
    <div className="question-card">
      <p className="question">{heading}</p>
      <div className={`radio-group${optionLabelContent ? " appt-entry-group" : ""}`}>
        {offeredAppointments.map((s) => (
          <label key={s}>
            <input
              type="radio"
              name="session"
              value={s}
              onChange={handleSessionChange}
              checked={session === s}
              disabled={!!session && session !== s}
            />
            {optionLabelContent && optionLabelContent[s] ? (
              <span className="appt-option-content">{optionLabelContent[s]}</span>
            ) : (
              <>
                {s}
                {optionLabelSuffix && optionLabelSuffix[s] ? (
                  <>
                    {" "}
                    <strong style={{ fontWeight: 700, fontStyle: "italic" }}>
                      {optionLabelSuffix[s]}
                    </strong>
                  </>
                ) : null}
              </>
            )}
          </label>
        ))}
      </div>
    </div>
  );

  // -- Shared searchable client picker (Review + Review Smart Goal) ---------
  const renderClientPicker = (questionText) => {
    const term = (clientSearchTerm || "").trim().toLowerCase();
    // Match on the displayed "Name - Code" label so the box accepts either
    // half of it. The label always starts with the name, so a name-only search
    // still behaves exactly as before.
    const matches = reviewClientOptions.filter(name =>
      String(reviewClientLabels[name] || name).toLowerCase().includes(term)
    );
    return (
      <div className="question-card">
        <p className="question">{questionText}</p>
        <div className="age-dropdown-container" style={{ position: "relative" }}>
          <label hidden htmlFor="client-search" className="age-label">Client</label>
          <input
            id="client-search"
            type="text"
            className="age-select-dropdown1"
            placeholder={reviewClientOptions.length === 0 ? "Loading clients..." : "Search or select client"}
            value={clientSearchTerm}
            onChange={(e) => { setClientSearchTerm(e.target.value); setShowClientDropdown(true); }}
            onFocus={() => setShowClientDropdown(true)}
            onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
            autoComplete="off"
          />
          {showClientDropdown && (
            <ul style={{ listStyle: "none", margin: 0, padding: "4px 0", position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: "6px", maxHeight: "170px", overflowY: "auto", zIndex: 20, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
              {matches.map((name, idx) => (
                <li key={idx} style={{ padding: "8px 12px", cursor: goalCapacityChecking === name ? "wait" : "pointer" }}
                  onMouseDown={async () => {
                    // Active-goal capacity guard (Set New Goal for Existing
                    // Clients). A client that already owns 4 goals with an
                    // `In progress - ...` status cannot be picked: NOTHING is
                    // written to state, the search box is reset so the box never
                    // shows a client the form does not hold, and the popup tells
                    // the therapist why. save_basic_details.php repeats the
                    // check server-side.
                    const resolvedAge = getReviewAgeForClient(name);
                    // Pass the id too. The API treats it as authoritative and only
                    // falls back to resolving by name + age group, so a client whose
                    // registered age band differs from the dropdown value can never
                    // slip past the guard as "unknown client, nothing to count".
                    const clientObj = eligibleReviewPathwaysData.find(c => c.client_name === name);
                    const accepted = await verifyClientCapacity(
                      name,
                      resolvedAge,
                      clientObj && clientObj.client_id != null ? clientObj.client_id : null
                    );
                    if (!accepted) {
                      setClientSearchTerm("");
                      setShowClientDropdown(false);
                      return;
                    }
                    // `client` state stays the bare name - that is the value
                    // save_basic_details.php and the ?client= param expect.
                    setClient(name);
                    setClientSearchTerm(reviewClientLabels[name] || name);
                    /*
                     * PICKING A CLIENT NEVER DERIVES THE AGE GROUP.
                     *
                     * NEW Review Appointment ("Set New Goal for Existing
                     * Clients"): the band must be chosen manually, so the
                     * selection CLEARS the dropdown - neither the client's
                     * registered band nor any previous session's band (nor a
                     * stale Review Smart Goal band) may auto-select, and
                     * step-1 Next stays disabled until Client + Age Group are
                     * both set. `resolvedAge` above is still passed to the
                     * capacity check only, where it is never displayed.
                     *
                     * Review Smart Goal: the band belongs to the EXACT
                     * session being reviewed and rides in as ?age= from
                     * goalreview, so that link value is re-applied as-is -
                     * but only as an AUTO-FILL: a band that has no `review
                     * smart goal` pathway was already refused by the step-1
                     * check (popup + value cleared + rejectedRsgAgeRef), so
                     * it is never written back here. With no ?age=, or with a
                     * refused one, the dropdown goes back to empty for
                     * manual selection.
                     */
                    const linkedAge = isSmartGoalMode
                      ? new URLSearchParams(location.search).get("age")
                      : "";
                    const fillableAge =
                      linkedAge && rejectedRsgAgeRef.current !== linkedAge ? linkedAge : "";
                    setAgeRange(fillableAge);
                    setSelectedReviewSessionId(null);
                    setShowClientDropdown(false);
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f7f0"}
                  onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                >{reviewClientLabels[name] || name}</li>
              ))}
              {matches.length === 0 && (
                <li style={{ padding: "8px 12px", color: "var(--cpa-gray-2, #626A6F)" }}>No matching clients</li>
              )}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // -- Shared age-group picker (Review + Review Smart Goal) -----------------
  // Uses reviewAgeGroups, which is derived from pathwaysData filtered by the
  // EXACT appointment type that is running - "review" for Review, "review smart
  // goal" for Review Smart Goal. Each appointment therefore only offers the age
  // groups that actually have a pathway of its own kind.
  //
  // The selected value is NOT force-added any more. Doing so is what leaked
  // between the two appointments: a Review age group carried into Review Smart
  // Goal (or vice versa) was spliced into the list, so both appointments ended
  // up offering the same ages. The validation effect below clears a value the
  // current appointment cannot honour instead, so the therapist always picks
  // from the list instead of silently running on another appointment's pathway.
  //
  // NO exception for Review Smart Goal's own band any more. The band recorded
  // on the selected session used to be spliced into the list while the
  // missing-pathway gate held, so the <select> kept displaying a band that
  // cannot be run. Spec: that band is auto-filled ONLY when a `review smart
  // goal` pathway exists for it - the check effect above clears the value and
  // raises the popup when it does not - so the list here is purely the bands
  // with a real pathway of this appointment's kind.
  const renderAgePicker = (questionText) => {
    const allAgeGroups = [...reviewAgeGroups];
    return (
      <div className="question-card">
        <p className="question">{questionText}</p>
        <div className="age-dropdown-container">
          <label hidden htmlFor="age-group-select" className="age-label">Age Group</label>
          <select id="age-group-select" name="ageGroup" className="age-select-dropdown"
            value={ageRange}
            onChange={(e) => setAgeRange(e.target.value)}
            aria-required="true">
            <option value="" disabled>{client ? "Select age group" : "Select a client first"}</option>
            {allAgeGroups.map((age, idx) => (
              <option key={idx} value={age}>{age}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // -- Initial age-group picker (Initial pathway only) ----------------------
  // Uses initialAgeGroups which is derived from pathwaysData filtered by
  // appointment_type === "initial". This ensures the dropdown only shows
  // age groups that have an initial pathway available.
  const renderInitialAgePicker = (questionText) => (
    <div className="question-card">
      <p className="question">{questionText}</p>
      <div className="age-dropdown-container">
        <label hidden htmlFor="initial-age-group-select" className="age-label">Age Group</label>
        <select id="initial-age-group-select" name="ageGroup" className="age-select-dropdown"
          value={ageRange}
          onChange={(e) => setAgeRange(e.target.value)}
          aria-required="true">
          <option value="" disabled>Select Age Group</option>
          {initialAgeGroups.map((ageObj, idx) => (
            <option key={idx} value={ageObj.age_group}>{ageObj.age_group}</option>
          ))}
        </select>
      </div>
    </div>
  );

  // -- Step 1: Initial-session basic details form ---------------------------
  const renderInitialStep1 = () => (
    <>
      {renderAppointmentOptions("1. Initial session of new Client")}
      <div className="question-card">
        <p className="question">2. Client - please type infant's first name only.</p>
        <input type="text" placeholder="Enter First Name" className="input-boxq2" value={client} aria-label="Infant's first name" onChange={e => {
          const value = e.target.value;
          const nameRegex = /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF 'ÃƒÆ’Ã‚Â¢ÃƒÂ¢Ã¢â‚¬Å¡Ã‚Â¬ÃƒÂ¢Ã¢â‚¬Å¾Ã‚Â¢-]*$/;
          if (!nameRegex.test(value)) {
            setToast({ message: "Special characters not allowed", type: "error" });
            return;
          }
          setClient(value);
        }} />
      </div>
      {renderInitialAgePicker("3. Age group - please select current age group of infant.")}
    </>
  );

  // -- Step 1: Review-session basic details form ----------------------------
  // Entry point "Set New Goal for Existing Client" (appointment=Review):
  //   Q1 heading  "1. Setting a new goal for an existing infant"
  //   Q1 option   the explanatory note, so the therapist knows a SECOND goal
  //               is being added and the current one stays active. The radio
  //               VALUE stays "Review" - that is what goes to the backend.
  //
  // The goalreview-launched Review form (fromGoalReview=1) keeps its own
  // heading and its plain option label: that path continues an existing
  // session rather than starting a brand-new goal journey.
  const renderReviewStep1 = () => {
    const fromGoalReview = urlParams.get("fromGoalReview") === "1";
    return (
      <>
        {renderAppointmentOptions(
          fromGoalReview
            ? "1. Review the existing goal of the client in this session"
            : "1. Setting a new goal for an existing infant",
          null,
          entryAppointment === APPT_REVIEW && !fromGoalReview
            ? {
              [APPT_REVIEW]:
                "You are setting an additional caregiver-led SMART goal for this infant. " +
                "Their current goal will stay active, and both goals will appear on the dashboard.",
            }
            : null
        )}
        {renderClientPicker("2. Client - please select infant's client.")}
        {renderAgePicker("3. Age group - What is the child's current corrected age?")}
      </>
    );
  };

  // -- Review Smart Goal: the goal text shown ON the appointment option ------
  // Exactly the "Smart Goal" column of the row the therapist clicked Review
  // on, e.g. "First Initial Smart Goal and Review Smart Goal sec time". It is
  // the one-line fallback for the goal card (see renderSmartGoalOptionContent
  // below); the card itself pairs each goal with its target date. No domain
  // labels or source metadata are shown.
  const smartGoalText = useMemo(() => {
    const ctx = smartGoalContext;
    if (!ctx || !ctx.status) return "";
    const texts = (Array.isArray(ctx.goals) ? ctx.goals : [])
      .map((g) => cleanGoalDomainText(g?.smartGoal || ""))
      .filter(Boolean);
    const fallback = [...new Set((Array.isArray(ctx.goalDomainTags) ? ctx.goalDomainTags : [])
      .map(cleanGoalDomainText)
      .filter(Boolean))];
    return (texts.length ? texts : fallback).join(", ");
  }, [smartGoalContext]);

  // -- Step 1: Review Smart Goal basic details form --------------------------
  // Same client/age selection as Review, but it launches the smart-goal
  // pathway which reviews (and later revises) the goal that already exists
  // on the originating pathway. ---------------------------------------------
  //
  // Q1 reads as the goal being updated:
  //   heading "1. Updating information for the current goal"
  //   option  "This infant's current caregiver-led SMART goal is: <goal>"
  //           "Target date: 10 Oct 2026"
  //
  // Both pieces come from get_smart_goal_context.php - the SAME fetch that
  // already fills smartGoalContext - so the target date travels through the
  // frontend and no backend change is required. The radio VALUE stays
  // "Review Smart Goal", which is what the backend receives.
  const rsgGoalRows = useMemo(() => {
    const ctx = smartGoalContext;
    if (!ctx || !ctx.status) return [];
    return (Array.isArray(ctx.goals) ? ctx.goals : [])
      .map((g) => ({
        text: cleanGoalDomainText(g?.smartGoal || ""),
        targetDate: g?.targetDate || "",
      }))
      .filter((g) => String(g.text || "").trim() !== "");
  }, [smartGoalContext]);

  const renderSmartGoalOptionContent = () => (
    <>
      {rsgGoalRows.length > 0 ? (
        rsgGoalRows.map((goal, idx) => (
          <React.Fragment key={idx}>
            {/* Sentence + goal share one row; the target date drops to the next. */}
            <span className="appt-option-line">
              <span className="appt-option-prefix">This infant’s current caregiver-led SMART goal is: </span>
              <strong className="appt-option-goal"><RichText inline html={goal.text} /></strong>
            </span>
            {goal.targetDate ? (
              <span className="appt-option-meta">Target date: {formatGoalTargetDate(goal.targetDate)}</span>
            ) : null}
          </React.Fragment>
        ))
      ) : (
        <span className="appt-option-line">
          <span className="appt-option-prefix">This infantâ€™s current caregiver-led SMART goal is: </span>
          <strong className="appt-option-goal">
            {smartGoalLoading
              ? "Loading the existing goal.."
              : (smartGoalText || "")}
          </strong>
        </span>
      )}
    </>
  );

  const renderSmartGoalStep1 = () => {
    // While the goal context is still on its way the option falls back to the
    // plain appointment name, so the radio is never blank. Once the context
    // (or the goal text) is available - or when the page was opened from
    // goalreview - the option shows the goal and its target date.
    const hasOptionContent =
      entryAppointment === APPT_REVIEW_SMART_GOAL || rsgGoalRows.length > 0 || Boolean(smartGoalText);
    return (
      <>
        {renderAppointmentOptions(
          "1. Updating information for the current goal",
          null,
          hasOptionContent ? { [APPT_REVIEW_SMART_GOAL]: renderSmartGoalOptionContent() } : null
        )}
        {renderClientPicker("2. Client - please select infant's client.")}
        {renderAgePicker("3. Age group - What is the child's current corrected age?")}
        {smartGoalLoading && (
          <div className="question-card">
            <p className="question">Loading the existing goal..</p>
          </div>
        )}
      </>
    );
  };

  // -- Goal-detail step block (domain sub-options) --------------------------

  // -- Goal-detail step block (domain sub-options) --------------------------
  const renderGoalDetailStep = () => {
    if (!(step >= 2 && isGoalDetailStep && visibleSteps[step - 2])) return null;
    return (
      <div className="question-card">
        <p className="question">Select the specific option from the list that supports the caregiver-led goal. Some domains do not have a list to select from - if so, no further selection is needed for that domain. Your selected domain(s) and option will determine the tailored information provided at the end of the pathway.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {visibleSteps[step - 2].options.map(opt => {
            const isSelected = Array.isArray(answers[visibleSteps[step - 2].node_number]) && answers[visibleSteps[step - 2].node_number].includes(opt.option_text);
            if (!isSelected) return null;
            const subOpts = opt.sub_options || [];
            const domainAnswerKey = `${visibleSteps[step - 2].node_number}_${opt.option_text}_sub`;
            const currentSelections = answers[domainAnswerKey] || [];
            return (
              <div key={opt.option_id} style={{ border: "1px solid #fff", borderRadius: "8px", padding: "15px", backgroundColor: "#fff" }}>
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "15px" : "0px" }}>
                  <div style={{ width: isMobile ? "100%" : "30%", fontWeight: "bold", display: "flex", alignItems: "start", gap: "10px" }}>
                    <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
                      <input type="checkbox" checked={isSelected} onChange={() => {
                        const qNode = visibleSteps[step - 2].node_number;
                        const allowed = handleMainOptionSelect(qNode, opt, true, true);
                        if (!allowed) return;
                        let curr = Array.isArray(answers[qNode]) ? [...answers[qNode]] : [];
                        let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                        let allIds = visibleSteps[step - 2].options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                        saveResponseAPI(currentSessionId, qNode, "option", visibleSteps[step - 2].question_id, null, allIds.length === 0 ? [] : allIds);
                      }} /> <RichText inline html={opt.option_text} />
                    </label>
                  </div>
                  <div style={{ width: isMobile ? "100%" : "70%" }}>
                    {subOpts.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        {subOpts.map(sub => {
                          const isSubSelected = currentSelections.includes(sub.option_text);
                          return (
                            <label key={sub.option_id} style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer" }}>
                              <input type="radio" value={sub.option_text} checked={isSubSelected}
                                onChange={() => {
                                  const qNode = visibleSteps[step - 2].node_number;
                                  handleSubOptionSelect(qNode, opt.option_text, sub, false, 1);
                                  if (!isSubSelected) saveResponseAPI(currentSessionId, sub.node_number, "option", sub.option_id, sub.option_text);
                                  else saveResponseAPI(currentSessionId, sub.node_number, "question", sub.option_id, "");
                                }} />
                              <span style={{ lineHeight: "1.4" }}><RichText inline html={sub.option_text} /></span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Copy for the "previously recorded information" read-and-continue page.
  // One sentence differs per appointment: the plain Review goes on to write a
  // BRAND NEW SMART goal, so it names that step; Review Smart Goal revises an
  // existing goal, so it points at the tailored information instead.
  const infoIntroText = isSmartGoalMode
    ? "The next pages show previously recorded information. Check each item and update only if something has changed. If nothing has changed, leave it as is and select Next. Any updates may change the suggested interventions and tailored information at the end of the pathway."
    : "The next pages show previously recorded information. Check each item and update only if something has changed. If nothing has changed, leave it as is and select Next. You will then select the goal domain(s) and write the new SMART goal.";

  // -- Right-content footer navigation bar ----------------------------------
  const renderFooterButtons = () => (
    <div className="footer-buttons">
      <button className="backbutton" onClick={handleBack} disabled={step === 1 && !isGoalDetailStep && !showInfoIntro}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
              {/* Quick-save: keep the incomplete session, straight to Continue Assessment. */}
              <button className="save-btn" onClick={handleKeepDraftAndLeave}>Save for Later</button>
      <button className={`footer-btn next-btn ${isFormValid() ? "active" : ""}`} disabled={!isFormValid()} onClick={handleNext}>
        {"Next "}<FontAwesomeIcon icon={faArrowRight} />
      </button>
    </div>
  );

  // ---------------------------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------------------------

  return (
    <>
      {isRestoringDraft && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(255,255,255,0.8)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px", fontWeight: "bold" }}>
          Restoring Assessment...
        </div>
      )}

      {isGeneratingPdf && <LoadingOverlay />}

      {showShareModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Share assessment summary"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10001, display: "flex", justifyContent: "center", alignItems: "center" }}
          onClick={() => setShowShareModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "16px", alignItems: "stretch" }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#222", textAlign: "center" }}>Share Assessment Summary</h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#666", textAlign: "center" }}>The summary will be converted to PDF and shared via your chosen app.</p>

            <button
              onClick={handleShareMail}
              style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
              onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
            >
              <span style={{ fontSize: "26px" }}>??</span>
              Share via Mail
            </button>

            <button
              onClick={handleShareWhatsApp}
              style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
              onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
            >
              <span style={{ fontSize: "26px" }}>??</span>
              Share via WhatsApp
            </button>

            <button
              onClick={() => setShowShareModal(false)}
              style={{ marginTop: "4px", background: "none", border: "none", color: "var(--cpa-gray-2, #626A6F)", cursor: "pointer", fontSize: "14px", textAlign: "center" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {networkError && (
        <div
          role="alert"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", maxWidth: "360px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#c0392b" }}>Network Issue</h3>
            <p style={{ margin: "12px 0 20px", fontSize: "14px", color: "#555", lineHeight: 1.5 }}>
              We're having trouble reaching the server. Please check your internet connection and try again.
            </p>
            <button
              onClick={() => { setNetworkError(false); retryPendingResponses(); }}
              style={{ padding: "10px 28px", borderRadius: "8px", border: "none", background: "var(--cpa-primary, #247A3D)", color: "#fff", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {showGoalDomainPopup && (
        <div
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", maxWidth: "360px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#c0392b" }}>Selection Not Allowed</h3>
            <p style={{ margin: "12px 0 20px", fontSize: "14px", color: "#555", lineHeight: 1.5 }}>
              Eating and Drinking domain can only be selected as a single choice.
            </p>
            <button
              onClick={() => setShowGoalDomainPopup(false)}
              style={{ padding: "10px 28px", borderRadius: "8px", border: "none", background: "var(--cpa-primary, #247A3D)", color: "#fff", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="assessment1">
        <div className="header-bar1">
          <img src={Frame} alt="" className="frame-icon" onClick={() => setShowLeftPanel(prev => !prev)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowLeftPanel(prev => !prev); } }} role="button" tabIndex="0" aria-label="Toggle navigation panel" aria-expanded={showLeftPanel} />
          <h1 tabIndex="0">
            <span className="sr-only">
              {entryAppointment === "Review"
                ? "Set New Goal for Existing Client: "
                : entryAppointment === "Review Smart Goal"
                  ? "Review SMART Goal: "
                  : "Start Initial Client Assessment: "}
            </span>
            Background and Assessment Information
          </h1>
          {/* Second copy of the title shown only on short screens. It is not a
              second page heading, so it must not be another <h1>. */}
          <span className="header-title-short" aria-hidden="true">Assessments</span>

          <div className="header-actions">
            <button className="header-btn restart-btn" onClick={handleRestart} aria-label="Restart assessment">
              <img src={restart} alt="" className="header-icon" aria-hidden="true" />
              <span className="header-btn-text">Restart</span>
            </button>
            {(step >= 2 || showReviewGoalScreen) && (
              <button
                className="header-btn fav-btn"
                onClick={handleToggleFavourite}
                aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                aria-pressed={isFavourite}
              >
                <FontAwesomeIcon
                  icon={faStar}
                  className="star-icon1"
                  aria-hidden="true"
                  style={{
                    color: isFavourite ? "var(--cpa-yellow-bright, #FFDD00)" : "#fff",
                  }}
                />
                <span className="header-btn-text">Favourite</span>
              </button>
            )}
          </div>
        </div>

        <div className="assessment-container">
          {/* PREVIOUS DATA: while the folder icon has the history open, the whole
              assessment area is replaced by one full-width pane instead of
              rendering the history inside the 75% right-hand column. */}
          {isHistoryPaneOpen ? (
            /* The assessment footer (Back / Save for Later / Next) is deliberately
               NOT rendered here. Those buttons act on the live questionnaire -
               moving the step, or saving a half-finished assessment - and the
               questionnaire is not on screen while Previous Data is open, so the
               two views must not share a footer. The panel's own "Previous Page"
               and "X" are the only ways in and out. */
            <div className="right-content previous-data-pane">
              <div className="right-content-over" tabIndex="0" aria-label="Client history">
                {renderPreviousDataPanel()}
              </div>
            </div>
          ) : (
            <>
          <div className={`left-panel ${showLeftPanel ? "show-panel" : "hide-panel"}`}>
            {renderLeftPanel()}
          </div>


          <div className="right-content">
            {showInfoIntro ? (
              /* Read-and-continue page between step 1 and the 4th question,
                 for Review + Review Smart Goal only. Mirrors the centred card
                 the review hand-off screen uses, so the two read the same. */
              <>
                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", height: "100%", padding: "40px 20px" }}>
                    <div style={{ background: "#ffffff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "28px 32px", maxWidth: "760px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <p style={{ color: "#288537", fontWeight: "bold", fontSize: "22px", textAlign: "center", lineHeight: 1.8, margin: 0 }}>
                        {infoIntroText}
                      </p>
                    </div>
                  </div>
                </div>
                {renderFooterButtons()}
              </>
            ) : showReviewGoalScreen ? (
              <>
                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", height: "100%", padding: "40px 20px" }}>
                    <div style={{ background: "#ffffff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "28px 32px", maxWidth: "760px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                      <p style={{ color: "#288537", fontWeight: "bold", fontSize: "24px", textAlign: "center", lineHeight: "1.8", margin: 0 }}>
                        Review pathways use background and assessment information gathered to date, alongside goal progress, to provide tailored guidance.
                      </p>
                      <p style={{ color: "#626A6F", fontWeight: "normal", fontSize: "14px", textAlign: "center", lineHeight: "1.8", margin: "18px 0 0" }}>
                        Please click below to update goal progress.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="footer-buttons">
                  <button className="backbutton" onClick={handleBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
                  <button className="footer-btn next-btn active" onClick={() => navigate(`/goalreview?sessionId=${selectedReviewSessionId || currentSessionId}`)}>
                    Goal Progress Update
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">

                  {!historyGroupName && (
                    <>
                      {step === 1 && session === APPT_INITIAL && renderInitialStep1()}

                      {step === 1 && session === APPT_REVIEW && renderReviewStep1()}

                      {step === 1 && session === APPT_REVIEW_SMART_GOAL && renderSmartGoalStep1()}

                      {/* Safety net: a bare /start-assessment with no entry
                          point must never leave step 1 blank - offer the
                          three appointment types to pick from. */}
                      {step === 1 && !session && renderAppointmentOptions("1. Select the appointment type")}

                      {renderCurrentQuestion()}

                      {renderGoalDetailStep()}
                    </>
                  )}

                  {step === (1 + visibleSteps.length + 1) && !isGoalDetailStep && (
                    <>
                      <div className="summ-container1" ref={summaryRef}>
                        <div className="sum-cont1">

                          {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                          <div className="summ-client-row">
                            {interventionsData?.session?.client_name || client}
                          </div>

                          {/* Section 1 - Row 2: Metadata pills */}
                          <div className="summ-meta-row">
                            <span className="summ-meta-pill summ-meta-plain">
                              <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                              <span className="summ-meta-value" style={{ color: "#247A33" }}>
                                {(() => {
                                  const code = String(interventionsData?.session?.client_code || "");
                                  const digits = code.replace(/\D/g, "").slice(0, 8);
                                  if (digits.length === 8) {
                                    const dd = digits.slice(0, 2);
                                    const mm = digits.slice(2, 4);
                                    const yyyy = digits.slice(4, 8);
                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                    const mIdx = parseInt(mm, 10) - 1;
                                    return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                                  }
                                  return "";
                                })()}
                              </span>
                            </span>
                            <span className="summ-meta-pill summ-meta-green">
                              <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                              <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                            </span>
                            <span className="summ-meta-pill summ-meta-green">
                              <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                              <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ageRange}</span>
                            </span>
                          </div>

                          {/* Section 1 - Row 3: Section title */}
                          <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                          {/* Section 1 - Row 4: Topography + Severity */}
                          <div className="summ-findings-block">
                            <div className="summ-finding-row">
                              <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                              <strong className="summ-finding-label">Topography:&nbsp;</strong>
                              <span className="summ-value-pill">
                                {(() => {
                                  const q = visibleSteps.find(v => v.question_text.toLowerCase().includes("topography"));
                                  if (!q) return "";
                                  const ans = answers[q.node_number];
                                  if (!ans) return "";
                                  const ansArray = Array.isArray(ans) ? ans : [ans];
                                  if (ansArray.length === 1) {
                                    const selectedOpt = q.options?.find(o => o.option_text === ansArray[0]);
                                    if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
                                      const subAns = answers[`${q.node_number}_${ansArray[0]}_sub`];
                                      if (subAns) return Array.isArray(subAns) ? subAns.join(", ") : subAns;
                                    }
                                    return ansArray[0];
                                  }
                                  return ansArray.join(", ");
                                })()}
                              </span>
                            </div>

                            <div className="summ-finding-row">
                              <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                              <strong className="summ-finding-label">Severity:&nbsp;</strong>
                              <span className="summ-value-pill">
                                {(() => {
                                  const q = visibleSteps.find(v => v.question_text.toLowerCase().includes("severity"));
                                  const ans = q ? answers[q.node_number] : "";
                                  return Array.isArray(ans) ? ans.join(", ") : (ans || "");
                                })()}
                              </span>
                            </div>
                          </div>

                          {/* Section 1 - Row 5: Early indicators of comorbidities */}
                          <div className="summ-finding-row">
                            <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                            <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                            <span className="summ-value-pill">
                              {(() => {
                                const comorbidityGroup = activePathway?.groups?.find(g => g.group_name.toLowerCase().includes("comorbidities"));
                                if (!comorbidityGroup) return "";
                                let keywordNames = [];
                                (comorbidityGroup.questions || []).forEach(q => {
                                  const ans = answers[q.node_number];
                                  if (ans && ans.toString().toLowerCase() === "yes") keywordNames.push(q.keywords || q.question_text);
                                });
                                return keywordNames.length > 0 ? keywordNames.join(", ") : "";
                              })()}
                            </span>
                          </div>
                          <div className="summ-finding-row">
                            <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                            <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                            <span className="summ-value-pill">
                              {(() => {
                                const factors = interventionsData?.session?.goal_limiting_factors || [];
                                return factors.join(", ");
                              })()}
                            </span>
                          </div>
                          {/* Section 1 - Row 6: Goals. Built from the suggested
                              interventions for this session ("domain - skill"
                              per group). The originating pathway's domain is only
                              used as a fallback so the row is never blank. */}
                          <div className="summ-finding-row">
                            <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                            <strong className="summ-finding-label">Goals:&nbsp;</strong>
                            <span className="summ-value-pill">
                              {(() => {
                                const originatingTags = (smartGoalContext?.goalDomainTags || [])
                                  .map((t) => cleanGoalDomainText(t))
                                  .filter(Boolean);

                                // Review Smart Goal: the Goals row must reflect
                                // the goal being reviewed - the originating
                                // session named by ?goalSessionId. Its domain-skill
                                // tags come from the smart-goal context and are
                                // authoritative, so a stale or unscoped
                                // intervention fetch can never leak another
                                // session's domains (e.g. the Initial's) in here.
                                if (isSmartGoalMode && originatingTags.length > 0) {
                                  return [...new Set(originatingTags)].join(", ");
                                }

                                const map = interventionsData?.data || {};
                                const tabs = Object.keys(map).map((key) => {
                                  const items = map[key] || [];
                                  const first = items[0] || {};
                                  const domain = first.goal_domain || key || "";
                                  const skill = first.goal_skill || "";
                                  return skill && skill !== "null" ? `${domain} - ${skill}` : domain;
                                });
                                const primary = [...new Set(tabs.filter(Boolean))];
                                if (primary.length > 0) {
                                  return primary.join(", ");
                                }
                                // Fall back to the originating session's own
                                // goals. `goalDomainTags` is built server-side
                                // from the PLANNING goals only, already split
                                // into "Domain - Skill" and de-duplicated.
                                // Joining smartGoalContext.goals here instead
                                // repeated every domain once per Goal Review
                                // and left legacy rows as a raw glued string.
                                return [...new Set(originatingTags)].join(", ");
                              })()}
                            </span>
                          </div>

                          {/* Review Smart Goal: the Goal Domains / Goals carried
                              over from the ORIGINATING pathway. Rendered as plain
                              finding rows directly after "Goal Limiting Factors" /
                              "Goals" so the therapist sees what is being reviewed
                              and revised before continuing to the Intervention
                              page. */}
                        


                          {/* Section 2 - Row 1: Info heading */}
                          <h4 className="summ-info-header">
                            You are about to receive suggested interventions based on your responses throughout this pathway.
                          </h4>

                          {/* Section 2 - Row 2: Info text */}
                          <p className="summ-info-text">
                            Review your answers now before confirming
                          </p>
                        </div>
                      </div>
                      <div className="summary-footer">
                        <button className="backbutton" onClick={handleBack} disabled={step === 1 && !isGoalDetailStep}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>

                        {/* Spec STEP 1.1 / 2.3: this single CTA is the hand-off
                            into /Interventions in every mode. The goal-setting
                            step ("Set Goal Procedure" / "Revise Selected
                            Intervention") lives on the /Interventions page, so
                            there is deliberately no second button here. */}
                        <button className=" summ-save-btn" onClick={handleSummaryConfirm}>
                          Save and View Intervention
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {step !== (1 + visibleSteps.length + 1) && renderFooterButtons()}
              </>
            )}
          </div>
            </>
          )}
          {renderHistoryPrintCopy()}
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* ACTIVE GOAL CAPACITY guard for "Set New Goal for Existing Clients".
          A client with 4 goals that are `In progress - on track` /
          `In progress - delayed` cannot start another goal journey until one of
          those journeys is closed. Selection is refused, so this popup is the
          only thing the therapist has to acknowledge - OK simply dismisses it
          and the dropdown stays empty and ready for a valid client. */}
      {goalCapacityBlocked && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Active goal limit reached"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10003, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px 32px", maxWidth: "520px", width: "90%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "18px" }}>
            <h3 style={{ margin: 0, color: "#247A33", fontSize: "20px" }}>Active goal limit reached</h3>
            <p style={{ margin: 0, color: "#626A6F", fontSize: "14px", lineHeight: 1.7 }}>
              {goalCapacityBlocked.message}
            </p>
            <p style={{ margin: 0, color: "#626A6F", fontSize: "13px", lineHeight: 1.7 }}>
              Client: <strong>{goalCapacityBlocked.client_name}</strong>
              {goalCapacityBlocked.age_group ? <> &nbsp;|&nbsp; </> : null}
              <> &nbsp;|&nbsp; Goals in progress: <strong>{goalCapacityBlocked.active_count}</strong></>
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                className="summ-save-btn"
                style={{ background: "#247A33", color: "#fff", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer" }}
                onClick={() => setGoalCapacityBlocked(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-time Initial Assessment guard. An Initial Assessment can only be
          performed once per client, so offer the two supported ways forward
          instead of a dead end. */}
      {initialBlocked && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Initial Assessment already completed"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px 32px", maxWidth: "520px", width: "90%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "18px" }}>
            <h2
              id="initial-blocked-title"
              style={{
                margin: 0,
                /* CPA brand green, pinned at the TOP of the popup card */
                color: "var(--cpa-primary, #247A3D)",
                fontSize: "22px",
                fontWeight: 700,
                lineHeight: 1.35,
              }}
            >
              This Client name already exists!
            </h2>
            <p style={{ margin: 0, color: "#626A6F", fontSize: "16px", lineHeight: 1.7 }}>
              {"Use ‘Review Existing Goal’ or ‘Set New Goal’ in Home page to review this client.Or kindly edit the name to continue with initial session."}
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                className="backbutton"
                onClick={() => setInitialBlocked(null)}
              >
                OK
              </button>
              
            </div>
          </div>
        </div>
      )}

      {/* Step-1 blockers that used to be transient toasts - one popup, one sentence.
          Deliberately has NO heading: the title used to read "Cannot Edit" /
          "Pathway not available" while the body carried the spec'd sentence, and
          the two together contradicted each other. Just the sentence, the age
          group, and a single OK - there is no way forward from here either way.
          zIndex 10005 sits above the save-for-later popup (10004) and both
          guards, so whichever blocker fires last is the one on screen. */}
      {pathwayBlocked && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Clinical pathway not available"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10005, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px 32px", maxWidth: "520px", width: "90%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "18px" }}>
            <p style={{ margin: 0, color: "#323C43", fontSize: "16px", fontWeight: 600, lineHeight: 1.6 }}>
              {pathwayBlocked.message}
            </p>
            {pathwayBlocked.ageGroup && (
              <p style={{ margin: 0, color: "#626A6F", fontSize: "13px", lineHeight: 1.7 }}>
                Age group: <strong>{pathwayBlocked.ageGroup}</strong>
              </p>
            )}
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                className="summ-save-btn"
                style={{ background: "#247A33", color: "#fff", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: "pointer" }}
                onClick={() => setPathwayBlocked(null)}
                autoFocus
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* "Leave guard" confirm - the save-or-delete question, raised when the
          therapist navigates away from an in-progress assessment.
          AssessmentLeaveGuard bounced the route back and stashed the
          destination. Cancel stays put; Save keeps the incomplete session and
          heads to the stashed destination; Delete discards the session. */}
      {leavePromptOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Save this incomplete session"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10005, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <div style={{ background: "#fff", borderRadius: "16px", padding: "28px 32px", maxWidth: "520px", width: "90%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "18px" }}>
            <p style={{ margin: 0, color: "#323C43", fontSize: "16px", fontWeight: 600, lineHeight: 1.6 }}>
              Do you want to Save this incomplete session to continue later? 
            </p>

            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                className="summ-save-btn"
                style={{ background: "#fff", color: "#555", border: "1px solid #b0b6bb", borderRadius: "6px", padding: "10px 18px", cursor: leavePromptBusy ? "wait" : "pointer", opacity: leavePromptBusy ? 0.6 : 1 }}
                onClick={handleLeavePromptCancel}
                disabled={leavePromptBusy}
              >
                Cancel
              </button>
              <button
                className="summ-save-btn"
                style={{ background: "#fff", color: "var(--cpa-error-red, #C22B27)", border: "1px solid var(--cpa-error-red, #C22B27)", borderRadius: "6px", padding: "10px 18px", cursor: leavePromptBusy ? "wait" : "pointer", opacity: leavePromptBusy ? 0.6 : 1 }}
                onClick={handleLeavePromptDelete}
                disabled={leavePromptBusy}
              >
                No, Delete this session
              </button>
              <button
                className="summ-save-btn"
                style={{ background: "#247A33", color: "#fff", border: "none", borderRadius: "6px", padding: "10px 18px", cursor: leavePromptBusy ? "wait" : "pointer", opacity: leavePromptBusy ? 0.6 : 1 }}
                onClick={handleLeavePromptSave}
                disabled={leavePromptBusy}
              >
                Yes, Save for later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

